import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { createPortal } from 'react-dom'
import {
  FiMessageSquare,
  FiX,
  FiUser,
  FiCheck,
  FiCheckCircle,
  FiArrowLeft,
  FiRefreshCw,
  FiSend,
  FiPaperclip,
  FiImage,
  FiVideo,
  FiFile,
  FiMapPin,
  FiPlay,
  FiPause,
  FiDownload,
  FiExternalLink,
  FiPhone,
  FiSearch,
  FiTrash2,
  FiUserPlus,
  FiSettings,
  FiBriefcase,
  FiAward,
  FiUserCheck,
  FiZapOff,
  FiArrowDown,
  FiInfo,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiList,
  FiClock,
  FiPlus,
  FiCheckSquare,
  FiFlag,
  FiTrendingUp,
  FiFilter,
  FiUploadCloud,
  FiCornerUpLeft,
  FiAlertTriangle,
  FiSlash,
} from 'react-icons/fi'
import { RiRobot2Line, RiUserLine } from 'react-icons/ri'
import { useAuth } from '../contexts/AuthContext'
import { useWhatsAppSocket, ConversationUpdate, BotTypingEvent, HumanTakeoverEvent, BotAutoResumedEvent, BotManualResumedEvent, ConversationBotToggledEvent, DeviceAIToggledEvent, MessageStatusUpdateEvent, LeadHandoffDeliveryFailedEvent, LeadAutoAssignHeldEvent, LeadAutoAssignReleasedEvent } from '../hooks/useWhatsAppSocket'
import AssignLeaderModal from './AssignLeaderModal'
import { ContactProfilePanel } from './ContactProfilePanel'
import { ForwardModal } from './ForwardModal'
import { formatWhatsAppText } from '../lib/whatsappFormat'
import EditColumnIcon from '@rsuite/icons/EditColumn'

// NEU-LAB: neumorfismo do painel (só no tema claro). LIGADO por padrão desde
// 2026-07-25; `?neu=0` desliga sem precisar de rebuild. Lido uma vez no
// carregamento do módulo: sobrevive às reescritas da URL (reflexo do ?panel=)
// e fica FORA do componente — dentro dele ficaria depois do `if (!isOpen) return
// null`, violando a regra dos hooks.
const neu = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('neu') !== '0'

interface Conversation {
  id: string
  device_id: string
  remote_jid: string
  remote_name: string | null
  remote_profile_pic: string | null
  last_message: string | null
  last_message_at: string | null
  last_message_from_me?: number | boolean  // 1 se última msg foi enviada por nós
  unread_count: number
  is_bot_active: number
  // Novos campos do sistema hierárquico
  bot_status?: 'active' | 'paused' | 'human'  // active, paused (manual), human (humano assumiu)
  bot_paused_until?: string | null  // Timestamp até quando o bot está pausado
  bot_paused_reason?: string | null  // 'manual' ou 'human_takeover'
  bot_paused_by?: string | null  // Quem pausou
  message_count?: number
  created_at: string
  // Tipo consultor e nome (enriquecidos pelo backend via cadastro_clientes ← Oracle DRACO)
  tipo_consultor?: string | null  // '1'=Empresário, '2'=Líder, '3'=Consultora
  nome_cadastro?: string | null   // Nome registrado no sistema (fonte de verdade sobre remote_name)
  converted_consultant_at?: string | null  // Quando virou consultora (era lead antes)
  // Campos de arquivamento
  is_archived?: number
  archived_at?: string | null
  // Dados extraídos pelo bot (CEP, nome, endereço etc.) — vindos de crm_conversation_status.lead_extracted_data
  lead_extracted_data?: string | Record<string, unknown> | null
  // Lead que o bot finalizou (cadastro completo) e ainda não foi atribuído a líder.
  // Sinaliza pro operador distribuir manualmente — caso contrário a conversa pode
  // ficar enterrada na lista quando outras mensagens chegam por cima.
  awaiting_leader?: boolean
  // Atribuição: nome da líder responsável + quando. null quando ninguém foi atribuído.
  // Lista principal oculta essas conversas por default (operador já passou a bola).
  assigned_leader?: { name: string | null; assigned_at: string | null } | null
  // Contato na blocklist (golpista) — enriquecido pelo backend via contact_blocklist.
  // Quando presente: a Jú não responde e o auto-assign nunca encaminha pra líder.
  // As mensagens continuam chegando de propósito, pra operação ver que ele voltou.
  blocked?: { reason: string | null; blocked_at: string | null; blocked_by: string | null } | null
}

interface Message {
  id: string
  conversation_id: string
  device_id: string
  message_id: string | null
  remote_jid: string
  from_me: number
  fromMe?: boolean
  content: string | null
  message_type: string
  media_url?: string | null
  mediaUrl?: string | null
  media_mimetype?: string | null
  media_filename?: string | null
  timestamp: string
  status: string
  is_bot_response: number
  isBotResponse?: boolean
  metadata: any
  // Reply/citação (Meta context.id) — preenchidos pelo backend via LEFT JOIN na
  // mensagem citada. Quando presentes, o balão renderiza o preview da original.
  quoted_message_id?: string | null
  quoted_content?: string | null
  quoted_message_type?: string | null
  quoted_from_me?: number | null
  quoted_is_bot_response?: number | null
}

interface WhatsAppInboxPanelProps {
  isOpen: boolean
  onClose: () => void
  deviceId: string
  deviceName: string
  deviceAiEnabled?: boolean  // Status de IA do dispositivo pai
  deviceApiType?: 'evolution' | 'meta'  // Tipo de API do dispositivo
  embedded?: boolean  // Se true, renderiza como painel embutido (sem modal)
  onConversationRead?: (deviceId: string, unreadCount?: number) => void  // Callback quando uma conversa é marcada como lida
  onAllConversationsRead?: (deviceId: string) => void  // Callback quando TODAS as conversas são marcadas como lidas
  externalSearchTerm?: string  // Busca controlada externamente (device bar)
  externalOpenConversationId?: string | null  // Deep-link: ao mudar pra um id, o painel auto-seleciona a conversa
  onExternalOpenHandled?: () => void  // Callback pra o pai limpar o pendingConversationId depois do auto-open
  toolbarSlot?: HTMLElement | null  // DOM node na barra de dispositivos onde a toolbar (Lista×Kanban + campanha) é renderizada via portal
}

// Tradução amigável dos códigos de erro de entrega da Meta (Cloud API) pro banner
// de falhas — o atendimento não decora número. Fallback mantém o código pra suporte.
const META_ERR_LABELS: Record<number, string> = {
  131026: 'número não recebe WhatsApp',
  131047: 'janela de 24h fechada',
  131049: 'limite de frequência da Meta',
  131051: 'tipo de mensagem não suportado',
  131053: 'falha ao enviar a mídia',
  131000: 'erro temporário da Meta',
  132000: 'parâmetros do template não batem',
  132001: 'template não aprovado/inexistente',
  132015: 'template pausado pela Meta',
  132016: 'template desabilitado pela Meta',
  130472: 'número em experimento da Meta',
}
function friendlyMetaError(code: number | null): string {
  if (code == null) return 'falha de entrega'
  return META_ERR_LABELS[code] || `falha de entrega (cód. ${code})`
}

// Tooltip do badge BLOQUEADO. O badge é compacto demais pra contar a história
// sozinho — o hover precisa dizer o motivo, quem bloqueou e o efeito prático.
// Gestão da lista fica no painel Bloqueados (admin).
function blockedTitle(b: { reason: string | null; blocked_at: string | null; blocked_by: string | null }): string {
  const linhas = ['Contato bloqueado — a Jú não responde e ele nunca é encaminhado a uma líder.']
  if (b.reason) linhas.push(`\nMotivo: ${b.reason}`)
  if (b.blocked_by) {
    const quando = b.blocked_at
      ? new Date((b.blocked_at.includes('T') ? b.blocked_at : b.blocked_at.replace(' ', 'T') + 'Z')).toLocaleDateString('pt-BR')
      : null
    linhas.push(`Bloqueado por ${b.blocked_by}${quando ? ` em ${quando}` : ''}`)
  }
  return linhas.join('\n')
}

// Quem (ou o quê) mandou a mensagem de saída. Antes o balão só sabia dizer
// "Campanha" (qualquer template), "Bot IA" ou "Humano" — e "Humano" pegava
// tanto a pessoa no painel quanto o follow-up automático de líderes, que não
// tem gente nenhuma escrevendo. A verdade já estava no metadata: os remetentes
// automáticos gravam `source`, e o envio manual grava `sent_by_name` (desde
// 2026-07-21). Mensagem antiga sem nenhum dos dois continua "Humano".
type OutboundOrigin = { kind: 'campaign' | 'followup' | 'human'; label: string; hint: string }
function outboundOrigin(msg: { metadata?: any; message_type?: string } | undefined): OutboundOrigin {
  let m: any = {}
  try { m = typeof msg?.metadata === 'string' ? JSON.parse(msg.metadata || '{}') : (msg?.metadata || {}) } catch { m = {} }
  const source = m?.source
  const nome = m?.sent_by_name
  if (source === 'meta_campaign') return { kind: 'campaign', label: 'Campanha', hint: 'Disparo de campanha pelo painel Marketing' }
  if (source === 'leader_followup_template' || source === 'leader_followup_session') {
    return { kind: 'followup', label: 'Follow-up automático', hint: 'Enviada pelo follow-up de líderes — automática, sem ninguém digitando' }
  }
  // manual_template cai aqui: template é disparado à mão, tem dono.
  if (nome) return { kind: 'human', label: nome, hint: `Enviada por ${nome} pelo painel` }
  if (msg?.message_type === 'template') return { kind: 'campaign', label: 'Campanha', hint: 'Template enviado antes do registro de origem' }
  return { kind: 'human', label: 'Humano', hint: 'Enviada por um operador pelo painel (antes do registro de autoria)' }
}

// Ícone do WhatsApp SVG que respeita o tema
function WhatsAppIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M26.576 5.363c-2.69-2.69-6.406-4.354-10.511-4.354-8.209 0-14.865 6.655-14.865 14.865 0 2.732 0.737 5.291 2.022 7.491l-0.038-0.070-2.109 7.702 7.879-2.067c2.051 1.139 4.498 1.809 7.102 1.809h0.006c8.209-0.003 14.862-6.659 14.862-14.868 0-4.103-1.662-7.817-4.349-10.507l0 0zM16.062 28.228h-0.005c-0 0-0.001 0-0.001 0-2.319 0-4.489-0.64-6.342-1.753l0.056 0.031-0.451-0.267-4.675 1.227 1.247-4.559-0.294-0.467c-1.185-1.862-1.889-4.131-1.889-6.565 0-6.822 5.531-12.353 12.353-12.353s12.353 5.531 12.353 12.353c0 6.822-5.53 12.353-12.353 12.353h-0zM22.838 18.977c-0.371-0.186-2.197-1.083-2.537-1.208-0.341-0.124-0.589-0.185-0.837 0.187-0.246 0.371-0.958 1.207-1.175 1.455-0.216 0.249-0.434 0.279-0.805 0.094-1.15-0.466-2.138-1.087-2.997-1.852l0.010 0.009c-0.799-0.74-1.484-1.587-2.037-2.521l-0.028-0.052c-0.216-0.371-0.023-0.572 0.162-0.757 0.167-0.166 0.372-0.434 0.557-0.65 0.146-0.179 0.271-0.384 0.366-0.604l0.006-0.017c0.043-0.087 0.068-0.188 0.068-0.296 0-0.131-0.037-0.253-0.101-0.357l0.002 0.003c-0.094-0.186-0.836-2.014-1.145-2.758-0.302-0.724-0.609-0.625-0.836-0.637-0.216-0.010-0.464-0.012-0.712-0.012-0.395 0.010-0.746 0.188-0.988 0.463l-0.001 0.002c-0.802 0.761-1.3 1.834-1.3 3.023 0 0.026 0 0.053 0.001 0.079l-0-0.004c0.131 1.467 0.681 2.784 1.527 3.857l-0.012-0.015c1.604 2.379 3.742 4.282 6.251 5.564l0.094 0.043c0.548 0.248 1.25 0.513 1.968 0.74l0.149 0.041c0.442 0.14 0.951 0.221 1.479 0.221 0.303 0 0.601-0.027 0.889-0.078l-0.031 0.004c1.069-0.223 1.956-0.868 2.497-1.749l0.009-0.017c0.165-0.366 0.261-0.793 0.261-1.242 0-0.185-0.016-0.366-0.047-0.542l0.003 0.019c-0.092-0.155-0.34-0.247-0.712-0.434z" />
    </svg>
  )
}

// Ícone Meta SVG (do arquivo public/meta-logo-facebook-svgrepo-com.svg)
function MetaIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11" />
    </svg>
  )
}

// Ícone de IA inline SVG (ai_6.svg - estrela/brilho)
// color: 'white' para uso com fundo colorido, 'dynamic' para usar azul/cinza baseado em active
function AIIcon({ className = "w-4 h-4", active = true, color = "white" }: { className?: string; active?: boolean; color?: "white" | "dynamic" }) {
  const fillColor = color === "white" ? "white" : (active ? "#3b82f6" : "#888888")
  return (
    <svg
      viewBox="0 0 1080 1080"
      fill={fillColor}
      className={`${className} ${color === "white" ? (active ? 'opacity-100' : 'opacity-60') : ''}`}
    >
      <path d="M0 0 C17.32 10.35 22.3 38.27 28.19 56 C43.16 100.59 53.19 124.81 137.46 258.99 C234.51 344.23 368.73 396.8 426.75 425.19 C429.09 437.69 426.13 443.38 409 455.85 C361.52 475.48 297.31 499.23 255.76 519.13 C147.88 599.25 87.23 669.21 34.04 795.49 C14.94 849.69 10.85 859.25 -0.63 869 C-25.39 865.84 -41.29 832.75 -56.54 791.65 C-86.69 715.73 -151.25 619.5 -240.16 536.07 C-356.56 484.44 -426 459.63 -444.81 449.6 C-448.77 434.62 -432.64 415.05 -408.25 405.5 C-303.6 364.86 -219.25 313.5 -151.27 251.07 C-92.87 164.85 -48.99 59.56 -19.13 -0.25 C-5.99 -2.65 0 0 0 0 Z" transform="translate(477.25,178.5)"/>
      <path d="M0 0 C5.87 1.5 9.02 6.76 12.14 11.65 C40.68 81.19 73.94 130.75 118.94 167.75 C178.12 193.54 218.94 215.75 219.94 221.75 C210.47 229.25 175.66 245.02 162.76 250.61 C89.16 293.71 49.63 340.19 25.14 396.89 C2.94 437.75 -2.69 437.82 -8.06 437.75 C-17.06 408.49 -51.73 327.75 -137.06 259.75 C-212.01 231.28 -217.68 223.22 -217.19 214.69 C-203.03 204.09 -166.14 189.08 -92.06 147.75 C-24.64 43.58 -9.76 8.56 0 0 Z" transform="translate(852.06,9.25)"/>
    </svg>
  )
}

// Componente de player de áudio customizado
interface AudioPlayerProps {
  src?: string | null
  duration?: number
  fromMe?: boolean
  messageId?: string
  initialTranscription?: string | null
}

function AudioPlayer({ src, duration, fromMe, messageId, initialTranscription }: AudioPlayerProps) {
  const { authenticatedFetch } = useAuth()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(duration || 0)
  const [error, setError] = useState(false)
  const [transcription, setTranscription] = useState<string | null>(initialTranscription || null)
  const [transcribing, setTranscribing] = useState(false)
  const [transcribeError, setTranscribeError] = useState<string | null>(null)
  const progressRef = useRef<HTMLDivElement>(null)

  const handleTranscribe = async () => {
    if (!messageId || transcribing) return
    setTranscribing(true)
    setTranscribeError(null)
    try {
      const res = await authenticatedFetch(`/api/whatsapp-transcribe/${messageId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.error || 'Falha na transcrição')
      setTranscription(data.transcription || '')
    } catch (err) {
      setTranscribeError(err instanceof Error ? err.message : 'Erro ao transcrever')
    } finally {
      setTranscribing(false)
    }
  }

  // Por padrão, sem click: ao montar o balão de áudio, transcreve automaticamente,
  // UMA vez. Não bloqueia (mostra "Transcrevendo…"); o backend cacheia em metadata,
  // então reabrir vem instantâneo. Pula se já tem transcrição.
  const autoTranscribedRef = useRef(false)
  useEffect(() => {
    if (!messageId || autoTranscribedRef.current) return
    if (!transcription && !transcribing) {
      autoTranscribedRef.current = true
      handleTranscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId])

  const formatTime = (seconds: number) => {
    if (!seconds || !isFinite(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const togglePlay = async () => {
    if (!audioRef.current || !src || error) return

    try {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        setIsLoading(true)
        await audioRef.current.play()
        setIsPlaying(true)
        setIsLoading(false)
      }
    } catch (err) {
      console.error('Erro ao reproduzir áudio:', err)
      setError(true)
      setIsLoading(false)
      setIsPlaying(false)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current && isFinite(audioRef.current.duration)) {
      setAudioDuration(audioRef.current.duration)
    }
    setIsLoading(false)
  }

  const handleCanPlay = () => {
    setIsLoading(false)
    setError(false)
  }

  const handleError = () => {
    console.error('Erro ao carregar áudio')
    setError(true)
    setIsLoading(false)
    setIsPlaying(false)
  }

  const handleEnded = () => {
    setIsPlaying(false)
    setCurrentTime(0)
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressRef.current || !src || error) return

    const rect = progressRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    const newTime = percentage * audioDuration

    if (isFinite(newTime)) {
      audioRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0

  // Fallback: se não tem src, mostrar placeholder - RESPONSIVO
  if (!src) {
    return (
      <div className="mb-1">
        <div className={`flex items-center gap-2 sm:gap-3 rounded-2xl px-2 sm:px-3 py-1.5 sm:py-2 min-w-[150px] sm:min-w-[200px] max-w-[220px] sm:max-w-[280px] ${
          fromMe ? 'bg-primary/20' : 'bg-muted/50'
        }`}>
          {/* Botão Play desabilitado */}
          <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
            fromMe
              ? 'bg-primary/50 text-primary-foreground'
              : 'bg-muted-foreground/20 text-muted-foreground'
          }`}>
            <FiPlay className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5 opacity-50" />
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <div className="h-1 sm:h-1.5 bg-muted-foreground/20 rounded-full" />
            <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
              <span>0:00</span>
              <span>{duration ? formatTime(duration) : '0:00'}</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-1">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onEnded={handleEnded}
        preload="metadata"
      />
      <div className={`flex items-center gap-2 sm:gap-3 rounded-2xl px-2 sm:px-3 py-1.5 sm:py-2 min-w-[150px] sm:min-w-[200px] max-w-[220px] sm:max-w-[280px] ${
        fromMe ? 'bg-primary/20' : 'bg-muted/50'
      }`}>
        {/* Botão Play/Pause com animação - RESPONSIVO */}
        <button
          onClick={togglePlay}
          disabled={isLoading || error}
          className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
            error
              ? 'bg-red-500/20 text-red-400 cursor-not-allowed'
              : fromMe
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105'
                : 'bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/30 hover:scale-105'
          } ${isPlaying ? 'animate-pulse' : ''}`}
        >
          {isLoading ? (
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : error ? (
            <FiPlay className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5 opacity-50" />
          ) : isPlaying ? (
            <FiPause className="w-4 h-4 sm:w-5 sm:h-5" />
          ) : (
            <FiPlay className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5" />
          )}
        </button>

        {/* Barra de progresso e tempo - RESPONSIVO */}
        <div className="flex-1 flex flex-col gap-0.5 sm:gap-1">
          {/* Barra de progresso clicável */}
          <div
            ref={progressRef}
            onClick={handleProgressClick}
            className={`h-1 sm:h-1.5 bg-muted-foreground/20 rounded-full relative overflow-hidden ${
              error ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          >
            {/* Barra de progresso preenchida */}
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-100 ${
                fromMe ? 'bg-primary' : 'bg-muted-foreground'
              }`}
              style={{ width: `${progress}%` }}
            />
            {/* Indicador de posição (bolinha) */}
            {!error && (
              <div
                className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full shadow-md transition-all duration-100 ${
                  fromMe ? 'bg-primary' : 'bg-muted-foreground'
                } ${isPlaying ? 'scale-110' : ''}`}
                style={{ left: `calc(${Math.min(progress, 100)}% - 6px)` }}
              />
            )}
          </div>

          {/* Tempo - RESPONSIVO */}
          <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
            <span className={isPlaying ? 'text-foreground font-medium' : ''}>
              {formatTime(currentTime)}
            </span>
            <span>{formatTime(audioDuration)}</span>
          </div>
        </div>
      </div>

      {/* Transcrição (Whisper local) */}
      {messageId && (
        <div className="mt-1.5">
          {transcription ? (
            <div className={`text-xs px-2.5 py-1.5 rounded-lg border ${
              fromMe ? 'bg-primary/10 border-primary/20' : 'bg-muted/30 border-border'
            }`}>
              <div className="italic"><span className="opacity-60 mr-1">🎤</span>{transcription}</div>
            </div>
          ) : (
            <button
              onClick={() => handleTranscribe()}
              disabled={transcribing}
              className={`text-[11px] sm:text-xs px-2 py-1 rounded-md transition-colors ${
                transcribing
                  ? 'bg-muted text-muted-foreground cursor-wait'
                  : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {transcribing ? 'Transcrevendo…' : 'Transcrever áudio'}
            </button>
          )}
          {transcribeError && (
            <div className="text-[10px] text-red-500 mt-1">{transcribeError}</div>
          )}
        </div>
      )}
    </div>
  )
}

export interface WhatsAppInboxPanelRef {
  openPromptModal: () => void
  markAllRead: () => void
  setSearch: (term: string) => void
}

export const WhatsAppInboxPanel = forwardRef<WhatsAppInboxPanelRef, WhatsAppInboxPanelProps>(function WhatsAppInboxPanel(
  { isOpen, onClose, deviceId, deviceAiEnabled = false, deviceApiType = 'evolution', embedded = false, onConversationRead, onAllConversationsRead, externalSearchTerm, externalOpenConversationId, onExternalOpenHandled, toolbarSlot },
  ref
) {
  const { authenticatedFetch } = useAuth()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  // Selection mode pra encaminhamento: toggle ativa checkbox nas msgs.
  // Click numa msg quando selectionMode=true alterna seleção em vez de comportamento normal.
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set())
  const [showForwardModal, setShowForwardModal] = useState(false)
  const toggleMessageSelect = (msgId: string) => {
    setSelectedMessageIds(prev => {
      const n = new Set(prev)
      n.has(msgId) ? n.delete(msgId) : n.add(msgId)
      return n
    })
  }
  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedMessageIds(new Set())
  }
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [typingConversations, setTypingConversations] = useState<Set<string>>(new Set())
  const [failedProfilePics, setFailedProfilePics] = useState<Set<string>>(new Set())
  const [showAssignLeaderModal, setShowAssignLeaderModal] = useState(false)
  // Perfil do contato como drawer deslizante em telas <lg (no desktop é coluna fixa).
  const [showContactDrawer, setShowContactDrawer] = useState(false)
  // Lista de falhas de handoff vindas do webhook Meta (failed em mensagens de
  // template pra líder ou "Pronto" pra lead). Empilha — operador limpa clicando
  // X ou abrindo a conversa pra reenviar. Caso real: Josimara 26/05 9:44.
  const [handoffFailures, setHandoffFailures] = useState<LeadHandoffDeliveryFailedEvent[]>([])
  const [heldLeads, setHeldLeads] = useState<LeadAutoAssignHeldEvent[]>([])
  // Telefones alternativos da líder por messageId — cacheado em demanda quando
  // o erro é 131026 (número inválido). Cada item é { source, formatted, valid }.
  type AltPhone = { source: 'celular' | 'telefone1' | 'telefone2'; formatted: string; valid: boolean }
  const [handoffAltPhones, setHandoffAltPhones] = useState<Record<string, AltPhone[]>>({})
  const [handoffRetrying, setHandoffRetrying] = useState<Record<string, boolean>>({})
  const [showAgentPromptModal, setShowAgentPromptModal] = useState(false)
  const [agentData, setAgentData] = useState<{ id?: string; name?: string; description?: string; system_prompt?: string; model?: string; welcome_message?: string; temperature?: number } | null>(null)
  const [agentLoading, setAgentLoading] = useState(false)
  const [agentEditing, setAgentEditing] = useState(false)
  const [agentEditPrompt, setAgentEditPrompt] = useState('')
  const [agentSaving, setAgentSaving] = useState(false)
  // Provider efetivo do bot (BOT_LLM_PROVIDER + cooldown 429). O `agent.model`
  // do DB pode mentir quando há override; este é o veredito real.
  const [llmConfig, setLlmConfig] = useState<{
    configured: 'claude' | 'ollama'
    effective: 'claude' | 'ollama'
    claude_model: string
    ollama_model: string
    cooldown_active: boolean
    cooldown_until: string | null
  } | null>(null)

  const handleOpenPromptModal = useCallback(async () => {
    setShowAgentPromptModal(true)
    setAgentLoading(true)
    setAgentData(null)
    setLlmConfig(null)
    // Provider efetivo em paralelo — não bloqueia o load do agente se falhar.
    authenticatedFetch('/api/bot/llm-config')
      .then(r => r.ok ? r.json() : null)
      .then(c => { if (c) setLlmConfig(c) })
      .catch(() => { /* silencioso — modal renderiza só agentData */ })
    try {
      let agentId: string | null = null
      let source = ''
      const r = await authenticatedFetch('/api/whatsapp-devices')
      const devices = await r.json()
      const device = Array.isArray(devices) ? devices.find((d: { id: string }) => d.id === deviceId) : null
      if (device?.default_agent_id) {
        agentId = device.default_agent_id
        source = 'dispositivo'
      } else {
        const ar = await authenticatedFetch('/api/agents')
        const al = await ar.json()
        const list = Array.isArray(al) ? al : (al?.data || [])
        const def = list.find((a: { is_default?: number }) => a.is_default === 1) || list[0]
        if (def) { agentId = def.id; source = 'padrão do usuário (fallback)' }
      }
      if (agentId) {
        const ar = await authenticatedFetch(`/api/agents/${agentId}`)
        const ad = await ar.json()
        const data = ad.data || ad
        setAgentData({ ...data, description: `${data.description || ''}${source ? ` · Origem: ${source}` : ''}`.trim() })
      } else {
        setAgentData({ name: 'Nenhum agente encontrado', system_prompt: 'Não há agente padrão configurado.' })
      }
    } catch {
      setAgentData({ name: 'Erro', system_prompt: 'Falha ao carregar dados do agente.' })
    } finally {
      setAgentLoading(false)
    }
  }, [authenticatedFetch, deviceId])

  // Ref para markAllActiveAsRead (definida mais abaixo — evita forward-reference)
  const markAllActiveAsReadRef = useRef<() => Promise<void>>(async () => {})

  useImperativeHandle(ref, () => ({
    openPromptModal: handleOpenPromptModal,
    markAllRead: () => markAllActiveAsReadRef.current(),
    setSearch: (term: string) => { setSearchInput(term); setSearchTerm(term) },
  }))

  // Sync external search term when changed by the device bar
  useEffect(() => {
    if (externalSearchTerm === undefined) return
    setSearchInput(externalSearchTerm)
    setSearchTerm(externalSearchTerm)
  }, [externalSearchTerm])

  // Banner sinalizando que o deep-link da Cobrança não achou a conversa na
  // lista carregada. Setado pelo useEffect abaixo quando search falha.
  const [deepLinkNotFound, setDeepLinkNotFound] = useState(false)

  // Deep-link externo: ao receber um conversationId, auto-seleciona a conversa.
  // 1) Procura na lista carregada (caso comum — conv ativa fica no topo).
  // 2) Fallback: fetch direto via /api/whatsapp-conversation/:id, injeta e seleciona.
  // 3) Se backend não achou (404), mostra banner pra o usuário buscar manualmente.
  useEffect(() => {
    if (!externalOpenConversationId) return
    if (isLoading) return
    let cancelled = false
    const open = async () => {
      const found = conversations.find(c => c.id === externalOpenConversationId)
      if (found) {
        setSelectedConversation(found)
        loadMessages(found)  // chat só atualiza se loadMessages for chamado explicitamente
        setDeepLinkNotFound(false)
        onExternalOpenHandled?.()
        return
      }
      // Não está na página atual — busca direto
      try {
        const r = await authenticatedFetch(`/api/whatsapp-conversation/${encodeURIComponent(externalOpenConversationId)}`)
        if (!r.ok) throw new Error('not_found')
        const conv = await r.json()
        if (cancelled || !conv?.id) return
        setConversations(prev => prev.some(c => c.id === conv.id) ? prev : [conv, ...prev])
        setSelectedConversation(conv)
        loadMessages(conv)
        setDeepLinkNotFound(false)
      } catch {
        if (!cancelled) {
          setDeepLinkNotFound(true)
          setSelectedConversation(null)
        }
      } finally {
        if (!cancelled) onExternalOpenHandled?.()
      }
    }
    open()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpenConversationId, conversations, isLoading])

  // Estado local para deviceAiEnabled (atualizado via prop E WebSocket)
  const [localDeviceAiEnabled, setLocalDeviceAiEnabled] = useState(deviceAiEnabled)

  // Sincronizar com a prop quando ela mudar
  useEffect(() => {
    setLocalDeviceAiEnabled(deviceAiEnabled)
  }, [deviceAiEnabled])

  // Estados para envio de mensagens
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)

  // Estado para modal de mídia expandida
  const [mediaModal, setMediaModal] = useState<{ url: string; type: 'image' | 'video' } | null>(null)

  // Total de não lidas do device (do servidor, inclui conversas não carregadas)
  const [deviceTotalUnread, setDeviceTotalUnread] = useState(0)

  // Contadores device-wide pros badges de filtro (Aguardando líder ⚑ / Convertidos 📈).
  // Vêm do servidor (/counts) porque a lista é paginada — contar no cliente subconta.
  // convertedIds: IDs das conversas convertidas (lista pequena) → o badge de Convertidos
  // é "novos desde que vi", comparando contra `seenConverted`.
  const [deviceCounts, setDeviceCounts] = useState<{ awaiting: number; assigned: number; converted: number; convertedIds: string[] }>({ awaiting: 0, assigned: 0, converted: 0, convertedIds: [] })

  // Convertidos já "vistos" pelo operador (por dispositivo, persistido em localStorage).
  // Badge de Convertidos = convertidos que ainda NÃO estão neste conjunto. Aguardando
  // líder NÃO usa isto — é total ao vivo de trabalho pendente (decisão de 2026-06-17).
  const [seenConverted, setSeenConverted] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!deviceId) { setSeenConverted(new Set()); return }
    try {
      const raw = localStorage.getItem(`wa_seen_converted_${deviceId}`)
      setSeenConverted(new Set(raw ? JSON.parse(raw) : []))
    } catch { setSeenConverted(new Set()) }
  }, [deviceId])
  const markConvertedSeen = (ids: string[]) => {
    if (!ids || ids.length === 0) return
    setSeenConverted(prev => {
      const next = new Set(prev)
      let changed = false
      ids.forEach(id => { if (!next.has(id)) { next.add(id); changed = true } })
      if (changed) {
        try { localStorage.setItem(`wa_seen_converted_${deviceId}`, JSON.stringify([...next])) } catch { /* quota/privado */ }
      }
      return changed ? next : prev
    })
  }

  // Estado para loading

  // Estado para busca de conversas (com debounce para performance)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Filtros da lista — multi-select: set vazio = "Todos" (sem filtro)
  // Filtros de tipo/status do contato (multi-select). 'awaiting' e 'converted'
  // são lifecycle (não-tipo) — filtragem client-side; os 4 primeiros vão via
  // param ?tipo= pro server. 'awaiting' = aguardando atribuição de líder OU já
  // atribuída; 'converted' = lead que virou consultora (converted_consultant_at).
  // Os antigos toggles de IA ativa / atendimento humano foram removidos em
  // 2026-05-27 — não eram úteis na operação real.
  type FType = 'empresario' | 'lider' | 'consultora' | 'lead' | 'awaiting' | 'converted'
  const [filterTypes, setFilterTypes] = useState<Set<FType>>(new Set())

  // Filtro de campanha — escopo do dropdown que substitui o "Todos" antigo
  type CampaignEntry = { template: string; contacts: number; last_used: string }
  const [filterCampaign, setFilterCampaign] = useState<string | null>(null)
  const [campaignsList, setCampaignsList] = useState<CampaignEntry[]>([])
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false)
  const campaignMenuRef = useRef<HTMLDivElement | null>(null)

  // Modo de visualização — toggle lista/kanban. Vale para qualquer device Meta:
  // cada um tem seus próprios modelos (escopo por device_id no backend
  // `/api/kanban/*`), então colunas e templates não vazam entre eles.
  //
  // [2026-08-20] Era uma lista fixa com os dois ids da matriz. Na praça Planalto
  // — mesmo arquivo, número próprio — isso desligava o kanban calado, sem nada
  // na tela dizendo por quê. Na matriz não muda nada: os dois devices de lá são
  // Meta e continuam com o toggle.
  const showKanbanToggle = deviceId.startsWith('meta_')
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list')
  // Se trocar de device pra um que não suporta kanban, força lista
  useEffect(() => { if (!showKanbanToggle) setViewMode('list') }, [showKanbanToggle])

  // Colunas retráteis (padrão "filipeta", portado do fin.twms em 2026-07-10):
  // lista de conversas vira trilho de siglas 68px; painel de perfil some 100%.
  // Controle é uma aba quadrada centrada na vertical, grudada na borda.
  const [listCollapsed, setListCollapsed] = useState(false)
  const [profileCollapsed, setProfileCollapsed] = useState(false)
  // Seção de notificações (falhas de entrega + leads segurados) recolhida numa
  // tira compacta com contadores — toggle pela filipeta, igual às laterais.
  // Portado do fin.twms em 2026-07-14.
  const [alertsCollapsed, setAlertsCollapsed] = useState(false)
  // No kanban sem conversa aberta a coluna da lista é o próprio board —
  // recolher deixaria a tela vazia; o colapso só vale em lista ou com chat aberto.
  const listShrunk = listCollapsed && (viewMode === 'list' || !!selectedConversation)

  // Colunas customizadas do kanban — primária (escopo do filtro atual) é sempre fixa;
  // até 3 customizáveis pelo usuário, totalizando 4. Estado local por enquanto (sem persistência).
  type KanbanColColor = 'sky' | 'emerald' | 'amber' | 'rose' | 'purple' | 'slate'
  type KanbanCol = { id: string; title: string; color: KanbanColColor }
  const MAX_KANBAN_COLS = 4
  const KANBAN_COLORS: Array<{ key: KanbanColColor; border: string; dot: string; label: string }> = [
    { key: 'sky',     border: 'border-t-sky-500',     dot: 'bg-sky-500',     label: 'Azul' },
    { key: 'emerald', border: 'border-t-emerald-500', dot: 'bg-emerald-500', label: 'Verde' },
    { key: 'amber',   border: 'border-t-amber-500',   dot: 'bg-amber-500',   label: 'Âmbar' },
    { key: 'rose',    border: 'border-t-rose-500',    dot: 'bg-rose-500',    label: 'Rosa' },
    { key: 'purple',  border: 'border-t-purple-500',  dot: 'bg-purple-500',  label: 'Roxo' },
    { key: 'slate',   border: 'border-t-slate-500',   dot: 'bg-slate-500',   label: 'Cinza' },
  ]
  const colorOf = (c: KanbanColColor) => KANBAN_COLORS.find(k => k.key === c) || KANBAN_COLORS[0]
  // Modelos persistidos no servidor — substituem customCols local.
  type KanbanModel = { id: string; user_id: string; device_id: string; scope: string | null; name: string; owner_username?: string; owner_name?: string; column_count?: number }
  type KanbanPlacement = { conversation_id: string; column_id: string; position: number }
  const [models, setModels] = useState<KanbanModel[]>([])
  const [activeModelId, setActiveModelId] = useState<string | null>(null)
  const [customCols, setCustomCols] = useState<KanbanCol[]>([])
  const [placements, setPlacements] = useState<KanbanPlacement[]>([])
  const [editingColId, setEditingColId] = useState<string | null>(null)
  const [editingModelId, setEditingModelId] = useState<string | null>(null)
  const [colorMenuColId, setColorMenuColId] = useState<string | null>(null)
  const [modelMenuOpen, setModelMenuOpen] = useState(false)
  const colorMenuRef = useRef<HTMLDivElement | null>(null)
  const modelMenuRef = useRef<HTMLDivElement | null>(null)
  // Guard contra click acidental disparado depois de um dragend (Chrome). Set true
  // brevemente após drag, bloqueia onClick por ~100ms.
  const justDraggedRef = useRef(false)
  const draggingIdRef = useRef<string | null>(null)
  // Hover de 500ms sobre uma coluna durante drag — pisca verde indicando alvo válido.
  // Key: '__primary__' pra coluna primária, ou o id da coluna custom.
  const [dropHoverKey, setDropHoverKey] = useState<string | null>(null)
  const dropHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Multi-seleção: ctrl/⌘+click toggle, shift+click range na mesma coluna.
  // Quando há seleção, arrastar qualquer card selecionado move todos juntos.
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set())
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  useEffect(() => {
    if (!colorMenuColId && !modelMenuOpen) return
    const onDown = (e: MouseEvent) => {
      if (colorMenuColId && colorMenuRef.current && !colorMenuRef.current.contains(e.target as Node)) {
        setColorMenuColId(null)
      }
      if (modelMenuOpen && modelMenuRef.current && !modelMenuRef.current.contains(e.target as Node)) {
        setModelMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [colorMenuColId, modelMenuOpen])

  // Resetar modelo ativo ao trocar de escopo (campanha ou device) — modelos são
  // vinculados a (deviceId, scope), então mudar escopo invalida a seleção atual.
  useEffect(() => {
    setActiveModelId(null)
    setCustomCols([])
    setPlacements([])
  }, [filterCampaign, deviceId])

  // Carregar modelos do escopo atual
  useEffect(() => {
    if (viewMode !== 'kanban' || !deviceId) return
    const scopeParam = filterCampaign ? `&scope=${encodeURIComponent(filterCampaign)}` : ''
    authenticatedFetch(`/api/kanban/models?deviceId=${encodeURIComponent(deviceId)}${scopeParam}`)
      .then(r => r.ok ? r.json() : { models: [] })
      .then(d => setModels(d.models || []))
      .catch(() => {})
  }, [viewMode, deviceId, filterCampaign, authenticatedFetch])

  // Tracking pra evitar refetch redundante das conversas placeadas
  const placedFetchedKeyRef = useRef<string>('')

  // Quando ativa um modelo, carregar suas colunas + placements + conversas placeadas.
  // O fetch das conversas placeadas é necessário porque a lista paginada normal pode
  // não conter cards que foram movidos quando o usuário tinha filtro de busca ativo.
  useEffect(() => {
    if (!activeModelId) { setCustomCols([]); setPlacements([]); return }
    const modelId = activeModelId
    Promise.all([
      authenticatedFetch(`/api/kanban/models/${modelId}`).then(r => r.ok ? r.json() : null),
      authenticatedFetch(`/api/kanban/models/${modelId}/conversations`).then(r => r.ok ? r.json() : { conversations: [] })
    ]).then(([modelData, placedData]) => {
      if (modelId !== activeModelId) return // mudou enquanto carregava
      if (modelData) {
        setCustomCols((modelData.columns || []).map((c: any) => ({ id: c.id, title: c.title, color: c.color as KanbanColColor })))
        setPlacements(modelData.placements || [])
      }
      // Merge conversas placeadas no estado local: adiciona qualquer conv ausente da página atual
      const placedConvs = placedData.conversations || []
      if (placedConvs.length > 0) {
        setConversations(prev => {
          const existingIds = new Set(prev.map(c => c.id))
          const additions = placedConvs.filter((c: any) => !existingIds.has(c.id))
          if (additions.length === 0) return prev
          return sortConversations([...prev, ...additions])
        })
      }
    }).catch(() => {})
  }, [activeModelId, authenticatedFetch])

  // Quando uma falha de handoff com erro 131026 (número inválido) chega, busca
  // os outros telefones do cadastro da líder pra oferecer retry. Casa real
  // LUANA 2026-05-28: telefone1 99158-0435 deu 131026, telefone2 99237-6166
  // ainda não testado. Sem este lookup, operador não sabe da alternativa.
  useEffect(() => {
    const candidates = handoffFailures.filter(f =>
      f.errorCode === 131026 &&
      f.source === 'lead_handoff_template' &&
      (f.leaderId || f.leaderCode) &&
      !handoffAltPhones[f.messageId]
    )
    if (candidates.length === 0) return
    candidates.forEach(async (f) => {
      try {
        const idOrCode = f.leaderId || f.leaderCode
        const resp = await authenticatedFetch(`/api/leaders/${encodeURIComponent(idOrCode || '')}/phones`)
        if (!resp.ok) return
        const data = await resp.json()
        const usedSource = f.leaderPhoneSource
        const alts: AltPhone[] = (data.phones || [])
          .filter((p: AltPhone) => p.valid && p.source !== usedSource)
        setHandoffAltPhones(prev => ({ ...prev, [f.messageId]: alts }))
      } catch { /* silent — banner segue com 'sem alternativa' */ }
    })
  }, [handoffFailures, handoffAltPhones, authenticatedFetch])

  // Retry de handoff usando um telefone alternativo do cadastro da líder.
  // Reusa POST /handoff-leader com leaderPhoneSource (qual campo do cadastro
  // tentar) e skipLeadNotify=true (lead já foi avisada na 1ª tentativa).
  const retryHandoffWithPhone = useCallback(async (
    failure: LeadHandoffDeliveryFailedEvent,
    phoneSource: 'celular' | 'telefone1' | 'telefone2'
  ) => {
    if (!failure.leadConversationId || !failure.deviceId) return
    const key = failure.messageId
    setHandoffRetrying(prev => ({ ...prev, [key]: true }))
    try {
      const resp = await authenticatedFetch(
        `/api/conversations/${encodeURIComponent(failure.leadConversationId)}/handoff-leader`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            leaderId: failure.leaderId || failure.leaderCode,
            deviceId: failure.deviceId,
            leaderPhoneSource: phoneSource,
            skipLeadNotify: true
          })
        }
      )
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data?.leaderTemplateSent) {
        // Sucesso: remove a falha do banner e persiste o dismiss. O webhook ainda
        // pode marcar como failed depois (assíncrono) — aí volta a aparecer no
        // banner com o novo messageId. Otimista por enquanto pra o operador ver progresso.
        dismissHandoffFailure(key)
      } else {
        const reason = data?.errors?.[0] || 'Sem resposta clara do servidor'
        alert(`Retry falhou: ${reason}`)
      }
    } catch (e) {
      alert(`Erro de rede no retry: ${(e as Error).message}`)
    } finally {
      setHandoffRetrying(prev => { const next = { ...prev }; delete next[key]; return next })
    }
  }, [authenticatedFetch])

  // Dispensa uma falha de handoff: remove do banner E persiste no backend pra não
  // reaparecer no próximo load. Usado tanto pelo X quanto pelo retry bem-sucedido.
  const dismissHandoffFailure = useCallback((messageId: string) => {
    setHandoffFailures(prev => prev.filter(x => x.messageId !== messageId))
    authenticatedFetch(`/api/handoff-failures/${encodeURIComponent(messageId)}/dismiss`, { method: 'POST' })
      .catch(err => console.error('Erro ao dispensar falha de handoff:', err))
  }, [authenticatedFetch])

  // Limpa TODAS as falhas de uma vez — pedido da Juliane (27/07): dispensar uma a
  // uma num X de 12px não escala quando o backlog carrega 10+ itens por reload.
  const dismissAllHandoffFailures = useCallback(() => {
    setHandoffFailures([])
    authenticatedFetch('/api/handoff-failures/dismiss-all', { method: 'POST' })
      .catch(err => console.error('Erro ao limpar falhas de handoff:', err))
  }, [authenticatedFetch])

  // Carrega falhas pendentes no mount — cobre falhas que ocorreram enquanto o
  // operador estava desconectado (o evento socket só chega a quem está online).
  useEffect(() => {
    authenticatedFetch('/api/handoff-failures')
      .then(r => r.ok ? r.json() : { failures: [] })
      .then(data => {
        if (Array.isArray(data.failures) && data.failures.length > 0) {
          setHandoffFailures(prev => {
            const seen = new Set(prev.map(f => f.messageId))
            const fresh = data.failures.filter((f: LeadHandoffDeliveryFailedEvent) => !seen.has(f.messageId))
            return [...prev, ...fresh].slice(0, 10)
          })
        }
      })
      .catch(err => console.error('Erro ao carregar falhas de handoff:', err))
  }, [authenticatedFetch])

  // Dispensa o aviso de lead segurado (operador clicou no X ou já atribuiu manual).
  const releaseHeldLead = useCallback((conversationId: string) => {
    setHeldLeads(prev => prev.filter(x => x.conversationId !== conversationId))
    authenticatedFetch(`/api/handoff-held/${encodeURIComponent(conversationId)}/release`, { method: 'POST' })
      .catch(err => console.error('Erro ao dispensar lead segurado:', err))
  }, [authenticatedFetch])

  // Limpa TODOS os avisos de lead segurado de uma vez (mesmo motivo do dismiss-all).
  const releaseAllHeldLeads = useCallback(() => {
    setHeldLeads([])
    authenticatedFetch('/api/handoff-held/release-all', { method: 'POST' })
      .catch(err => console.error('Erro ao limpar leads segurados:', err))
  }, [authenticatedFetch])

  // Carrega leads segurados pelo auto-assign no mount (mesmo motivo das falhas).
  useEffect(() => {
    authenticatedFetch('/api/handoff-held')
      .then(r => r.ok ? r.json() : { held: [] })
      .then(data => {
        if (Array.isArray(data.held) && data.held.length > 0) {
          setHeldLeads(prev => {
            const seen = new Set(prev.map(h => h.conversationId))
            const fresh = data.held.filter((h: LeadAutoAssignHeldEvent) => !seen.has(h.conversationId))
            return [...prev, ...fresh].slice(0, 20)
          })
        }
      })
      .catch(err => console.error('Erro ao carregar leads segurados:', err))
  }, [authenticatedFetch])

  // Ref pra evitar refetch concorrente do auto-load do kanban (declaração da função
  // está mais abaixo, depois de sortConversations, pra evitar TDZ).
  const isLoadingMoreRef = useRef(false)

  // Sempre que `placements` muda OU `conversations` é recarregada (busca/filtro),
  // re-baixa as conversas faltantes pra que cards do kanban não sumam de vista.
  useEffect(() => {
    if (!activeModelId || placements.length === 0) return
    const placedIds = placements.map(p => p.conversation_id)
    const existing = new Set(conversations.map(c => c.id))
    const missing = placedIds.filter(id => !existing.has(id))
    if (missing.length === 0) return
    // Evita refetch repetido pro mesmo set
    const key = `${activeModelId}:${missing.sort().join(',')}`
    if (placedFetchedKeyRef.current === key) return
    placedFetchedKeyRef.current = key
    authenticatedFetch(`/api/kanban/models/${activeModelId}/conversations`)
      .then(r => r.ok ? r.json() : { conversations: [] })
      .then(d => {
        setConversations(prev => {
          const existingIds = new Set(prev.map(c => c.id))
          const additions = (d.conversations || []).filter((c: any) => !existingIds.has(c.id))
          if (additions.length === 0) return prev
          return sortConversations([...prev, ...additions])
        })
      })
      .catch(() => {})
    // sortConversations é estável (useCallback []), omitido das deps pra evitar TDZ
    // (declarado em ordem posterior no componente). eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModelId, placements, conversations, authenticatedFetch])

  const createModel = async (name: string) => {
    const r = await authenticatedFetch('/api/kanban/models', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, scope: filterCampaign || null, name })
    })
    if (!r.ok) return
    const { model } = await r.json()
    setModels(prev => [...prev, model])
    setActiveModelId(model.id)
  }
  const renameModel = async (id: string, name: string) => {
    if (!name.trim()) return
    await authenticatedFetch(`/api/kanban/models/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: name.trim() })
    })
    setModels(prev => prev.map(m => m.id === id ? { ...m, name: name.trim() } : m))
  }
  const deleteModel = async (id: string) => {
    if (!confirm('Excluir esse modelo? Os cards voltam à coluna principal.')) return
    await authenticatedFetch(`/api/kanban/models/${id}`, { method: 'DELETE' })
    setModels(prev => prev.filter(m => m.id !== id))
    if (activeModelId === id) { setActiveModelId(null); setCustomCols([]); setPlacements([]) }
  }
  const addKanbanCol = async () => {
    if (!activeModelId || customCols.length >= MAX_KANBAN_COLS - 1) return
    const usedColors = new Set(customCols.map(c => c.color))
    const nextColor = (KANBAN_COLORS.find(k => !usedColors.has(k.key))?.key) || 'sky'
    const r = await authenticatedFetch(`/api/kanban/models/${activeModelId}/columns`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: 'Nova coluna', color: nextColor })
    })
    if (!r.ok) return
    const { column } = await r.json()
    setCustomCols(prev => [...prev, { id: column.id, title: column.title, color: column.color as KanbanColColor }])
  }
  const renameKanbanCol = async (id: string, title: string) => {
    const trimmed = title.trim()
    if (!trimmed) return
    setCustomCols(prev => prev.map(c => c.id === id ? { ...c, title: trimmed } : c))
    await authenticatedFetch(`/api/kanban/columns/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: trimmed })
    })
  }
  const recolorKanbanCol = async (id: string, color: KanbanColColor) => {
    setCustomCols(prev => prev.map(c => c.id === id ? { ...c, color } : c))
    await authenticatedFetch(`/api/kanban/columns/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ color })
    })
  }
  const removeKanbanCol = async (id: string) => {
    setCustomCols(prev => prev.filter(c => c.id !== id))
    setPlacements(prev => prev.filter(p => p.column_id !== id))
    await authenticatedFetch(`/api/kanban/columns/${id}`, { method: 'DELETE' })
  }
  // Move um ou mais cards. columnId=null → tira do modelo (volta pra primária).
  // Optimistic update + rollback do batch inteiro se qualquer request falhar.
  const moveCards = async (conversationIds: string[], columnId: string | null) => {
    if (!activeModelId || conversationIds.length === 0) return
    const modelIdAtMove = activeModelId
    const prevPlacements = placements
    setPlacements(prev => {
      const filtered = prev.filter(p => !conversationIds.includes(p.conversation_id))
      if (columnId === null) return filtered
      const additions = conversationIds.map((id, i) => ({
        conversation_id: id, column_id: columnId, position: i
      }))
      return [...filtered, ...additions]
    })
    try {
      await Promise.all(conversationIds.map((id, i) =>
        authenticatedFetch('/api/kanban/cards/move', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ modelId: modelIdAtMove, conversationId: id, columnId, position: i })
        }).then(r => { if (!r.ok) throw new Error(`fail ${id}`) })
      ))
    } catch (err: any) {
      if (activeModelId === modelIdAtMove) setPlacements(prevPlacements)
      console.error('Erro ao mover cards:', err?.message || err)
    }
  }
  const moveCard = (conversationId: string, columnId: string | null) =>
    moveCards([conversationId], columnId)

  const toggleFType = (v: FType) => {
    // Abrir o filtro Convertidos = "vi os novos" → marca todos como vistos (badge zera).
    if (v === 'converted' && !filterTypes.has('converted')) markConvertedSeen(deviceCounts.convertedIds)
    setFilterTypes(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n })
  }

  // Debounce do termo de busca (300ms)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput)
    }, 300)
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchInput])

  // MySQL guarda bot_paused_until como UTC mas sem sufixo 'Z'. Forçar UTC para evitar
  // que o browser some o offset local (UTC-4 = +240min). Definido aqui pois é usado
  // em filteredConversations antes dos helpers mais abaixo.
  const parsePausedUntil = (s: string) => new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z')

  // Filtros: client-side como preview instantâneo + server-side para resultado completo.
  // Ao mudar o filtro, o useEffect re-carrega do servidor (para buscar TODAS as conversas que
  // batem, não só as 30 da página). Enquanto carrega, o filtro local mostra o subconjunto
  // das conversas já em memória — feedback imediato sem tela vazia.
  const filteredConversations = conversations.filter(conv => {
    // Conversas já atribuídas a uma líder ficam OCULTAS da lista principal por default
    // — operador passou a bola, não precisa mais agir. Toggles "Aguardando líder" e
    // "Convertidos" reabrem essa visão: o primeiro mostra atribuídas pendentes, o
    // segundo mostra leads que viraram consultora (toda convertida foi atribuída antes).
    // EXCEÇÃO: se há mensagem não lida (cliente voltou a falar), a conversa REAPARECE
    // mesmo atribuída — senão a resposta do cliente fica invisível pro operador.
    if (conv.assigned_leader && !filterTypes.has('awaiting') && !filterTypes.has('converted') && !(conv.unread_count > 0)) return false

    if (filterTypes.size > 0) {
      const t = conv.tipo_consultor
      // 'awaiting' atravessa tipo: passa qualquer conv com awaiting_leader OU
      // assigned_leader (todo lead numa fase de espera de líder).
      // 'converted' atravessa tipo também: passa qualquer conv com
      // converted_consultant_at (era lead, virou consultora — celebração).
      const match =
        (filterTypes.has('empresario') && t === '1') ||
        (filterTypes.has('lider')      && t === '2') ||
        (filterTypes.has('consultora') && t === '3') ||
        (filterTypes.has('lead')       && (t == null || t === '')) ||
        (filterTypes.has('awaiting')   && (!!conv.awaiting_leader || !!conv.assigned_leader)) ||
        (filterTypes.has('converted')  && !!conv.converted_consultant_at)
      if (!match) return false
    }
    return true
  })

  // Estados para paginação numérica - Ativas
  const [currentPage, setCurrentPage] = useState(1)
  const [totalConversations, setTotalConversations] = useState<number | null>(null)
  const CONVERSATIONS_PER_PAGE = 30 // Reduzido de 100 para melhor performance

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const attachMenuRef = useRef<HTMLDivElement>(null)

  // Auto-scroll inteligente: se o usuário está perto do fim, segue automaticamente.
  // Se rolou pra ler mensagens antigas, segura o scroll e mostra contador de novas.
  const [isNearBottom, setIsNearBottom] = useState(true)
  const [unseenCount, setUnseenCount] = useState(0)

  // Função de ordenação inteligente: não lidas no topo, depois por data
  // Movida para cima para ser usada no handleNewMessage
  const sortConversations = useCallback((convs: Conversation[]): Conversation[] => {
    // Ordenação puramente cronológica (mais recentes primeiro), igual WhatsApp Web:
    // qualquer interação nova atualiza last_message_at no backend e a conversa
    // sobe pro topo naturalmente — não precisa de critério adicional pra "não lidas".
    return [...convs].sort((a, b) => {
      const aTime = new Date(a.last_message_at || 0).getTime()
      const bTime = new Date(b.last_message_at || 0).getTime()
      return bTime - aTime
    })
  }, [])

  // Abre uma conversa pelo id, esteja ela carregada ou não.
  //
  // Os alertas do topo (lead segurado pelo auto-assign, falha de entrega) vêm do
  // servidor e são independentes da lista, que carrega 30 por vez. Um lead
  // segurado ontem está listado no alerta e fora da página — e o clique fazia
  // `conversations.find(...)` devolver undefined, com um `if (conv)` sem else:
  // nada acontecia, sem erro, sem explicação. Quanto mais antigo o aviso, mais
  // certo o silêncio; e é o aviso antigo que mais precisa de ação.
  //
  // Busca por `?id=` no servidor, injeta na lista e seleciona. `dispositivo`
  // vem do próprio alerta: o registro guarda o device de origem, que nem sempre
  // é o device aberto agora.
  const openConversationById = useCallback(async (conversationId: string, dispositivo?: string | null) => {
    const known = conversations.find(c => c.id === conversationId)
    // loadMessages, não setSelectedConversation: só setar a seleção troca o
    // cabeçalho mas deixa o CORPO com as mensagens da conversa anterior (bug
    // dos alertas de lead segurado, 2026-07-31). Mesmo padrão do deep-link.
    if (known) { loadMessages(known); return }
    const dev = dispositivo || deviceId
    if (!dev) return
    try {
      const r = await authenticatedFetch(`/api/whatsapp-conversations/${dev}?id=${encodeURIComponent(conversationId)}`)
      const d = r.ok ? await r.json() : null
      const conv = d?.conversations?.[0]
      if (!conv) {
        // Silêncio aqui foi o bug. Se a conversa sumiu de verdade, dizer isso.
        setError('Conversa não encontrada — pode ter sido removida.')
        return
      }
      setConversations(prev => prev.some(c => c.id === conv.id) ? prev : sortConversations([...prev, conv]))
      loadMessages(conv)
    } catch {
      setError('Não foi possível abrir a conversa.')
    }
    // loadMessages fica fora das deps de propósito (recriada a cada render;
    // usa setState funcional e refs estáveis — mesma exceção do deep-link).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, deviceId, authenticatedFetch, sortConversations])

  // Append da próxima página de conversas (não substitui — empilha pro auto-load do kanban).
  // Declarado aqui (não junto dos outros loaders no topo) pra usar `sortConversations` sem TDZ.
  const loadMoreConversations = async () => {
    if (isLoadingMoreRef.current) return
    if (!deviceId) return
    if (totalConversations !== null && conversations.length >= totalConversations) return
    isLoadingMoreRef.current = true
    try {
      const nextOffset = conversations.length
      const qParam = searchTerm.trim() ? `&q=${encodeURIComponent(searchTerm.trim())}` : ''
      const tipoMap: Record<FType, string> = { empresario: '1', lider: '2', consultora: '3', lead: 'lead', awaiting: '', converted: 'converted' }
      const tipoParam = filterTypes.size > 0 ? `&tipo=${Array.from(filterTypes).map(t => tipoMap[t]).filter(Boolean).join(',')}` : ''
      const campaignParam = filterCampaign ? `&campaign=${encodeURIComponent(filterCampaign)}` : ''
      const r = await authenticatedFetch(`/api/whatsapp-conversations/${deviceId}?limit=${CONVERSATIONS_PER_PAGE}&offset=${nextOffset}${qParam}${tipoParam}${campaignParam}`)
      if (!r.ok) return
      const data = await r.json()
      const newConvs: Conversation[] = Array.isArray(data) ? data : (data.conversations || [])
      if (newConvs.length === 0) return
      setConversations(prev => {
        const existing = new Set(prev.map(c => c.id))
        const additions = newConvs.filter(c => !existing.has(c.id))
        if (additions.length === 0) return prev
        return sortConversations([...prev, ...additions])
      })
    } finally {
      isLoadingMoreRef.current = false
    }
  }

  // Auto-load no kanban: quando coluna primária esvazia (<5 cards), puxa próxima página.
  // (Em kanban com filterCampaign, o initial load já trouxe tudo então isso não dispara.)
  useEffect(() => {
    if (viewMode !== 'kanban') return
    if (totalConversations !== null && conversations.length >= totalConversations) return
    const customColIds = new Set(customCols.map(c => c.id))
    const primaryCount = conversations.filter(c => {
      const placedColId = placements.find(p => p.conversation_id === c.id)?.column_id
      return !placedColId || !customColIds.has(placedColId)
    }).length
    if (primaryCount < 5) {
      loadMoreConversations()
    }
  }, [viewMode, conversations, placements, customCols, totalConversations])

  // Infinite scroll na lista: sentinel no fim → IntersectionObserver dispara load
  const listSentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (viewMode !== 'list') return
    const el = listSentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadMoreConversations()
      }
    }, { rootMargin: '200px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [viewMode, conversations.length, totalConversations])

  // Callbacks para eventos WebSocket
  // OTIMIZAÇÃO: Usar refs para evitar re-criação do callback quando listas mudam
  const selectedConversationRef = useRef(selectedConversation)
  selectedConversationRef.current = selectedConversation
  // Refs com os filtros ativos pro WebSocket handler decidir se injeta conversa nova.
  // Sem isso, o useCallback fica com closure stale e qualquer filtro é ignorado em tempo real.
  const activeFiltersRef = useRef({ campaign: filterCampaign, types: filterTypes, search: searchTerm })
  activeFiltersRef.current = { campaign: filterCampaign, types: filterTypes, search: searchTerm }

  // Recarga da lista pedida pelo tempo real. Com filtro ligado, a tela não tem
  // como saber se a conversa que chegou pertence à view — quem sabe reaplicar o
  // filtro é o servidor. Então em vez de descartar o evento, a gente repergunta.
  //
  // Ref porque `handleNewMessage` é memoizado e `loadConversations` nasce de
  // novo a cada render, com o `searchTerm` daquele render dentro. Guardar a
  // função direto na closure congelaria a busca do primeiro render.
  const recarregaFiltradaRef = useRef<() => void>(() => {})
  const recargaAgendadaRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Estrangulada: um disparo de 250 pessoas gera 250 eventos, um a cada 2s. Sem
  // teto seriam 250 recargas. Com ele é uma a cada 4s enquanto durar a rajada.
  const pedeRecargaFiltrada = useCallback(() => {
    if (recargaAgendadaRef.current) return
    recargaAgendadaRef.current = setTimeout(() => {
      recargaAgendadaRef.current = null
      recarregaFiltradaRef.current()
    }, 4000)
  }, [])
  useEffect(() => () => { if (recargaAgendadaRef.current) clearTimeout(recargaAgendadaRef.current) }, [])

  // Status delivery em tempo real (sent → delivered → read).
  // Atualiza só a mensagem específica (match por message_id da Meta) sem recarregar nada.
  const handleMessageStatusUpdate = useCallback((data: MessageStatusUpdateEvent) => {
    setMessages(prev =>
      prev.map(m => m.message_id === data.messageId ? { ...m, status: data.status } : m)
    )
  }, [])

  const handleLeadHandoffDeliveryFailed = useCallback((data: LeadHandoffDeliveryFailedEvent) => {
    setHandoffFailures(prev => {
      // dedupe por messageId
      if (prev.some(f => f.messageId === data.messageId)) return prev
      return [data, ...prev].slice(0, 10)
    })
  }, [])

  const handleLeadAutoAssignHeld = useCallback((data: LeadAutoAssignHeldEvent) => {
    setHeldLeads(prev => {
      // dedupe por conversationId (re-segurar o mesmo lead atualiza, não duplica)
      const rest = prev.filter(h => h.conversationId !== data.conversationId)
      return [data, ...rest].slice(0, 20)
    })
  }, [])

  // Lead segurado ganhou líder (aqui ou em outra aba/operador) — some do banner
  // na hora, sem esperar F5. O backend emite após persistir a atribuição.
  const handleLeadAutoAssignReleased = useCallback((data: LeadAutoAssignReleasedEvent) => {
    setHeldLeads(prev => prev.filter(h => h.conversationId !== data.conversationId))
  }, [])

  const handleNewMessage = useCallback((data: ConversationUpdate) => {
    const currentSelectedConv = selectedConversationRef.current
    const isCurrentConversation = currentSelectedConv?.id === data.conversationId

    // Se a mensagem é da conversa selecionada e não é nossa, marcar como lida
    if (isCurrentConversation && !data.message.fromMe) {
      // Marcar como lida no backend (o backend já incrementou, então resetamos)
      authenticatedFetch(`/api/whatsapp-conversations/${data.conversationId}/read`, { method: 'POST' })
        .catch(err => console.error('Erro ao marcar como lida:', err))
    }

    // Atualizar lista de conversas ativas (usar callback funcional para acessar estado atual)
    setConversations(prev => {
      const isInActiveList = prev.some(c => c.id === data.conversationId)

      if (!isInActiveList) {
        // Conversa fora da lista carregada. Com filtro (campanha, tipo, busca)
        // não dá pra decidir aqui se ela pertence à view, então pedimos a lista
        // de novo ao servidor, que reaplica o filtro.
        //
        // Isto era um `return prev` seco, e era a razão de o Planalto trabalhar
        // apertando F5: lá se opera filtrado por campanha o dia inteiro, então
        // NENHUMA conversa nova entrava — nem as respostas de verdade.
        const f = activeFiltersRef.current
        const hasRestrictiveFilter = !!f.campaign || f.types.size > 0 || (f.search && f.search.trim().length > 0)
        if (hasRestrictiveFilter) { pedeRecargaFiltrada(); return prev }

        // `fromMe` também entra. Numa praça de campanha a conversa NASCE de um
        // disparo nosso: exigir mensagem recebida aqui descartava justamente a
        // linha que a operadora está esperando ver.
        if (data.conversation) {
          const newConv: Conversation = {
            id: data.conversationId,
            device_id: deviceId,
            remote_jid: data.conversation.remote_jid,
            remote_name: data.conversation.remote_name || data.message.pushName || null,
            remote_profile_pic: data.conversation.remote_profile_pic || null,
            last_message: data.message.content,
            last_message_at: data.message.timestamp,
            // Mensagem nossa não gera não-lida: ninguém precisa "ler" o que
            // acabou de enviar.
            unread_count: data.message.fromMe || isCurrentConversation ? 0 : 1,
            last_message_from_me: data.message.fromMe ? 1 : 0,
            is_bot_active: data.conversation.is_bot_active ?? 1,
            bot_status: (data.conversation.bot_status || 'active') as 'active' | 'paused' | 'human',
            created_at: data.message.timestamp,
            is_archived: 0,
            tipo_consultor: data.conversation.tipo_consultor || null,
            // Sem isto a conversa de um bloqueado que chega em tempo real nasce
            // SEM o badge vermelho e o operador pode responder na mão sem saber.
            // (O caminho de conversa já existente, abaixo, preserva via spread.)
            blocked: data.conversation.blocked || null
          }
          return sortConversations([...prev, newConv])
        }
        return prev
      }

      const updated = prev.map(conv => {
        if (conv.id === data.conversationId) {
          return {
            ...conv,
            last_message: data.message.content,
            last_message_at: data.message.timestamp,
            // Preenche tipo_consultor se ainda não temos (evita badge N ficar errada
            // para consultores quando entrada chega primeiro via socket)
            tipo_consultor: conv.tipo_consultor || data.conversation?.tipo_consultor || null,
            // [2026-08-20] Este handler serve aos TRÊS eventos: new_message (a
            // pessoa escreveu), message_sent (nós enviamos) e bot_response (o bot
            // respondeu). Sem guardar quem mandou, o campo ficava congelado no
            // último carregamento da lista e o `shouldPulse` — que é
            // `hasUnread && !last_message_from_me` — continuava verdadeiro depois
            // da nossa própria resposta: a conversa piscava pedindo atenção que
            // já tinha sido dada.
            last_message_from_me: data.message.fromMe ? 1 : 0,
            // E o contador só sobe com mensagem RECEBIDA. Antes subia com a nossa
            // também: o bot respondendo uma conversa fora da seleção aumentava o
            // badge de não-lidas dela.
            unread_count: data.message.fromMe
              ? 0
              : (isCurrentConversation ? 0 : (conv.unread_count || 0) + 1)
          }
        }
        return conv
      })
      // Reordenar: não lidas no topo, depois por data
      return sortConversations(updated)
    })

    // Se a mensagem é da conversa selecionada, adicionar às mensagens
    if (isCurrentConversation && currentSelectedConv) {
      const newMsg: Message = {
        id: data.message.id,
        conversation_id: data.conversationId,
        device_id: deviceId,
        message_id: null,
        remote_jid: currentSelectedConv.remote_jid,
        from_me: data.message.fromMe ? 1 : 0,
        fromMe: data.message.fromMe,
        content: data.message.content,
        message_type: data.message.messageType || 'text',
        mediaUrl: data.message.mediaUrl,
        media_url: data.message.mediaUrl,
        timestamp: data.message.timestamp,
        status: 'received',
        is_bot_response: data.message.isBotResponse ? 1 : 0,
        isBotResponse: data.message.isBotResponse,
        metadata: data.message.metadata || null
      }
      setMessages(prev => [...prev, newMsg])
    }
  }, [deviceId, authenticatedFetch, sortConversations, pedeRecargaFiltrada])

  const handleBotResponse = useCallback((data: ConversationUpdate) => {
    handleNewMessage(data)
  }, [handleNewMessage])

  const handleBotTyping = useCallback((data: BotTypingEvent) => {
    setTypingConversations(prev => {
      const next = new Set(prev)
      if (data.typing) {
        next.add(data.conversationId)
      } else {
        next.delete(data.conversationId)
      }
      return next
    })
  }, [])

  const handleConversationBotToggled = useCallback((data: ConversationBotToggledEvent) => {
    // Atualizar com novo sistema hierárquico
    const updates: Partial<Conversation> = {
      is_bot_active: data.isActive ? 1 : 0,
      bot_status: (data.botStatus || (data.isActive ? 'active' : 'paused')) as 'active' | 'paused' | 'human',
      bot_paused_reason: data.reason || null,
      bot_paused_until: null,  // Toggle manual limpa o timeout
      bot_paused_by: data.operator || null
    }

    setConversations(prev =>
      prev.map(conv =>
        conv.id === data.conversationId
          ? { ...conv, ...updates }
          : conv
      )
    )
    if (selectedConversation?.id === data.conversationId) {
      setSelectedConversation(prev =>
        prev ? { ...prev, ...updates } : null
      )
    }
  }, [selectedConversation])

  // Handler para human takeover (humano assumiu a conversa)
  const handleHumanTakeover = useCallback((data: HumanTakeoverEvent) => {
    const updates: Partial<Conversation> = {
      is_bot_active: 0,
      bot_status: 'human',
      bot_paused_until: data.pausedUntil,
      bot_paused_reason: 'human_takeover',
      bot_paused_by: data.operator || null
    }

    setConversations(prev =>
      prev.map(conv =>
        conv.id === data.conversationId
          ? { ...conv, ...updates }
          : conv
      )
    )
    if (selectedConversation?.id === data.conversationId) {
      setSelectedConversation(prev =>
        prev ? { ...prev, ...updates } : null
      )
    }
  }, [selectedConversation])

  // Handler para bot auto-reativado após timeout
  const handleBotAutoResumed = useCallback((data: BotAutoResumedEvent) => {
    const updates: Partial<Conversation> = {
      is_bot_active: 1,
      bot_status: 'active',
      bot_paused_until: null,
      bot_paused_reason: null,
      bot_paused_by: null
    }

    setConversations(prev =>
      prev.map(conv =>
        conv.id === data.conversationId
          ? { ...conv, ...updates }
          : conv
      )
    )
    if (selectedConversation?.id === data.conversationId) {
      setSelectedConversation(prev =>
        prev ? { ...prev, ...updates } : null
      )
    }
  }, [selectedConversation])

  // Handler para bot reativado manualmente
  const handleBotManualResumed = useCallback((data: BotManualResumedEvent) => {
    const updates: Partial<Conversation> = {
      is_bot_active: 1,
      bot_status: 'active',
      bot_paused_until: null,
      bot_paused_reason: null,
      bot_paused_by: null
    }

    setConversations(prev =>
      prev.map(conv =>
        conv.id === data.conversationId
          ? { ...conv, ...updates }
          : conv
      )
    )
    if (selectedConversation?.id === data.conversationId) {
      setSelectedConversation(prev =>
        prev ? { ...prev, ...updates } : null
      )
    }
  }, [selectedConversation])

  const handleConversationUpdated = useCallback((data: { conversationId: string, profilePic?: string }) => {
    // Atualizar foto de perfil na lista de conversas
    if (data.profilePic) {
      const profilePicUrl = data.profilePic
      setConversations(prev =>
        prev.map(conv =>
          conv.id === data.conversationId
            ? { ...conv, remote_profile_pic: profilePicUrl }
            : conv
        )
      )
      // Atualizar conversa selecionada também
      if (selectedConversation?.id === data.conversationId) {
        setSelectedConversation(prev =>
          prev ? { ...prev, remote_profile_pic: profilePicUrl } : null
        )
      }
    }
  }, [selectedConversation])

  // Handler para toggle master do device - atualiza TODAS as conversas
  const handleDeviceAIToggled = useCallback((data: DeviceAIToggledEvent) => {
    console.log('[WhatsAppInbox] Device AI toggled recebido:', data)
    console.log('[WhatsAppInbox] deviceId atual:', deviceId, ', deviceId do evento:', data.deviceId)

    // Verificar se o evento é para este device
    if (data.deviceId !== deviceId) {
      console.log('[WhatsAppInbox] Evento ignorado - deviceId diferente')
      return
    }

    const newStatus = data.enabled ? 'active' : 'paused'
    const newBotActive = data.enabled ? 1 : 0

    console.log(`[WhatsAppInbox] Atualizando estado do device para: ${data.enabled}`)

    // IMPORTANTE: Atualizar o estado local do device AI
    setLocalDeviceAiEnabled(data.enabled)

    // Atualizar TODAS as conversas para o novo estado
    setConversations(prev =>
      prev.map(conv => ({
        ...conv,
        is_bot_active: newBotActive,
        bot_status: newStatus as 'active' | 'paused' | 'human',
        bot_paused_until: null,
        bot_paused_reason: null,
        bot_paused_by: null
      }))
    )

    // Atualizar conversa selecionada também
    if (selectedConversation) {
      setSelectedConversation(prev =>
        prev ? {
          ...prev,
          is_bot_active: newBotActive,
          bot_status: newStatus as 'active' | 'paused' | 'human',
          bot_paused_until: null,
          bot_paused_reason: null,
          bot_paused_by: null
        } : null
      )
    }
  }, [deviceId, selectedConversation])

  // Conectar ao WebSocket
  const { isConnected, markRead } = useWhatsAppSocket({
    deviceId,
    autoConnect: isOpen,
    callbacks: {
      onNewMessage: handleNewMessage,
      onBotResponse: handleBotResponse,
      onBotTyping: handleBotTyping,
      onConversationBotToggled: handleConversationBotToggled,
      onConversationUpdated: handleConversationUpdated,
      onMessageSent: handleNewMessage,
      onHumanTakeover: handleHumanTakeover,
      onBotAutoResumed: handleBotAutoResumed,
      onBotManualResumed: handleBotManualResumed,
      onDeviceAIToggled: handleDeviceAIToggled,
      onMessageStatusUpdate: handleMessageStatusUpdate,
      onLeadHandoffDeliveryFailed: handleLeadHandoffDeliveryFailed,
      onLeadAutoAssignHeld: handleLeadAutoAssignHeld,
      onLeadAutoAssignReleased: handleLeadAutoAssignReleased
    }
  })

  // Resetar conversa selecionada quando mudar de dispositivo
  useEffect(() => {
    setSelectedConversation(null)
    setMessages([])
  }, [deviceId])

  // Carregar conversas quando abrir
  useEffect(() => {
    if (isOpen && deviceId) {
      loadConversations(1, true) // page=1, selectFirst=true para selecionar a primeira conversa automaticamente
    }
  }, [isOpen, deviceId])

  // Refazer busca server-side quando o termo mudar (após debounce de 300ms já aplicado em searchTerm).
  // Pula a primeira execução para não conflitar com o load inicial acima.
  const isFirstSearchRun = useRef(true)
  useEffect(() => {
    if (!isOpen || !deviceId) return
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false
      return
    }
    loadConversations(1, false, searchTerm)
  }, [searchTerm])

  // Recarregar server-side quando filtros mudarem ou alternar lista↔kanban
  // (kanban com campanha pede limit muito maior pra distribuir todos os contatos)
  const isFirstFilterRun = useRef(true)
  useEffect(() => {
    if (!isOpen || !deviceId) return
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false
      return
    }
    loadConversations(1, false, searchTerm)
  }, [filterTypes, filterCampaign, viewMode])

  // Carregar lista de campanhas (count = conversas distintas no device atual,
  // bate exato com o que o filtro vai retornar). Refetch ao trocar de device.
  useEffect(() => {
    if (!isOpen || !deviceId) return
    let cancelled = false
    authenticatedFetch(`/api/whatsapp/campaigns-list?deviceId=${encodeURIComponent(deviceId)}`)
      .then(r => r.ok ? r.json() : { campaigns: [] })
      .then(d => { if (!cancelled) setCampaignsList(d.campaigns || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [isOpen, deviceId, authenticatedFetch])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    if (!campaignMenuOpen) return
    const onDown = (e: MouseEvent) => {
      if (campaignMenuRef.current && !campaignMenuRef.current.contains(e.target as Node)) {
        setCampaignMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [campaignMenuOpen])

  // Auto-scroll inteligente:
  // - Se usuário está perto do fim (≤120px): segue automaticamente cada msg nova
  // - Se está lendo histórico: segura, incrementa contador de mensagens não vistas
  // - Trocar de conversa sempre vai pro fim (instantâneo) e zera contador
  const previousMessagesLengthRef = useRef(0)
  const previousConversationIdRef = useRef<string | null>(null)

  useEffect(() => {
    const isNewConversation = selectedConversation?.id !== previousConversationIdRef.current
    const messagesAdded = messages.length - previousMessagesLengthRef.current

    if (isNewConversation && selectedConversation && messages.length > 0) {
      // Trocou de conversa: scroll instantâneo pro fim, foca input, zera contador
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
        inputRef.current?.focus()
      }, 50)
      setIsNearBottom(true)
      setUnseenCount(0)
    } else if (messagesAdded > 0 && !isNewConversation) {
      // Mensagens novas chegaram na conversa atual
      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      } else {
        setUnseenCount(prev => prev + messagesAdded)
      }
    }

    previousMessagesLengthRef.current = messages.length
    previousConversationIdRef.current = selectedConversation?.id ?? null
  }, [messages, selectedConversation?.id, isNearBottom])

  // Detecta se o usuário está perto do fim do scroll (tolerância de 120px)
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const nearBottom = distanceFromBottom < 120
    setIsNearBottom(nearBottom)
    if (nearBottom) setUnseenCount(0)
  }, [])

  // Click no botão "novas mensagens": rola até o fim com smooth
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setUnseenCount(0)
  }, [])

  // Buscar total de não lidas do device (inclui conversas não carregadas)
  const fetchDeviceTotalUnread = async () => {
    try {
      const response = await authenticatedFetch(`/api/whatsapp-unread/${deviceId}`)
      if (response.ok) {
        const data = await response.json()
        setDeviceTotalUnread(Number(data.unreadCount) || 0)
      }
    } catch {
      // silencioso
    }
  }

  // Contadores device-wide (Aguardando líder / Convertidos) — server-side pra não
  // subcontar com a lista paginada. Mesma semântica do enrich da lista (ver endpoint /counts).
  const fetchDeviceCounts = async () => {
    try {
      const response = await authenticatedFetch(`/api/whatsapp-conversations/${deviceId}/counts`)
      if (response.ok) {
        const data = await response.json()
        const convertedIds: string[] = Array.isArray(data.convertedIds) ? data.convertedIds.map(String) : []
        setDeviceCounts({
          awaiting: Number(data.awaiting) || 0,
          assigned: Number(data.assigned) || 0,
          converted: Number(data.converted) || 0,
          convertedIds,
        })
        // Se o operador já está com o filtro Convertidos aberto, considera todos vistos
        // (ref fresca pra não pegar filterTypes stale do closure).
        if (activeFiltersRef.current.types.has('converted')) markConvertedSeen(convertedIds)
      }
    } catch {
      // silencioso
    }
  }

  // [CONTACT-BLOCKLIST 2026-07-16] Chamado quando a sidebar bloqueia o contato.
  // Update otimista (mesmo padrão do leaderAssigned logo abaixo): sem isso o
  // badge vermelho só apareceria no próximo F5 — e o operador que acabou de
  // bloquear ficaria sem confirmação visual de que pegou. O refetch em seguida
  // reconcilia com o servidor (que também desligou o bot da conversa).
  const refreshAfterBlock = (reason: string) => {
    const convId = selectedConversation?.id
    const optimistic = { reason: reason || null, blocked_at: new Date().toISOString(), blocked_by: null }
    setSelectedConversation(prev => prev ? { ...prev, blocked: optimistic, bot_status: 'paused' as const, is_bot_active: 0 } : prev)
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, blocked: optimistic, bot_status: 'paused' as const, is_bot_active: 0 } : c))
    loadConversations(1, false, searchTerm)
  }

  const loadConversations = async (page = 1, selectFirst = false, search: string = '') => {
    setIsLoading(true)
    setError(null)

    try {
      // No kanban com filtro de campanha (universo finito), carrega tudo de uma vez —
      // distribuição entre colunas precisa do total real, não da página atual.
      const effectiveLimit = viewMode === 'kanban' && filterCampaign ? 10000 : CONVERSATIONS_PER_PAGE
      const offset = (page - 1) * effectiveLimit
      const qParam = search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ''
      const tipoMap: Record<FType, string> = { empresario: '1', lider: '2', consultora: '3', lead: 'lead', awaiting: '', converted: 'converted' }
      const tipoParam = filterTypes.size > 0 ? `&tipo=${Array.from(filterTypes).map(t => tipoMap[t]).filter(Boolean).join(',')}` : ''
      const campaignParam = filterCampaign ? `&campaign=${encodeURIComponent(filterCampaign)}` : ''
      const response = await authenticatedFetch(`/api/whatsapp-conversations/${deviceId}?limit=${effectiveLimit}&offset=${offset}${qParam}${tipoParam}${campaignParam}`)
      if (!response.ok) throw new Error('Erro ao carregar conversas')
      const data = await response.json()

      // Suporta tanto formato antigo (array) quanto novo (com paginação)
      const newConversations = Array.isArray(data) ? data : data.conversations
      const pagination = Array.isArray(data) ? null : data.pagination

      const sortedConversations = sortConversations(newConversations)
      setConversations(sortedConversations)
      setCurrentPage(page)

      if (pagination?.total) {
        setTotalConversations(pagination.total)
      }

      // Buscar total de não lidas do device (para visibilidade do botão "marcar todas como lidas")
      fetchDeviceTotalUnread()
      // Contadores device-wide pros badges (independem da paginação/filtro)
      fetchDeviceCounts()

      // Selecionar primeira conversa automaticamente se solicitado
      if (selectFirst && sortedConversations.length > 0) {
        loadMessages(sortedConversations[0])
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // A recarga estrangulada acima chama SEMPRE a versão desta renderização,
  // com o filtro e a busca de agora.
  recarregaFiltradaRef.current = () => { if (isOpen && deviceId) loadConversations(1, false, searchTerm) }

  // Resync da lista ao (re)conectar o socket ou ao voltar o foco da aba.
  // Socket.IO NÃO reentrega eventos perdidos durante uma desconexão (sleep do
  // notebook, blip de rede, restart do backend). Sem este catch-up, leads novos
  // que chegaram na janela offline só apareciam após F5 manual — exatamente a
  // queixa de "tenho de dar F5 pra ver a lista atualizada".
  const hasConnectedOnceRef = useRef(false)
  useEffect(() => {
    if (!isConnected) return
    if (!hasConnectedOnceRef.current) {
      // 1ª conexão: o load inicial (efeito de isOpen/deviceId) já cobre.
      hasConnectedOnceRef.current = true
      return
    }
    // Reconexão real → recarrega a lista (respeita filtros/busca atuais),
    // sem mexer na conversa aberta.
    if (isOpen && deviceId) loadConversations(1, false, searchTerm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, isOpen, deviceId])

  // Aba volta a ficar visível (operador acordou o monitor / trocou de aba) →
  // resync leve pra pegar o que chegou enquanto a aba estava em background.
  useEffect(() => {
    if (!isOpen || !deviceId) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadConversations(1, false, searchTerm)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deviceId, searchTerm])

  // Excluir conversa permanentemente
  const deleteConversation = async (conversationId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()

    // Confirmação antes de excluir
    if (!confirm('Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      const response = await authenticatedFetch(`/api/whatsapp-conversations/${conversationId}`, {
        method: 'DELETE'
      })
      if (!response.ok) throw new Error('Erro ao excluir conversa')

      // Remover da lista ativa
      setConversations(prev => prev.filter(c => c.id !== conversationId))
      const newTotal = (totalConversations || 0) - 1
      setTotalConversations(newTotal)

      // Se era a conversa selecionada, limpar seleção
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
        setMessages([])
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  // Recarregar arquivadas APENAS na primeira vez que entrar na aba
  // (já carregamos no isOpen, então não precisa recarregar a cada troca de aba)
  // Removido para evitar chamadas desnecessárias à API

  const loadMessages = async (conversation: Conversation) => {
    setSelectedConversation(conversation)
    setIsLoading(true)
    try {
      const response = await authenticatedFetch(`/api/whatsapp-messages/${conversation.id}`)
      if (response.status === 404) {
        // Conversa não existe mais (provavelmente deletada por cleanup/merge).
        // Limpa seleção e remove da lista local pra não loopar.
        setSelectedConversation(null)
        setMessages([])
        setConversations(prev => prev.filter(c => c.id !== conversation.id))
        setError('Esta conversa não está mais disponível (pode ter sido mergeada com outra). Selecione outra.')
        return
      }
      if (!response.ok) throw new Error('Erro ao carregar mensagens')
      const data = await response.json()
      setMessages(data)

      // Só marcar como lida se tinha mensagens não lidas
      const hadUnreadMessages = (conversation.unread_count || 0) > 0

      // Marcar como lida
      await authenticatedFetch(`/api/whatsapp-conversations/${conversation.id}/read`, { method: 'POST' })
      markRead(conversation.id)

      // Atualizar contagem local e reordenar (a conversa lida pode descer)
      setConversations(prev =>
        sortConversations(prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c))
      )

      // Notificar o DevicesPanel para decrementar o badge pela contagem real
      if (hadUnreadMessages && onConversationRead) {
        onConversationRead(deviceId, conversation.unread_count || 1)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Marcar todas as conversas ativas como lidas (TODAS do device, não só as carregadas)
  const markAllActiveAsRead = async () => {
    // Otimista: resetar badge, total e estado local IMEDIATAMENTE (antes da API)
    setConversations(prev =>
      sortConversations(prev.map(c => ({ ...c, unread_count: 0 })))
    )
    setDeviceTotalUnread(0)
    if (onAllConversationsRead) {
      onAllConversationsRead(deviceId)
    }

    try {
      // Marcar TODAS as conversas ativas do device como lidas no servidor
      await authenticatedFetch(`/api/whatsapp-devices/${deviceId}/read-all`, { method: 'POST' })
    } catch (err) {
      console.error('Erro ao marcar todas ativas como lidas no servidor:', err)
      // Fallback: tentar marcar individualmente as conversas carregadas
      conversations.filter(c => (c.unread_count || 0) > 0).forEach(conv => {
        authenticatedFetch(`/api/whatsapp-conversations/${conv.id}/read`, { method: 'POST' }).catch(() => {})
      })
    }
  }

  // Manter ref atualizada para o useImperativeHandle
  markAllActiveAsReadRef.current = markAllActiveAsRead

  // Helper: Bot está efetivamente ativo?
  // Schema MySQL real: bot_status = 'enabled' | 'disabled' | 'paused'
  // Suporta legado ('active'/'human') para compatibilidade com dados antigos
  const isBotEffectivelyActive = (conversation: Conversation) => {
    if (!localDeviceAiEnabled) return false
    const s = conversation.bot_status
    const isEnabled = s === 'enabled' || s === 'active'
    if (!isEnabled) return false
    if (conversation.bot_paused_until) {
      return parsePausedUntil(conversation.bot_paused_until).getTime() <= Date.now()
    }
    return true
  }

  // Helper: Está em human takeover (bot_paused_until no futuro)
  const isHumanTakeover = (conversation: Conversation) => {
    // Mesmo portao de device do isBotEffectivelyActive logo acima: sem bot no
    // numero, ninguem "assumiu" nada dele. Sem esta linha o painel desenhava o
    // selo ambar "Humano assumiu" toda vez que um operador respondia, num
    // device com ai_agent_enabled=0.
    if (!localDeviceAiEnabled) return false
    if (!conversation.bot_paused_until) return false
    return parsePausedUntil(conversation.bot_paused_until).getTime() > Date.now()
  }

  // Helper: Formatar tempo restante do human takeover
  const getHumanTakeoverTimeRemaining = (conversation: Conversation) => {
    if (!conversation.bot_paused_until) return null
    const diffMs = parsePausedUntil(conversation.bot_paused_until).getTime() - Date.now()
    if (diffMs <= 0) return null
    const min = Math.ceil(diffMs / 60000)
    // Humaniza — antes vinha "38722199min" quando a pausa era indefinida (até 2099)
    if (min < 60) return `${min}min`
    if (min < 1440) return `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}min` : ''}`
    if (min < 1440 * 30) return `${Math.floor(min / 1440)} dias`
    return 'indefinido (líder atribuída)'
  }


  // Função para enviar mensagem de texto
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || isSending) return

    setIsSending(true)
    try {
      const response = await authenticatedFetch(`/api/whatsapp-send/${deviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          remoteJid: selectedConversation.remote_jid,
          messageType: 'text',
          text: newMessage.trim()
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erro ao enviar mensagem')
      }

      setNewMessage('')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsSending(false)
      // Focus em setTimeout pra rodar APÓS o re-render que tira disabled={isSending}.
      // Sem isso, focus() é chamado enquanto o textarea ainda está disabled e é ignorado,
      // forçando o usuário a clicar no campo de novo entre cada envio.
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  // Função para enviar mídia (imagem, vídeo, documento)
  const sendMedia = async (file: File, mediaType: 'image' | 'video' | 'document') => {
    if (!selectedConversation || isSending) return

    setIsSending(true)
    setShowAttachMenu(false)

    try {
      // Converter arquivo para base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const response = await authenticatedFetch(`/api/whatsapp-send/${deviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          remoteJid: selectedConversation.remote_jid,
          messageType: mediaType,
          mediaUrl: base64,
          fileName: file.name,
          caption: ''
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erro ao enviar mídia')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsSending(false)
    }
  }

  // Handler para seleção de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, mediaType: 'image' | 'video' | 'document') => {
    const file = event.target.files?.[0]
    if (file) {
      sendMedia(file, mediaType)
    }
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    event.target.value = ''
  }

  // Mapeia o MIME do arquivo pro type aceito por sendMedia. Áudio cai em
  // 'document' (sendMedia só aceita image|video|document).
  const mediaTypeForFile = (file: File): 'image' | 'video' | 'document' => {
    const t = (file?.type || '').toLowerCase()
    if (t.startsWith('image/')) return 'image'
    if (t.startsWith('video/')) return 'video'
    return 'document'
  }
  // Envia arquivos em sequência (sendMedia trava enquanto envia 1).
  const sendFilesSeq = async (files: File[]) => {
    for (const file of files) { if (file) await sendMedia(file, mediaTypeForFile(file)) }
  }

  // Colar print/imagem (Ctrl+V) → mesmo fluxo do anexo. Print do clipboard vem
  // sem nome, então batizamos pra virar um anexo nomeado.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!selectedConversation || isSending) return
    const files = Array.from(e.clipboardData?.items || [])
      .filter(it => it.kind === 'file').map(it => it.getAsFile()).filter(Boolean) as File[]
    if (!files.length) return
    e.preventDefault()
    const named = files.map(f => f.name ? f : new File([f], `print-${Date.now()}.png`, { type: f.type || 'image/png' }))
    sendFilesSeq(named)
  }

  // Arrastar arquivo(s) pra área da conversa → envia.
  const [dragOver, setDragOver] = useState(false)
  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true) }
  }
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragOver(false) }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    if (!selectedConversation) return
    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length) sendFilesSeq(files)
  }

  // Fechar menu de anexo ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target as Node)) {
        setShowAttachMenu(false)
      }
    }

    if (showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showAttachMenu])

  // Handler para tecla Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Helper para formatar última mensagem com ícones para mídia
  const formatLastMessage = (message: string | null) => {
    if (!message) return <span>Sem mensagens</span>

    const lowerMsg = message.toLowerCase()

    // Detectar tipos de mídia pelo texto
    if (lowerMsg === '[áudio]' || lowerMsg === '[audio]' || lowerMsg.includes('ptt') || lowerMsg.includes('audio message')) {
      return (
        <span className="flex items-center gap-1">
          <FiPlay className="w-3 h-3" />
          <span>Áudio</span>
        </span>
      )
    }

    if (lowerMsg === '[imagem]' || lowerMsg === '[image]' || lowerMsg.includes('image message')) {
      return (
        <span className="flex items-center gap-1">
          <FiImage className="w-3 h-3" />
          <span>Imagem</span>
        </span>
      )
    }

    if (lowerMsg === '[vídeo]' || lowerMsg === '[video]' || lowerMsg.includes('video message')) {
      return (
        <span className="flex items-center gap-1">
          <FiVideo className="w-3 h-3" />
          <span>Vídeo</span>
        </span>
      )
    }

    if (lowerMsg === '[documento]' || lowerMsg === '[document]' || lowerMsg.includes('document message')) {
      return (
        <span className="flex items-center gap-1">
          <FiFile className="w-3 h-3" />
          <span>Documento</span>
        </span>
      )
    }

    if (lowerMsg === '[sticker]' || lowerMsg.includes('sticker message')) {
      return (
        <span className="flex items-center gap-1">
          <FiImage className="w-3 h-3" />
          <span>Sticker</span>
        </span>
      )
    }

    if (lowerMsg === '[localização]' || lowerMsg === '[location]' || lowerMsg.includes('location message')) {
      return (
        <span className="flex items-center gap-1">
          <FiMapPin className="w-3 h-3" />
          <span>Localização</span>
        </span>
      )
    }

    // Texto normal
    return <span>{message}</span>
  }

  // Helper para determinar cor de urgência baseado no tempo sem leitura
  // Vermelho: >24h | Amarelo: 6-24h | Verde: <6h
  const getUnreadUrgencyColor = (lastMessageAt: string | null): { bg: string; ring: string; text: string; badge: string } => {
    if (!lastMessageAt) {
      return { bg: 'bg-green-500/20', ring: 'ring-green-500/40', text: 'text-green-500', badge: 'bg-green-500' }
    }

    const lastMessageTime = new Date(lastMessageAt).getTime()
    const now = Date.now()
    const hoursAgo = (now - lastMessageTime) / (1000 * 60 * 60)

    if (hoursAgo > 24) {
      // Vermelho - Urgente (mais de 24h)
      return { bg: 'bg-red-500/20', ring: 'ring-red-500/40', text: 'text-red-500', badge: 'bg-red-500' }
    } else if (hoursAgo >= 6) {
      // Amarelo - Atenção (6-24h)
      return { bg: 'bg-yellow-500/20', ring: 'ring-yellow-500/40', text: 'text-yellow-500', badge: 'bg-yellow-500' }
    } else {
      // Verde - Recente (<6h)
      return { bg: 'bg-green-500/20', ring: 'ring-green-500/40', text: 'text-green-500', badge: 'bg-green-500' }
    }
  }

  // Helper para obter a cor de urgência mais crítica de uma lista de conversas
  const getMostUrgentColor = (convs: Conversation[]): string => {
    let hasRed = false
    let hasYellow = false

    for (const conv of convs) {
      if ((conv.unread_count || 0) > 0) {
        const colors = getUnreadUrgencyColor(conv.last_message_at)
        if (colors.badge === 'bg-red-500') hasRed = true
        else if (colors.badge === 'bg-yellow-500') hasYellow = true
      }
    }

    if (hasRed) return 'bg-red-500'
    if (hasYellow) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  // Helper para obter URL de mídia (usa proxy para URLs do WhatsApp)
  // Suporta: Evolution API (URLs do whatsapp.net) e Meta API (IDs numéricos)
  const getMediaUrl = (msg: Message): string | null => {
    const rawUrl = msg.mediaUrl || msg.media_url
    if (!rawUrl) return null
    const mediaUrl = String(rawUrl)

    // Base64 inline — usar direto
    if (mediaUrl.startsWith('data:')) return mediaUrl

    // URL local do servidor (já é proxy ou upload) — usar direto
    if (mediaUrl.startsWith('/api/') || mediaUrl.startsWith('/uploads/')) return mediaUrl

    // Qualquer coisa que NÃO seja uma URL válida (http/https) deve ir pelo proxy
    // Isso cobre: IDs numéricos da Meta (inclusive em notação científica), URLs do WhatsApp/Evolution, etc.
    // Tags <img> e <audio> não enviam headers de Authorization, então passamos token via query string
    const isDirectUrl = mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')
    const needsProxy = !isDirectUrl || mediaUrl.includes('whatsapp.net') || mediaUrl.includes('mmg.')

    if (needsProxy) {
      const token = localStorage.getItem('auth-token')
      return `/api/whatsapp-media/${msg.id}?token=${encodeURIComponent(token || '')}`
    }
    return mediaUrl
  }

  // Prévia visual de um anexo (message_type='document'). Imagem enviada como
  // arquivo já é renderizável direto pelo proxy; PDF precisa do /thumb, que
  // rasteriza a 1ª página no backend. Demais formatos: null (só o chip).
  //
  // O tipo NÃO pode sair só de media_mimetype: o evento new_message do socket
  // não carrega essa chave (chega em metadata.mimetype) e o envio pelo painel
  // grava a coluna NULL. Sem os dois fallbacks a prévia só apareceria depois
  // de um F5, e nunca em anexo que o próprio operador mandou.
  const docPreviewUrl = (msg: Message, docName: string, metadata: any): { url: string; isImage: boolean } | null => {
    const mime = String((msg as any).media_mimetype || metadata?.mimetype || '')
    const name = String(docName || '')
    if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(name)) {
      const url = getMediaUrl(msg)
      return url ? { url, isImage: true } : null
    }
    if (mime.includes('pdf') || /\.pdf$/i.test(name)) {
      const token = localStorage.getItem('auth-token') || ''
      return { url: `/api/whatsapp-media/${msg.id}/thumb?token=${encodeURIComponent(token)}`, isImage: false }
    }
    return null
  }

  // Renderizar conteúdo de mídia
  const renderMediaContent = (msg: Message) => {
    const mediaUrl = getMediaUrl(msg)
    const messageType = msg.message_type
    const metadata = typeof msg.metadata === 'string' ? JSON.parse(msg.metadata || '{}') : (msg.metadata || {})

    switch (messageType) {
      case 'image':
        return mediaUrl ? (
          <div className="mb-1">
            <img
              src={mediaUrl}
              alt="Imagem"
              className="max-w-full rounded-lg max-h-40 sm:max-h-56 md:max-h-64 object-contain cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setMediaModal({ url: mediaUrl, type: 'image' })}
            />
          </div>
        ) : null

      case 'video':
        return mediaUrl ? (
          <div className="mb-1">
            <div className="relative cursor-pointer" onClick={() => setMediaModal({ url: mediaUrl, type: 'video' })}>
              <video
                src={mediaUrl}
                className="max-w-full rounded-lg max-h-40 sm:max-h-56 md:max-h-64"
                muted
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg hover:bg-black/40 transition-colors">
                <FiPlay className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
        ) : null

      case 'audio':
      case 'ptt':
        return (
          <AudioPlayer
            src={mediaUrl}
            duration={metadata.seconds}
            fromMe={msg.from_me === 1 || msg.fromMe === true}
            messageId={msg.id}
            initialTranscription={metadata.transcription || null}
          />
        )

      case 'document': {
        // Prévia do anexo: imagem mandada como arquivo renderiza direto; PDF vai
        // pelo /thumb (pdftoppm no backend). Outros formatos ficam só no chip.
        // onError esconde a prévia e o balão volta ao chip clássico.
        const docName = metadata.fileName || (msg.content && !msg.content.startsWith('[') ? msg.content : 'Documento')
        const preview = docPreviewUrl(msg, docName, metadata)
        return (
          <div className="mb-1">
            {preview && (
              <a href={mediaUrl || undefined} target="_blank" rel="noopener noreferrer" title={`Abrir ${docName}`}>
                <img src={preview.url} alt={`Prévia de ${docName}`} loading="lazy"
                  className={`mb-1 rounded-lg max-h-44 w-full border border-border/50 bg-white cursor-pointer ${preview.isImage ? 'object-contain' : 'object-cover object-top'}`}
                  onError={e => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }} />
              </a>
            )}
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
              <FiFile className="w-5 h-5 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{docName}</p>
                {metadata.pageCount && <p className="text-xs text-muted-foreground">{metadata.pageCount} páginas</p>}
              </div>
              {mediaUrl && (
                <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-muted rounded">
                  <FiDownload className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        )
      }

      case 'sticker':
        return mediaUrl ? (
          <div className="mb-1">
            <img
              src={mediaUrl}
              alt="Sticker"
              className="w-32 h-32 object-contain cursor-pointer"
              onClick={() => setMediaModal({ url: mediaUrl, type: 'image' })}
            />
          </div>
        ) : null

      case 'location':
        return (
          <div className="mb-1">
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
              <FiMapPin className="w-5 h-5 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">{metadata.name || 'Localização'}</p>
                {metadata.address && <p className="text-xs text-muted-foreground">{metadata.address}</p>}
                {metadata.latitude && metadata.longitude && (
                  <a
                    href={`https://maps.google.com/?q=${metadata.latitude},${metadata.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    Abrir no mapa
                  </a>
                )}
              </div>
            </div>
          </div>
        )

      case 'contact':
        return (
          <div className="mb-1">
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
              <FiUser className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm font-medium">{metadata.displayName || msg.content || 'Contato'}</p>
                {metadata.contacts && (
                  <p className="text-xs text-muted-foreground">{metadata.contacts.length} contato(s)</p>
                )}
              </div>
            </div>
          </div>
        )

      // 'template': renderizado como card completo no bloco de mensagem
      // (imagem + corpo + footer + botões juntos), não aqui.

      default:
        return null
    }
  }

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })
  }

  // Tooltip canônico: "16/05/2026 - 13:52 - CG/MS"
  const fmtCGTooltip = (timestamp: string | null) => {
    if (!timestamp) return ''
    const d = new Date(timestamp)
    if (isNaN(d.getTime())) return ''
    const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Campo_Grande' })
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })
    return `${date} - ${time} - CG/MS`
  }

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    // Hoje
    if (date.toDateString() === today.toDateString()) {
      return 'Hoje'
    }

    // Ontem
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem'
    }

    // Calcular diferença em dias
    const diffTime = today.getTime() - date.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    // Até 7 dias: mostrar dia da semana
    if (diffDays <= 7) {
      const weekDays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']
      return weekDays[date.getDay()]
    }

    // Mais de 7 dias: mostrar data completa
    return date.toLocaleDateString('pt-BR')
  }

  // Formata número BR — aceita com/sem código do país (55), com 8/9 dígitos
  // de celular. Antes o regex `(\d{2})(\d{5})(\d{4})` cuspia "(55) 67996-724450"
  // pra um 13-dígitos (55+67+9+8), porque consumia o "55" como se fosse DDD.
  // Saída padrão: "(67) 99672-4450" — sem código do país.
  const formatBrazilianPhone = (raw: string): string => {
    const d = String(raw || '').replace(/\D/g, '')
    // 13 dígitos: 55 + DDD(2) + 9 + 8 → "(67) 99672-4450"
    if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`
    // 12 dígitos: 55 + DDD(2) + 8 (fixo / celular antigo) → "(67) 3333-4444"
    if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`
    // 11 dígitos: DDD(2) + 9 + 8 → "(67) 99672-4450"
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
    // 10 dígitos: DDD(2) + 8 → "(67) 3333-4444"
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
    return d
  }

  const getContactName = (conversation: Conversation) => {
    // Prioridade:
    //  1. nome_cadastro (Oracle DRACO) — fonte de verdade pra cadastrados
    //  2. lead_extracted_data.nome (bot capturou via registrar_dados_cadastro)
    //     — é resposta direta do lead à pergunta "qual seu nome completo?",
    //     mais confiável que pushname.
    //  3. remote_name (pushname WhatsApp) — pode ser abreviação ou apelido
    //     (visto: "CRBA" pra Cínthia Raquel Bordón Avelar, "Mhy❤️")
    //  4. Fallback pro telefone formatado.
    // Saída sempre em MAIÚSCULAS pra ficar consistente com cadastro Oracle
    // (que armazena tudo em CAIXA ALTA — "VALERIA MARTINS RIBEIRO").
    let name: string | null = null
    if (conversation.nome_cadastro) name = conversation.nome_cadastro
    else {
      const led = (conversation as any).lead_extracted_data
      if (led) {
        try {
          const parsed = typeof led === 'string' ? JSON.parse(led) : led
          const n = parsed?.nome || parsed?.name
          // Gemma 4 às vezes serializa "null" como string em vez de JSON null
          // (caso Divanir 2026-05-24 — header virou "NULL" após toUpperCase).
          const t = n ? String(n).trim() : ''
          if (t && !/^(null|undefined|none|n\/a)$/i.test(t)) name = t
        } catch { /* JSON inválido, ignora */ }
      }
      if (!name && conversation.remote_name) name = conversation.remote_name
    }
    if (name) return name.toUpperCase()
    return formatBrazilianPhone(conversation.remote_jid.replace('@s.whatsapp.net', ''))
  }

  const getPhoneNumber = (remoteJid: string) => {
    return formatBrazilianPhone(remoteJid.replace('@s.whatsapp.net', ''))
  }

  if (!isOpen) return null

  // Classes condicionais baseadas no modo embedded - RESPONSIVO
  const containerClass = embedded
    ? "h-full w-full flex flex-col overflow-hidden"
    : "fixed inset-0 glass-overlay flex items-center justify-center z-50 p-2 sm:p-4"

  const panelClass = embedded
    ? "h-full w-full flex flex-col overflow-hidden"
    : "glass-panel rounded-xl w-full max-w-[100%] sm:max-w-2xl md:max-w-4xl lg:max-w-6xl h-[100%] sm:h-[95vh] md:h-[90vh] flex flex-col overflow-hidden"

  return (
    <>
      <div className={neu ? `${containerClass} neu-lab` : containerClass}>
        <div className={panelClass}>
        {/* Seção de notificações (falhas de handoff + leads segurados) — colapsável
            pela filipeta central (mesmo design das laterais), pra devolver o espaço
            do inbox sem dispensar os avisos. Recolhida vira uma tira com contadores.
            Portado do fin.twms em 2026-07-14. */}
        {(handoffFailures.length > 0 || heldLeads.length > 0) && (
        <div className="relative z-40 shrink-0">
        {/* Colapso suave: truque do grid 1fr↔0fr anima a altura REAL do conteúdo
            (max-height animava um teto fictício e dava salto seco). Tira e banners
            ficam sempre montados, cada um deslizando pro seu lado com fade. */}
        <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${alertsCollapsed ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
          <div className="min-h-0 overflow-hidden">
          <button
            type="button"
            onClick={() => setAlertsCollapsed(false)}
            title="Expandir notificações"
            className={`w-full flex items-center justify-center gap-4 px-3 py-1 border-b text-[11px] font-semibold transition-colors ${heldLeads.length > 0 ? 'border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15' : 'border-red-500/30 bg-red-500/10 hover:bg-red-500/15'}`}
          >
            {handoffFailures.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-red-700 dark:text-red-300">
                <FiAlertTriangle className="w-3 h-3" />
                {handoffFailures.length} {handoffFailures.length === 1 ? 'não entregue' : 'não entregues'}
              </span>
            )}
            {heldLeads.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                <FiClock className="w-3 h-3" />
                {heldLeads.length} {heldLeads.length === 1 ? 'lead aguardando líder' : 'leads aguardando líder'}
              </span>
            )}
          </button>
          </div>
        </div>
        <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${alertsCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`}>
          <div className="min-h-0 overflow-hidden">
        {/* Banner de falhas de handoff — webhook Meta marcou status=failed em
            mensagem de template pra líder ou "Pronto" pra lead. Empilha até
            10 falhas. Operador clica no item pra abrir a conversa e reenviar. */}
        {handoffFailures.length > 0 && (
          <div className="bg-red-500/10 border-b border-red-500/30 px-3 py-2 flex flex-col gap-1.5 max-h-52 overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-red-700 dark:text-red-300 flex items-center gap-2 min-w-0">
                <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{handoffFailures.length} {handoffFailures.length === 1 ? 'mensagem não foi entregue' : 'mensagens não foram entregues'} — clique pra abrir a conversa</span>
              </div>
              <button
                type="button"
                onClick={dismissAllHandoffFailures}
                className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-red-500/40 text-red-700 dark:text-red-300 hover:bg-red-500/15 transition-colors"
                title="Dispensa todas as falhas listadas (inclusive as que não couberam na tela)"
              >
                Limpar tudo
              </button>
            </div>
            {handoffFailures.map(f => {
              const target = f.source === 'forward' ? 'contato ' + (f.recipient || '').split('@')[0]
                           : f.source === 'lead_handoff_notify' ? 'lead'
                           : 'líder ' + (f.leaderName || '')
              const isCad = !!f.cadastroError
              const errCode = isCad ? 'cadastro errado' : friendlyMetaError(f.errorCode)
              const alts = handoffAltPhones[f.messageId] || []
              const retrying = !!handoffRetrying[f.messageId]
              const canRetry = f.errorCode === 131026 && f.source === 'lead_handoff_template' && alts.length > 0
              const sourceLabel = (s: string) => s === 'celular' ? 'celular' : s === 'telefone1' ? 'tel.1' : 'tel.2'
              return (
                <div key={f.messageId} className={`text-xs px-2 py-1.5 rounded-lg border transition-colors shadow-sm ${isCad ? 'border-red-500/50 bg-red-600/15' : 'border-red-500/30 bg-red-500/10 hover:bg-red-500/15'}`}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openConversationById(f.conversationId)}
                      className="group/hf flex-1 min-w-0 flex items-center gap-2 text-left"
                      title={`Abrir a conversa · ${f.contentPreview}`}
                    >
                      {isCad && (
                        <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold uppercase tracking-wide">
                          <FiAlertTriangle className="w-2.5 h-2.5" /> Cadastro errado
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-foreground group-hover/hf:underline">→ {target}</span>
                        <span className="block truncate text-[10px] text-muted-foreground leading-tight">{f.contentPreview}</span>
                      </span>
                      <span
                        className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold text-red-800 dark:text-red-200 bg-red-500/20 border border-red-500/40"
                        title={f.errorCode != null ? `Código Meta ${f.errorCode}` : undefined}
                      >
                        {errCode}
                      </span>
                      <FiChevronRight className="shrink-0 w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover/hf:opacity-100 transition-opacity" />
                    </button>
                    <button
                      type="button"
                      onClick={() => dismissHandoffFailure(f.messageId)}
                      className="shrink-0 p-1 rounded-md text-red-500/70 hover:text-red-600 hover:bg-red-500/15 dark:text-red-400/60 dark:hover:text-red-400 transition-colors"
                      title="Dispensar"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {isCad && (
                    <div className="mt-1 pl-2 text-[10px] font-medium text-red-700 dark:text-red-300">
                      Tentamos enviar <strong>com e sem o 9º dígito</strong> e as duas falharam — o número parece estar <strong>cadastrado errado</strong>. Confira/atualize o cadastro e reenvie.
                    </div>
                  )}
                  {canRetry && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-2 text-[10px]">
                      <span className="text-red-700 dark:text-red-300 font-medium">Reenviar pra:</span>
                      {alts.map(p => (
                        <button
                          key={p.source}
                          type="button"
                          disabled={retrying}
                          onClick={() => retryHandoffWithPhone(f, p.source)}
                          className="px-2 py-0.5 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-800 dark:text-emerald-200 border border-emerald-500/40 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                          title={`Tenta enviar o template pra ${sourceLabel(p.source)} do cadastro (${p.formatted}). Queima outro template Meta.`}
                        >
                          ↻ {p.formatted} <span className="opacity-80">({sourceLabel(p.source)})</span>
                        </button>
                      ))}
                      {retrying && <span className="text-red-700 dark:text-red-300">enviando…</span>}
                    </div>
                  )}
                  {f.errorCode === 131026 && f.source === 'lead_handoff_template' && alts.length === 0 && handoffAltPhones[f.messageId] !== undefined && (
                    <div className="mt-1 pl-2 text-[10px] text-red-700 dark:text-red-300">
                      Sem telefones alternativos no cadastro — atualize o cadastro da líder.
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {/* Leads que o auto-assign SEGUROU pro humano (sem CEP, geo impreciso, ou
            nenhuma líder elegível no raio). Sem isso o lead some no limbo após
            completar o cadastro (casos Stephany/Jéssica 2026-06-10). Operador
            clica pra abrir a conversa e atribuir a líder manualmente. */}
        {heldLeads.length > 0 && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-3 py-2 flex flex-col gap-1.5 max-h-52 overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-2 min-w-0">
                <FiClock className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{heldLeads.length} {heldLeads.length === 1 ? 'lead aguardando' : 'leads aguardando'} atribuição manual de líder — clique pra abrir</span>
              </div>
              <button
                type="button"
                onClick={releaseAllHeldLeads}
                className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15 transition-colors"
                title="Dispensa todos os avisos listados (os leads continuam sem líder — atribua manualmente depois)"
              >
                Limpar tudo
              </button>
            </div>
            {heldLeads.map(h => {
              // [2026-08-02] Os rótulos mentiam. "sem CEP" aparecia em 47 de 47
              // registros que TINHAM CEP gravado — o motivo real era o geocode
              // ter falhado, e o operador saía procurando um dado que já estava
              // no cadastro. Cada motivo agora diz o que fazer a respeito.
              const reasonLabel = h.reason === 'no_cep' ? 'lead não informou o CEP'
                : h.reason === 'cep_nao_resolvido' ? 'CEP não localizado — confira com o lead'
                : h.reason === 'fora_de_territorio' ? 'lead de outro estado'
                : h.reason === 'low_confidence_geo' ? 'endereço impreciso na cidade'
                : h.reason === 'sem_lider_no_raio' ? 'nenhuma líder ao alcance'
                : h.reason === 'sem_lider_elegivel' ? 'nenhuma líder disponível'
                // Motivos do desenho antigo — ainda existem em registros gravados
                // antes de 02/08 e continuam aparecendo até serem dispensados.
                : h.reason === 'no_proven_in_radius' ? 'sem líder ativa na região'
                : h.reason === 'no_responsive_in_radius' ? 'líderes da região não respondem'
                : h.reason
              // Sigla pro avatar: 1ª letra dos dois primeiros nomes (pula conectivos).
              const initials = (h.leadName || '')
                .split(/\s+/)
                .filter(w => w && !['de', 'da', 'do', 'das', 'dos', 'e'].includes(w.toLowerCase()))
                .slice(0, 2)
                .map(w => w[0]?.toUpperCase() || '')
                .join('') || '?'
              return (
                <div key={h.conversationId} className="text-xs px-2 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15 transition-colors shadow-sm">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openConversationById(h.conversationId, h.deviceId)}
                      className="group/hl flex-1 min-w-0 flex items-center gap-2 text-left"
                      title={`Abrir a conversa pra atribuir líder · Motivo: ${reasonLabel}${h.leadCep ? ` · CEP ${h.leadCep}` : ''}`}
                    >
                      <span className="shrink-0 w-6 h-6 rounded-full bg-amber-500/25 text-amber-800 dark:text-amber-200 flex items-center justify-center text-[9px] font-bold tracking-wide">
                        {initials}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-foreground group-hover/hl:underline">{h.leadName || 'Lead sem nome'}</span>
                        {h.leadCep && <span className="block text-[10px] text-muted-foreground leading-tight">CEP {h.leadCep}</span>}
                      </span>
                      <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold text-amber-800 dark:text-amber-200 bg-amber-500/20 border border-amber-500/40">
                        {reasonLabel}
                      </span>
                      <FiChevronRight className="shrink-0 w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover/hl:opacity-100 transition-opacity" />
                    </button>
                    <button
                      type="button"
                      onClick={() => releaseHeldLead(h.conversationId)}
                      className="shrink-0 p-1 rounded-md text-amber-500/70 hover:text-amber-600 hover:bg-amber-500/15 dark:text-amber-400/60 dark:hover:text-amber-400 transition-colors"
                      title="Dispensar este aviso (o lead continua sem líder)"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
          </div>
        </div>
        {/* Filipeta recolher/expandir — pendurada embaixo da seção, centrada na
            horizontal (mesmo padrão das filipetas laterais da lista/perfil).
            O chevron gira em vez de trocar de ícone — acompanha o slide. */}
        <button
          type="button"
          onClick={() => setAlertsCollapsed(c => !c)}
          title={alertsCollapsed ? 'Expandir notificações' : 'Recolher notificações'}
          className={`absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-full z-50 flex items-center justify-center w-9 h-6 rounded-b-lg border border-t-0 transition-colors shadow-sm ${heldLeads.length > 0
            ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 hover:text-amber-800 dark:hover:text-amber-200'
            : 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300 hover:bg-red-500/20 hover:text-red-800 dark:hover:text-red-200'}`}
        >
          <FiChevronUp className={`w-4 h-4 transition-transform duration-300 ease-in-out ${alertsCollapsed ? 'rotate-180' : ''}`} />
        </button>
        </div>
        )}
        {/* Body - RESPONSIVO */}
        <div className={`flex flex-1 overflow-hidden bg-background min-h-0 ${neu ? 'neu-canvas gap-2 p-2 xl:gap-3 xl:p-3' : ''}`}>
          {/* Lista de conversas - RESPONSIVO: full em mobile, 45% em tablet, 35% em desktop, 30% em telas grandes */}
          <div className={`${neu ? 'neu-raised ' : ''}relative z-30 transition-[width] duration-200 border-r border-border flex flex-col bg-card shrink-0 shadow-[1px_0_2px_0_rgba(2,6,23,0.15),4px_0_10px_-2px_rgba(2,6,23,0.10),10px_0_22px_-4px_rgba(2,6,23,0.07),18px_0_36px_-8px_rgba(2,6,23,0.04)] ${selectedConversation ? 'hidden md:flex' : 'flex'} ${listShrunk ? 'w-full md:w-[68px]' : `w-full md:w-[38%] ${viewMode === 'kanban' ? 'md:w-[55%] lg:w-[60%] xl:w-[60%]' : 'lg:w-[28%] xl:w-[24%] 2xl:w-[20%]'}`}`}>
            {/* Recolher/expandir a lista — "filipeta" grudada na borda direita,
                centrada na vertical (o z-30 é na COLUNA: glass/stacking context
                engoliria um z só no botão). Oculta no kanban sem conversa.
                REMOVIDA no tema neu (2026-07-24): quebrava visualmente e a
                diretoria não usa recolher/expandir coluna. */}
            {!neu && (viewMode === 'list' || !!selectedConversation) && (
              <button
                onClick={() => setListCollapsed(c => !c)}
                title={listShrunk ? 'Expandir lista' : 'Recolher lista'}
                className="hidden md:flex absolute right-0 translate-x-full top-1/2 -translate-y-1/2 z-50 items-center justify-center w-7 h-9 rounded-r-lg text-muted-foreground hover:text-foreground transition-colors bg-card border border-l-0 border-border hover:bg-secondary shadow-[4px_0_8px_-2px_rgba(2,6,23,0.12),10px_0_18px_-4px_rgba(2,6,23,0.06)]"
              >
                {listShrunk ? <FiChevronRight className="w-4 h-4" /> : <FiChevronLeft className="w-4 h-4" />}
              </button>
            )}
            {/* Trilho recolhido — siglas dos nomes (1ª letra dos dois primeiros
                nomes, pulando conectivos) ou foto; badge de não lidas; clique abre. */}
            {listShrunk && (
              <div className="hidden md:flex flex-1 min-h-0 flex-col items-center gap-1.5 overflow-y-auto scrollbar-thin py-2">
                {filteredConversations.map(conv => {
                  const railUnread = (conv.unread_count || 0) > 0
                  const railUrgency = railUnread ? getUnreadUrgencyColor(conv.last_message_at) : null
                  const railWords = getContactName(conv).split(/\s+/)
                    .filter(w => /^[a-zà-ú]/i.test(w) && !/^(da|de|do|das|dos|e)$/i.test(w))
                  const railSigla = railWords.length >= 2
                    ? (railWords[0][0] + railWords[1][0]).toUpperCase()
                    : railWords.length === 1 ? railWords[0].slice(0, 2).toUpperCase() : null
                  return (
                    <button
                      key={conv.id}
                      onClick={() => loadMessages(conv)}
                      title={getContactName(conv)}
                      className={`relative shrink-0 rounded-full transition-shadow ${selectedConversation?.id === conv.id ? 'ring-2 ring-primary' : 'hover:ring-2 hover:ring-border'}`}
                    >
                      <span className="w-9 h-9 rounded-full flex items-center justify-center bg-muted overflow-hidden">
                        {conv.remote_profile_pic && !failedProfilePics.has(conv.id) ? (
                          <img
                            src={conv.remote_profile_pic}
                            alt=""
                            className="w-9 h-9 rounded-full object-cover"
                            onError={() => setFailedProfilePics(prev => new Set(prev).add(conv.id))}
                          />
                        ) : railSigla ? (
                          <span className="text-[11px] font-bold text-muted-foreground leading-none">{railSigla}</span>
                        ) : (
                          <FiUser className="w-4 h-4 text-muted-foreground" />
                        )}
                      </span>
                      {railUnread && (
                        <span className={`absolute -top-1 -right-1 ${railUrgency?.badge || 'bg-emerald-500'} text-white text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 ring-2 ring-card`}>
                          {conv.unread_count}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
            {/* Conteúdo da coluna — no colapso (md+) dá lugar ao trilho, mas segue montado. */}
            <div className={`flex-1 min-h-0 flex-col overflow-hidden ${listShrunk ? 'flex md:hidden' : 'flex'}`}>

            {/* ── Barra de filtros — dropdown de escopo + toggles somatórios.
                Wrapper externo ocupa 100% (mantém o border-b atravessando toda a coluna).
                Inner mantém largura equivalente ao modo lista (28vw em lg, 24vw em xl)
                quando kanban — toggles ficam exatamente onde estavam na lista. ── */}
            <div className="flex-shrink-0 border-b border-border">
              <div className={`px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1.5 ${viewMode === 'kanban' ? 'lg:w-[28vw] xl:w-[24vw]' : ''}`}>

              {/* Toolbar do inbox (Lista×Kanban + dropdown de campanha): por padrão
                  renderiza na barra de DISPOSITIVOS (App.tsx) via portal pro
                  toolbarSlot; sem slot, cai aqui na barra de filtros (fallback). */}
              {(() => {
                const __toolbar = (
                  <>
              {/* Toggle Lista | Kanban */}
              {showKanbanToggle && (
                <div className={`shrink-0 inline-flex rounded-md border border-border overflow-hidden h-8 sm:h-9 ${neu ? 'neu-seg' : ''}`} role="group" aria-label="Modo de visualização">
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    title="Lista (padrão)"
                    aria-pressed={viewMode === 'list'}
                    className={`px-2 inline-flex items-center justify-center transition-colors ${neu ? `neu-seg-btn ${viewMode === 'list' ? 'neu-on ' : ''}` : ''}${viewMode === 'list' ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
                  >
                    <FiList className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('kanban')}
                    title="Kanban — organize conversas em colunas customizadas"
                    aria-pressed={viewMode === 'kanban'}
                    className={`px-2 inline-flex items-center justify-center transition-colors border-l border-border ${neu ? `neu-seg-btn ${viewMode === 'kanban' ? 'neu-on ' : ''}` : ''}${viewMode === 'kanban' ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
                  >
                    <EditColumnIcon style={{ fontSize: '1.1em' }} />
                  </button>
                </div>
              )}

              {/* Dropdown de escopo (campanha) — Trigger
                  com ícone de filtro, badge de contagem inline (Todos · 2.3k ou
                  Campanha X · 1.234) e largura confortável (max-w-[200px]). Popover
                  ancorado em left-0 — abre pra baixo-direita do trigger. */}
              {(() => {
                const selected = filterCampaign
                  ? campaignsList.find(c => c.template === filterCampaign)
                  : null
                const totalContacts = campaignsList.reduce((a, c) => a + (c.contacts || 0), 0)
                const triggerLabel = filterCampaign || 'Todos'
                const triggerCount = selected
                  ? selected.contacts
                  : (campaignsList.length > 0 ? totalContacts : null)
                return (
                  <div ref={campaignMenuRef} className="relative min-w-0 shrink">
                    <button
                      type="button"
                      onClick={() => setCampaignMenuOpen(o => !o)}
                      title={filterCampaign
                        ? `Filtrando pela campanha "${filterCampaign}"${triggerCount ? ` · ${triggerCount.toLocaleString('pt-BR')} contatos` : ''}`
                        : `Exibindo todas as conversas${triggerCount ? ` · ${triggerCount.toLocaleString('pt-BR')} contatos no total` : ''}`}
                      className={[
                        'inline-flex items-center gap-1.5 rounded-lg pl-2.5 pr-2 h-8 sm:h-9 text-xs font-medium transition-all border max-w-[200px]',
                        filterCampaign
                          ? 'bg-sky-500/15 border-sky-500/40 text-sky-700 dark:text-sky-300 hover:bg-sky-500/20'
                          : (neu ? 'neu-inset text-foreground' : 'bg-foreground/5 border-foreground/20 text-foreground hover:bg-foreground/10')
                      ].join(' ')}
                    >
                      <FiFilter className={`w-3.5 h-3.5 shrink-0 ${filterCampaign ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`} />
                      <span className="truncate">{triggerLabel}</span>
                      {triggerCount !== null && (
                        <span className={`text-[10px] tabular-nums px-1 rounded shrink-0 ${filterCampaign ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300' : 'bg-foreground/10 text-muted-foreground'}`}>
                          {triggerCount > 9999 ? `${Math.floor(triggerCount / 1000)}k` : triggerCount.toLocaleString('pt-BR')}
                        </span>
                      )}
                      <FiChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${campaignMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {campaignMenuOpen && (
                      <div className="absolute left-0 top-full mt-1.5 z-50 w-80 max-w-[calc(100vw-2rem)] max-h-96 overflow-hidden rounded-xl glass-popover flex flex-col">
                        <div className="px-3 py-2 border-b border-border/50 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                          <FiFilter className="w-3 h-3" />
                          Filtrar por campanha
                        </div>
                        <div className="overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => { setFilterCampaign(null); setCampaignMenuOpen(false) }}
                            className={`w-full text-left px-3 py-2.5 text-xs hover:bg-muted/60 flex items-center justify-between gap-2 transition-colors ${!filterCampaign ? 'bg-foreground/8 font-semibold' : ''}`}
                          >
                            <span className="flex items-center gap-2">
                              {!filterCampaign && <FiCheck className="w-3 h-3 text-sky-500" />}
                              <span className={!filterCampaign ? '' : 'ml-5'}>Todos os contatos</span>
                            </span>
                            <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
                              {totalContacts > 0 ? totalContacts.toLocaleString('pt-BR') : '—'}
                            </span>
                          </button>
                          {campaignsList.length === 0 ? (
                            <div className="px-3 py-4 text-[11px] text-muted-foreground text-center">
                              Nenhuma campanha encontrada neste device.
                            </div>
                          ) : campaignsList.map(c => {
                            const active = filterCampaign === c.template
                            return (
                              <button
                                key={c.template}
                                type="button"
                                onClick={() => { setFilterCampaign(c.template); setCampaignMenuOpen(false) }}
                                title={c.template}
                                className={`w-full text-left px-3 py-2.5 text-xs hover:bg-muted/60 flex items-center justify-between gap-2 transition-colors ${active ? 'bg-sky-500/10 font-semibold text-sky-700 dark:text-sky-300' : ''}`}
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  {active && <FiCheck className="w-3 h-3 text-sky-500 shrink-0" />}
                                  <span className={`truncate ${active ? '' : 'ml-5'}`}>{c.template}</span>
                                </span>
                                <span className={`text-[10px] tabular-nums shrink-0 ${active ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`}>
                                  {c.contacts.toLocaleString('pt-BR')}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}

                  </>
                )
                return toolbarSlot ? createPortal(__toolbar, toolbarSlot) : __toolbar
              })()}

              {/* Toggles — dois grupos com separador no meio.
                  Grupo 1 (tipo): empresário, líder, consultora, novo lead.
                  Grupo 2 (lifecycle): aguardando líder, convertidos.
                  Cada grupo flex-wrap independente — em larguras apertadas,
                  cada grupo se reorganiza sem atravessar o separador. Toggle
                  estreitado de w-10 → w-9 (36px) pra caber mais em telas
                  menores sem afetar o thumb (h-5 w-5, translate-x-4 → 3.5).
                  Antigos toggles "IA ativa" e "Atendimento humano" removidos
                  em 2026-05-27 — não eram usados na operação real; o slot do
                  primeiro virou "Convertidos" (lead que virou consultora). */}
              <div className="flex-1 flex flex-nowrap items-center justify-center min-w-0 gap-x-2">

                {(() => {
                  // Contadores device-wide (servidor) — não dependem da página carregada.
                  const awaitingCount = deviceCounts.awaiting
                  const assignedCount = deviceCounts.assigned
                  // Aguardando líder = total ao vivo. Convertidos = "novos desde que vi"
                  // (total real - já vistos). Ver markConvertedSeen / seenConverted.
                  const convertedTotal = deviceCounts.converted
                  const convertedNew = deviceCounts.convertedIds.filter(id => !seenConverted.has(id)).length
                  const renderToggle = (cfg: {
                    v: FType, Icon: React.ComponentType<{ className?: string }>,
                    track: string, iconOn: string, title: string, badgeCount?: number, badgeColor?: string
                  }) => {
                    const on = filterTypes.has(cfg.v)
                    return (
                      <button
                        key={cfg.v}
                        type="button"
                        role="switch"
                        aria-checked={on}
                        onClick={() => toggleFType(cfg.v)}
                        title={cfg.title}
                        className={`relative inline-flex items-center justify-center h-7 w-7 shrink-0 cursor-pointer rounded-full border transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 ${on ? `${cfg.track} border-transparent text-white shadow-sm` : 'bg-muted border-border text-muted-foreground hover:bg-muted/70'}`}
                      >
                        <cfg.Icon className="w-3.5 h-3.5" />
                        {!!cfg.badgeCount && cfg.badgeCount > 0 && (
                          <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full ${cfg.badgeColor || 'bg-orange-500'} text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-background pointer-events-none`}>
                            {cfg.badgeCount > 99 ? '99+' : cfg.badgeCount}
                          </span>
                        )}
                      </button>
                    )
                  }
                  // Grupo 1 — tipo do contato (4 filtros)
                  const tipoFilters = [
                    { v: 'empresario' as FType, Icon: FiBriefcase, track: 'bg-amber-500',   iconOn: 'text-amber-500',   tip: ['Empresários', 'Empresários — filtrando'] },
                    { v: 'lider'      as FType, Icon: FiAward,     track: 'bg-purple-500',  iconOn: 'text-purple-500',  tip: ['Líderes',     'Líderes — filtrando']     },
                    { v: 'consultora' as FType, Icon: FiUserCheck, track: 'bg-sky-500',     iconOn: 'text-sky-500',     tip: ['Consultoras', 'Consultoras — filtrando'] },
                    { v: 'lead'       as FType, Icon: FiUserPlus,  track: 'bg-emerald-500', iconOn: 'text-emerald-500', tip: ['Novos Leads', 'Novos Leads — filtrando'] },
                  ]
                  // Grupo 2 — lifecycle/status (2 filtros)
                  const awaitingOn = filterTypes.has('awaiting')
                  const awaitingTitle = awaitingOn
                    ? `Aguardando líder — mostrando ${awaitingCount} sem atribuir + ${assignedCount} com líder`
                    : `Aguardando líder · ${awaitingCount} sem atribuir${assignedCount > 0 ? ` · ${assignedCount} com líder (ocultas)` : ''}`
                  const convertedOn = filterTypes.has('converted')
                  const convertedTitle = convertedOn
                    ? `Convertidos — ${convertedTotal} lead${convertedTotal === 1 ? '' : 's'} que virou consultora`
                    : `Convertidos · ${convertedTotal} no total${convertedNew > 0 ? ` · ${convertedNew} novo${convertedNew === 1 ? '' : 's'} desde que viu` : ' · nenhum novo'} (clique pra ver)`
                  return (
                    <>
                      <div className="flex flex-nowrap items-center justify-center gap-2">
                        {tipoFilters.map(f => renderToggle({
                          v: f.v, Icon: f.Icon, track: f.track, iconOn: f.iconOn,
                          title: f.tip[filterTypes.has(f.v) ? 1 : 0],
                        }))}
                      </div>
                      <div className="flex flex-nowrap items-center justify-center gap-2">
                        {renderToggle({
                          v: 'awaiting', Icon: FiFlag, track: 'bg-orange-500', iconOn: 'text-orange-500',
                          title: awaitingTitle,
                          badgeCount: awaitingCount, badgeColor: 'bg-orange-500',
                        })}
                        {renderToggle({
                          v: 'converted', Icon: FiTrendingUp, track: 'bg-rose-500', iconOn: 'text-rose-500',
                          title: convertedTitle,
                          badgeCount: convertedNew, badgeColor: 'bg-rose-500',
                        })}
                      </div>
                    </>
                  )
                })()}
              </div>

              </div>
            </div>
            {/* ── fim barra de filtros ── */}

            {/* Banner de deep-link que não achou a conversa carregada */}
            {deepLinkNotFound && (
              <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/30 text-[11px] text-amber-600 flex items-center gap-2">
                <FiInfo className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1">
                  Conversa solicitada não está nesta página — limpe os filtros e busque pelo nome do cliente.
                </span>
                <button onClick={() => setDeepLinkNotFound(false)} className="text-amber-600 hover:text-amber-700" title="Dispensar">
                  <FiX className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <div className={`flex-1 min-h-0 ${viewMode === 'list' ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}${neu && viewMode === 'list' ? ' px-2 pt-2 pb-3 space-y-2 rounded-b-2xl' : ''}`}>
              {viewMode === 'kanban' && (() => {
                // Modo kanban com modelo persistido. Sem modelo ativo, mostra só coluna primária.
                // Com modelo, distribui cards entre primária (não-classificados) e colunas custom.
                const placementByConv = new Map(placements.map(p => [p.conversation_id, p.column_id]))
                const inCustomColumn = (conv: typeof filteredConversations[number]) =>
                  placementByConv.has(conv.id) && customCols.some(c => c.id === placementByConv.get(conv.id))
                const cardsByColumn = (colId: string) => filteredConversations.filter(c => placementByConv.get(c.id) === colId)
                const primaryItems = filteredConversations.filter(c => !inCustomColumn(c))

                // Helper: dado um card, retorna a lista ordenada da sua coluna (pra shift+range)
                const columnOfCard = (id: string): { key: string; items: typeof filteredConversations } => {
                  const placedColId = placements.find(p => p.conversation_id === id)?.column_id
                  if (placedColId && customCols.some(c => c.id === placedColId)) {
                    return { key: placedColId, items: cardsByColumn(placedColId) }
                  }
                  return { key: '__primary__', items: primaryItems }
                }
                const handleCardClick = (conv: typeof filteredConversations[number], e: React.MouseEvent) => {
                  // ctrl/⌘ toggle
                  if (e.ctrlKey || e.metaKey) {
                    e.preventDefault()
                    setSelectedCardIds(prev => {
                      const next = new Set(prev)
                      next.has(conv.id) ? next.delete(conv.id) : next.add(conv.id)
                      return next
                    })
                    setLastSelectedId(conv.id)
                    return
                  }
                  // shift range na mesma coluna
                  if (e.shiftKey && lastSelectedId && lastSelectedId !== conv.id) {
                    e.preventDefault()
                    const cur = columnOfCard(conv.id)
                    const last = columnOfCard(lastSelectedId)
                    if (cur.key === last.key) {
                      const startIdx = cur.items.findIndex(i => i.id === lastSelectedId)
                      const endIdx = cur.items.findIndex(i => i.id === conv.id)
                      if (startIdx >= 0 && endIdx >= 0) {
                        const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx]
                        const rangeIds = cur.items.slice(from, to + 1).map(i => i.id)
                        setSelectedCardIds(prev => {
                          const next = new Set(prev)
                          rangeIds.forEach(id => next.add(id))
                          return next
                        })
                      }
                    } else {
                      // colunas diferentes — comporta como ctrl (toggle do atual)
                      setSelectedCardIds(prev => { const n = new Set(prev); n.add(conv.id); return n })
                    }
                    setLastSelectedId(conv.id)
                    return
                  }
                  // Click normal: limpa seleção e abre conversa
                  if (selectedCardIds.size > 0) setSelectedCardIds(new Set())
                  setLastSelectedId(conv.id)
                  loadMessages(conv)
                }

                // Card do kanban — visual idêntico ao item da lista: avatar com badges,
                // nome + botão excluir (hover only), preview da última mensagem, timestamp.
                const renderCard = (conv: typeof filteredConversations[number]) => {
                  const hasUnread = (conv.unread_count || 0) > 0
                  // Pulso só quando a última msg foi RECEBIDA (enviadas não justificam atenção visual)
                  const shouldPulse = hasUnread && !conv.last_message_from_me
                  const urgencyColors = hasUnread ? getUnreadUrgencyColor(conv.last_message_at) : null
                  const botActiveForConv = isBotEffectivelyActive(conv)
                  const humanTakeoverForConv = isHumanTakeover(conv)
                  const isSelected = selectedConversation?.id === conv.id
                  const draggable = !!activeModelId
                  const smartDate = (() => {
                    if (!conv.last_message_at) return ''
                    const d = new Date(conv.last_message_at)
                    const now = new Date()
                    if (d.toDateString() === now.toDateString())
                      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })
                    const yest = new Date(now); yest.setDate(yest.getDate() - 1)
                    if (d.toDateString() === yest.toDateString()) return 'Ontem'
                    const diff = Math.floor((now.getTime() - d.getTime()) / 86400000)
                    if (diff <= 6) return ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d.getDay()]
                    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                  })()
                  const isMultiSelected = selectedCardIds.has(conv.id)
                  return (
                    <div
                      key={conv.id}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        if (justDraggedRef.current) { e.preventDefault(); return }
                        handleCardClick(conv, e)
                      }}
                      draggable={draggable}
                      onDragStart={(e) => {
                        if (!draggable) return
                        const idsToDrag = isMultiSelected && selectedCardIds.size > 0
                          ? Array.from(selectedCardIds)
                          : [conv.id]
                        if (!isMultiSelected && selectedCardIds.size > 0) setSelectedCardIds(new Set())
                        e.dataTransfer.setData('text/plain', idsToDrag.join(','))
                        // effectAllowed obrigatório em alguns browsers (Firefox), senão drop é cancelado
                        e.dataTransfer.effectAllowed = 'move'
                        draggingIdRef.current = conv.id
                      }}
                      onDragEnd={() => { justDraggedRef.current = true; setTimeout(() => { justDraggedRef.current = false }, 100); draggingIdRef.current = null }}
                      className={neu
                        ? `neu-item ${isSelected || isMultiSelected ? 'neu-selected ' : ''}group p-2 flex items-start gap-2 min-w-0 ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`
                        : `group p-2 rounded-md border bg-card hover:bg-muted/40 transition-colors flex items-start gap-2 min-w-0 ${isMultiSelected ? 'border-sky-500 ring-2 ring-sky-500/40 bg-sky-500/5' : isSelected ? 'border-sky-500 ring-1 ring-sky-500/30' : 'border-border'} ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`
                      }
                    >
                      <div className="relative flex-shrink-0">
                        {shouldPulse && (
                          <span className={`absolute inset-0 rounded-full opacity-75 animate-ping ${urgencyColors?.badge}`} aria-hidden />
                        )}
                        <div className={`${neu ? 'neu-avatar ' : ''}relative w-9 h-9 rounded-full flex items-center justify-center bg-muted ${shouldPulse ? `ring-2 ${urgencyColors?.ring}` : ''}`}>
                          {conv.remote_profile_pic && !failedProfilePics.has(conv.id) ? (
                            <img
                              src={conv.remote_profile_pic}
                              alt=""
                              draggable={false}
                              className="w-full h-full rounded-full object-cover"
                              onError={() => setFailedProfilePics(prev => new Set(prev).add(conv.id))}
                            />
                          ) : (
                            <FiUser className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        {/* Badge unread count */}
                        {conv.unread_count > 0 && (
                          <span className={`absolute -top-1 -right-1 ${urgencyColors?.badge} text-white text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1`}>
                            {conv.unread_count}
                          </span>
                        )}
                        {/* Badge tipo (G/E/L/C/N) */}
                        <div className={`absolute -bottom-0.5 -right-0.5 w-[14px] h-[14px] rounded-full flex items-center justify-center ring-1 ring-background text-[8px] font-bold text-white ${
                          conv.tipo_consultor === '0' ? 'bg-slate-700' : conv.tipo_consultor === '1' ? 'bg-amber-500' : conv.tipo_consultor === '2' ? 'bg-purple-600' : conv.tipo_consultor === '3' ? 'bg-sky-500' : 'bg-emerald-500'
                        }`} title={conv.tipo_consultor === '0' ? 'Ger. Distribuição' : conv.tipo_consultor === '1' ? 'Empresário' : conv.tipo_consultor === '2' ? 'Líder' : conv.tipo_consultor === '3' ? 'Consultora' : 'Novo Lead'}>
                          {conv.tipo_consultor === '0' ? 'G' : conv.tipo_consultor === '1' ? 'E' : conv.tipo_consultor === '2' ? 'L' : conv.tipo_consultor === '3' ? 'C' : 'N'}
                        </div>
                        {/* Badge IA/humano */}
                        {(botActiveForConv || humanTakeoverForConv) && (
                          <div
                            className={`absolute -top-0.5 -left-0.5 w-[14px] h-[14px] rounded-full flex items-center justify-center ring-1 ring-background ${humanTakeoverForConv ? 'bg-amber-500' : 'bg-blue-500'}`}
                            title={humanTakeoverForConv ? 'Humano assumiu' : 'IA ativa'}
                          >
                            {humanTakeoverForConv ? <RiUserLine className="w-2.5 h-2.5 text-white" /> : <AIIcon className="w-2.5 h-2.5" active color="white" />}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <span className={`text-[11px] truncate min-w-0 ${hasUnread ? 'font-bold' : 'font-medium'} text-foreground`}>
                            {getContactName(conv)}
                          </span>
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="p-0.5 rounded hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                            title="Excluir conversa"
                          >
                            <FiTrash2 className="w-3 h-3 text-red-500" />
                          </button>
                        </div>
                        <div className="flex items-end justify-between gap-1 mt-0.5">
                          <p className={`text-[10px] truncate min-w-0 ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {formatLastMessage(conv.last_message)}
                          </p>
                          <span className="text-[9px] text-muted-foreground/70 shrink-0" title={fmtCGTooltip(conv.last_message_at)}>{smartDate}</span>
                        </div>
                      </div>
                    </div>
                  )
                }

                // Handlers de drop e hover. preventDefault em dragenter/dragover é OBRIGATÓRIO
                // pra browser aceitar o drop. dropEffect='move' garante o cursor correto e
                // evita que Firefox cancele com "🚫" e devolva o card à origem.
                const resetHoverTimer = () => {
                  if (dropHoverTimerRef.current) { clearTimeout(dropHoverTimerRef.current); dropHoverTimerRef.current = null }
                }
                const onDragEnterColumn = (e: React.DragEvent) => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                }
                const onDragOverColumn = (key: string) => (e: React.DragEvent) => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  if (dropHoverKey === key) return
                  resetHoverTimer()
                  dropHoverTimerRef.current = setTimeout(() => setDropHoverKey(key), 500)
                }
                // dragleave dispara em FILHOS via bubble; ignorar quando relatedTarget é descendente.
                const onDragLeaveColumn = (e: React.DragEvent) => {
                  const related = e.relatedTarget as Node | null
                  if (related && (e.currentTarget as Node).contains(related)) return
                  resetHoverTimer()
                  setDropHoverKey(null)
                }
                const onDropTo = (targetColumnId: string | null) => (e: React.DragEvent) => {
                  e.preventDefault()
                  e.stopPropagation()
                  resetHoverTimer()
                  setDropHoverKey(null)
                  const raw = e.dataTransfer.getData('text/plain')
                  const ids = raw.split(',').map(s => s.trim()).filter(Boolean)
                  if (ids.length > 0) moveCards(ids, targetColumnId)
                  setSelectedCardIds(new Set())
                }

                const primaryTitle = filterCampaign || 'Todos os contatos'
                const totalCols = 1 + customCols.length
                const emptySlots = activeModelId ? Math.max(0, MAX_KANBAN_COLS - totalCols) : 0
                const colWrapperBase = 'grow-0 shrink-0 basis-[calc(25%-6px)] min-w-[260px] flex flex-col rounded-md min-h-0'
                const activeModel = models.find(m => m.id === activeModelId)

                return (
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Header do kanban: seletor de modelo */}
                    <div className="flex-shrink-0 px-2 py-1.5 border-b border-border/40 flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Modelo:</span>
                      <div ref={modelMenuRef} className="relative">
                        <button
                          type="button"
                          onClick={() => setModelMenuOpen(o => !o)}
                          className={`inline-flex items-center gap-1 px-2 h-7 rounded-md border text-xs font-medium transition-colors ${activeModelId ? 'bg-sky-500/15 border-sky-500/40 text-sky-700 dark:text-sky-300' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
                        >
                          <span className="max-w-[160px] truncate">{activeModel?.name || 'Nenhum (apenas leitura)'}</span>
                          <FiChevronDown className={`w-3 h-3 transition-transform ${modelMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {modelMenuOpen && (
                          <div className="absolute left-0 top-full mt-1 z-50 w-72 max-w-[calc(100vw-1.5rem)] max-h-80 overflow-auto rounded-lg glass-popover">
                            <button
                              type="button"
                              onClick={() => { setActiveModelId(null); setModelMenuOpen(false) }}
                              className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-border/50 ${!activeModelId ? 'bg-foreground/10 font-medium' : ''}`}
                            >Nenhum (apenas leitura)</button>
                            {models.map(m => (
                              <div key={m.id} className={`flex items-center hover:bg-muted/60 ${activeModelId === m.id ? 'bg-sky-500/10' : ''}`}>
                                <button
                                  type="button"
                                  onClick={() => { setActiveModelId(m.id); setModelMenuOpen(false) }}
                                  className="flex-1 text-left px-3 py-2 text-xs min-w-0"
                                >
                                  <div className={`truncate ${activeModelId === m.id ? 'font-medium' : ''}`}>{m.name}</div>
                                  <div className="text-[10px] text-muted-foreground truncate">{m.owner_name || m.owner_username || '—'} · {m.column_count || 0} colunas</div>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); deleteModel(m.id) }}
                                  title="Excluir modelo"
                                  className="px-2 py-2 text-muted-foreground hover:text-destructive"
                                ><FiX className="w-3 h-3" /></button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const name = prompt('Nome do novo modelo:', `Modelo ${models.length + 1}`)
                                if (name?.trim()) { createModel(name.trim()); setModelMenuOpen(false) }
                              }}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-t border-border/50 text-sky-600 dark:text-sky-400 font-medium inline-flex items-center gap-1.5"
                            ><FiPlus className="w-3 h-3" /> Novo modelo para {primaryTitle}</button>
                          </div>
                        )}
                      </div>
                      {activeModel && (
                        <button
                          type="button"
                          onClick={() => {
                            const name = prompt('Renomear modelo:', activeModel.name)
                            if (name?.trim()) renameModel(activeModel.id, name.trim())
                          }}
                          className="text-[10px] text-muted-foreground hover:text-foreground"
                          title="Renomear modelo"
                        >Renomear</button>
                      )}
                    </div>

                    {/* Board */}
                    <div className="flex-1 flex gap-2 overflow-x-auto overflow-y-hidden p-2 min-h-0">
                    {/* Coluna primária */}
                    <div
                      className={`${colWrapperBase} bg-muted/30 border-t-2 border-t-sky-500 border-x border-b border-border transition-shadow ${dropHoverKey === '__primary__' ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20' : ''}`}
                      onDragEnter={onDragEnterColumn}
                      onDragOver={onDragOverColumn('__primary__')}
                      onDragLeave={onDragLeaveColumn}
                      onDrop={onDropTo(null)}
                    >
                      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/60 flex-shrink-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2 h-2 rounded-full bg-sky-500" />
                          <span className="text-[11px] font-semibold truncate" title="Coluna padrão — não classificados no modelo">{primaryTitle}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground tabular-nums">{primaryItems.length}</span>
                      </div>
                      <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5 min-h-0">
                        {dropHoverKey === '__primary__' && (
                          <div className="border-2 border-dashed border-emerald-500 rounded-md py-6 flex items-center justify-center text-[10px] font-medium text-emerald-600 bg-emerald-500/5 animate-pulse">
                            Solte aqui
                          </div>
                        )}
                        {primaryItems.length === 0 && dropHoverKey !== '__primary__'
                          ? <div className="text-[10px] text-muted-foreground/60 text-center py-4">— vazia —</div>
                          : primaryItems.map(renderCard)
                        }
                      </div>
                    </div>

                    {/* Colunas customizadas (apenas se há modelo ativo) */}
                    {activeModelId && customCols.map(col => {
                      const c = colorOf(col.color)
                      const isEditing = editingColId === col.id
                      const items = cardsByColumn(col.id)
                      return (
                        <div
                          key={col.id}
                          className={`${colWrapperBase} bg-muted/30 border-t-2 ${c.border} border-x border-b border-border transition-shadow ${dropHoverKey === col.id ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20' : ''}`}
                          onDragEnter={onDragEnterColumn}
                          onDragOver={onDragOverColumn(col.id)}
                          onDragLeave={onDragLeaveColumn}
                          onDrop={onDropTo(col.id)}
                        >
                          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border/60 flex-shrink-0">
                            <div className="relative shrink-0">
                              <button type="button" onClick={() => setColorMenuColId(colorMenuColId === col.id ? null : col.id)} title="Mudar cor" className={`w-3 h-3 rounded-full ${c.dot} hover:ring-2 hover:ring-foreground/20 transition`} />
                              {colorMenuColId === col.id && (
                                <div ref={colorMenuRef} className="absolute left-0 top-full mt-1 z-50 flex gap-1 p-1.5 rounded-md glass-popover">
                                  {KANBAN_COLORS.map(k => (
                                    <button key={k.key} type="button" onClick={() => { recolorKanbanCol(col.id, k.key); setColorMenuColId(null) }} title={k.label}
                                      className={`w-4 h-4 rounded-full ${k.dot} ${col.color === k.key ? 'ring-2 ring-foreground' : 'hover:ring-2 hover:ring-foreground/40'} transition`} />
                                  ))}
                                </div>
                              )}
                            </div>
                            {isEditing ? (
                              <input type="text" defaultValue={col.title} autoFocus
                                onBlur={(e) => { renameKanbanCol(col.id, e.currentTarget.value); setEditingColId(null) }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { renameKanbanCol(col.id, e.currentTarget.value); setEditingColId(null) }
                                  if (e.key === 'Escape') setEditingColId(null)
                                }}
                                className="flex-1 min-w-0 bg-background border border-border rounded px-1 py-0.5 text-[11px] font-semibold focus:outline-none focus:ring-1 focus:ring-sky-500"
                              />
                            ) : (
                              <button type="button" onClick={() => setEditingColId(col.id)} title="Renomear"
                                className="flex-1 min-w-0 text-left text-[11px] font-semibold truncate hover:text-sky-500 transition"
                              >{col.title}</button>
                            )}
                            <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{items.length}</span>
                            <button type="button" onClick={() => removeKanbanCol(col.id)} title="Remover coluna"
                              className="shrink-0 p-0.5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition"
                            ><FiX className="w-3 h-3" /></button>
                          </div>
                          <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5 min-h-0">
                            {dropHoverKey === col.id && (
                              <div className="border-2 border-dashed border-emerald-500 rounded-md py-6 flex items-center justify-center text-[10px] font-medium text-emerald-600 bg-emerald-500/5 animate-pulse">
                                Solte aqui
                              </div>
                            )}
                            {items.length === 0 && dropHoverKey !== col.id
                              ? <div className="text-[10px] text-muted-foreground/60 text-center py-4">— solte um card aqui —</div>
                              : items.map(renderCard)
                            }
                          </div>
                        </div>
                      )
                    })}

                    {/* Slots vazios — visíveis só com modelo ativo */}
                    {Array.from({ length: emptySlots }).map((_, i) => (
                      <div key={`slot_${i}`} className={`${colWrapperBase} border-2 border-dashed border-border/50 bg-transparent flex items-center justify-center`}>
                        <button type="button" onClick={addKanbanCol}
                          className="inline-flex flex-col items-center gap-1 px-3 py-2 rounded-md text-muted-foreground hover:text-sky-500 hover:bg-muted/30 transition-colors"
                        ><FiPlus className="w-5 h-5" /><span className="text-[11px] font-medium">Criar coluna</span></button>
                      </div>
                    ))}
                    </div>
                  </div>
                )
              })()}
              {/* Lista de Conversas Ativas - RESPONSIVO */}
              {viewMode === 'list' && <>
                {filteredConversations.length === 0 ? (
                    <div className="p-4 sm:p-8 text-center text-muted-foreground">
                      <FiMessageSquare className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3 opacity-50" />
                      <p className="text-xs sm:text-sm">{searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}</p>
                      <p className="text-[10px] sm:text-xs mt-1">{searchTerm ? 'Tente outro termo de busca' : 'Mensagens aparecerão aqui'}</p>
                    </div>
                  ) : (
                    filteredConversations.map(conv => {
                      const hasUnread = (conv.unread_count || 0) > 0
                      // Pulso só quando a última msg foi RECEBIDA
                      const shouldPulse = hasUnread && !conv.last_message_from_me
                      const urgencyColors = hasUnread ? getUnreadUrgencyColor(conv.last_message_at) : null
                      const botActiveForConv = isBotEffectivelyActive(conv)
                      const humanTakeoverForConv = isHumanTakeover(conv)
                      // Data inteligente: hoje → HH:MM, ontem → "Ontem", esta semana → "Seg"…, mais antigo → "15/05"
                      const smartDate = (() => {
                        if (!conv.last_message_at) return ''
                        const d = new Date(conv.last_message_at)
                        const now = new Date()
                        if (d.toDateString() === now.toDateString())
                          return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })
                        const yest = new Date(now); yest.setDate(yest.getDate() - 1)
                        if (d.toDateString() === yest.toDateString()) return 'Ontem'
                        const diff = Math.floor((now.getTime() - d.getTime()) / 86400000)
                        if (diff <= 6) return ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d.getDay()]
                        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                      })()
                      return (
                        <div
                          key={conv.id}
                          onClick={() => loadMessages(conv)}
                          className={neu
                            ? `neu-item ${selectedConversation?.id === conv.id ? 'neu-selected ' : ''}group px-2 sm:px-3 py-2 sm:py-2.5 cursor-pointer`
                            : `group px-2 sm:px-3 py-2 sm:py-2.5 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors ${selectedConversation?.id === conv.id ? 'bg-secondary' : ''}`
                          }
                        >
                          <div className="flex items-start gap-2 sm:gap-2.5">
                            {/* Avatar com badges */}
                            <div className="relative flex-shrink-0">
                              {/* Anel pulsante atrás do avatar — só quando há mensagem RECEBIDA não lida.
                                  animate-ping cria a "onda de rádio" expandindo pra fora; cor
                                  segue a urgência (verde <6h, amarelo 6-24h, vermelho >24h). */}
                              {shouldPulse && (
                                <span
                                  className={`absolute inset-0 rounded-full opacity-75 animate-ping ${urgencyColors?.badge}`}
                                  aria-hidden
                                />
                              )}
                              <div className={`${neu ? 'neu-avatar ' : ''}relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-muted ${
                                shouldPulse ? `ring-2 ${urgencyColors?.ring}` : ''
                              }`}>
                                {conv.remote_profile_pic && !failedProfilePics.has(conv.id) ? (
                                  <img
                                    src={conv.remote_profile_pic}
                                    alt=""
                                    className="w-full h-full rounded-full object-cover"
                                    onError={() => setFailedProfilePics(prev => new Set(prev).add(conv.id))}
                                  />
                                ) : (
                                  <FiUser className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                                )}
                              </div>
                              {/* Badge de não lidas — urgência só aqui */}
                              {conv.unread_count > 0 && (
                                <span className={`absolute -top-1 -right-1 ${urgencyColors?.badge} text-white text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1`}>
                                  {conv.unread_count}
                                </span>
                              )}
                              {/* Badge tipo consultor — inferior direito (G/E/L/C/N) */}
                              <div className={`absolute -bottom-0.5 -right-0.5 w-[14px] h-[14px] rounded-full flex items-center justify-center ring-1 ring-background text-[8px] font-bold text-white ${
                                conv.tipo_consultor === '0' ? 'bg-slate-700' : conv.tipo_consultor === '1' ? 'bg-amber-500' : conv.tipo_consultor === '2' ? 'bg-purple-600' : conv.tipo_consultor === '3' ? 'bg-sky-500' : 'bg-emerald-500'
                              }`} title={conv.tipo_consultor === '0' ? 'Ger. Distribuição' : conv.tipo_consultor === '1' ? 'Empresário' : conv.tipo_consultor === '2' ? 'Líder' : conv.tipo_consultor === '3' ? 'Consultora' : 'Novo Lead'}>
                                {conv.tipo_consultor === '0' ? 'G' : conv.tipo_consultor === '1' ? 'E' : conv.tipo_consultor === '2' ? 'L' : conv.tipo_consultor === '3' ? 'C' : 'N'}
                              </div>
                              {/* Badge IA — azul quando ativa, âmbar no human takeover, oculto quando inativa */}
                              {(botActiveForConv || humanTakeoverForConv) && (
                                <div
                                  className={`absolute -top-0.5 -left-0.5 w-[14px] h-[14px] rounded-full flex items-center justify-center ring-1 ring-background ${
                                    humanTakeoverForConv ? 'bg-amber-500' : 'bg-blue-500'
                                  }`}
                                  title={humanTakeoverForConv ? `Humano assumiu · ${getHumanTakeoverTimeRemaining(conv)} restante` : 'IA ativa'}
                                >
                                  {humanTakeoverForConv
                                    ? <RiUserLine className="w-2 h-2 text-white" />
                                    : <AIIcon className="w-2 h-2" active={true} color="white" />
                                  }
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Linha 1: Nome | Excluir */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1 min-w-0 flex-1">
                                  <span className={`text-[11px] truncate ${hasUnread ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>
                                    {getContactName(conv)}
                                  </span>
                                  {/* Bloqueado vem PRIMEIRO: é a informação mais crítica da linha —
                                      quem atende precisa saber antes de qualquer outra coisa. */}
                                  {conv.blocked && (
                                    <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 font-bold whitespace-nowrap cursor-help" title={blockedTitle(conv.blocked)}>
                                      <FiSlash className="w-2.5 h-2.5" />
                                      BLOQUEADO
                                    </span>
                                  )}
                                  {conv.converted_consultant_at && (() => {
                                    const iso = (conv.converted_consultant_at as string).includes('T') ? conv.converted_consultant_at as string : (conv.converted_consultant_at as string).replace(' ', 'T') + 'Z'
                                    return (Date.now() - new Date(iso).getTime()) < 28 * 24 * 60 * 60 * 1000 ? (
                                      <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-bold whitespace-nowrap" title="Virou consultora recentemente">✦ Convertida</span>
                                    ) : null
                                  })()}
                                  {conv.awaiting_leader && (
                                    <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/40 font-bold whitespace-nowrap" title="Lead finalizado pelo bot — atribua a uma líder">⚑ Aguarda líder</span>
                                  )}
                                  {conv.assigned_leader && (
                                    <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/40 font-bold whitespace-nowrap max-w-[180px] truncate" title={`Atribuído a ${conv.assigned_leader.name || 'líder'}${conv.assigned_leader.assigned_at ? ` em ${new Date(conv.assigned_leader.assigned_at).toLocaleDateString('pt-BR')}` : ''}`}>⚑ {conv.assigned_leader.name?.split(' ')[0] || 'líder'}</span>
                                  )}
                                </div>
                                <div className="flex items-center flex-shrink-0">
                                  {/* Botão Excluir — visível só no hover */}
                                  <button
                                    onClick={(e) => deleteConversation(conv.id, e)}
                                    className="p-1 rounded hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                                    title="Excluir conversa"
                                  >
                                    <FiTrash2 className="w-3 h-3 text-red-500" />
                                  </button>
                                </div>
                              </div>
                              {/* Linha 2: Mensagem | Data/Hora */}
                              <div className="flex items-center justify-between mt-0.5">
                                <p className={`text-[11px] truncate flex-1 min-w-0 pr-2 ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                                  {formatLastMessage(conv.last_message)}
                                </p>
                                <span className="text-[11px] flex-shrink-0 text-muted-foreground group inline-flex items-center gap-0.5" title={fmtCGTooltip(conv.last_message_at)}>
                                  {smartDate}
                                  <FiInfo className="w-2.5 h-2.5 text-sky-500/80 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                  {/* Sentinel de infinite scroll — IntersectionObserver dispara load.
                      rootMargin 200px antecipa o load antes do user encostar no fim. */}
                  {viewMode === 'list' && totalConversations !== null && conversations.length < totalConversations && (
                    <div ref={listSentinelRef} className="py-3 text-center text-[10px] text-muted-foreground">
                      Carregando mais…
                    </div>
                  )}
                </>}
            </div>

            {/* Rodapé fixo: só contagem (sem botões de página — infinite scroll).
                No modo neu a barra é omitida — só atrapalha; a rolagem infinita
                (sentinel acima) segue funcionando sem ela. */}
            {!neu && (
            <div className="flex-shrink-0 p-2 border-t border-border flex items-center justify-center gap-1 bg-card">
              <span className="text-[10px] text-muted-foreground">
                {viewMode === 'list' && totalConversations !== null
                  ? `${conversations.length} de ${totalConversations} conversas`
                  : `${totalConversations ?? conversations.length} conversas`
                }
              </span>
            </div>
            )}
            </div>
          </div>

          {/* Chat - RESPONSIVO. No kanban, larga fixa ~30% pra liberar 60% pro board e 10% pro perfil. */}
          <div className={`${neu ? 'neu-canvas ' : ''}flex flex-col bg-card min-h-0 ${selectedConversation ? 'flex' : 'hidden md:flex'} ${viewMode === 'kanban' ? (neu ? 'flex-1 min-w-0' : 'lg:w-[30%] xl:w-[30%] flex-shrink-0') : 'flex-1 min-w-0'}`}>
            {selectedConversation ? (
              <>
                {/* Header contextual de seleção — substitui o header normal quando ativo.
                    Sinaliza forte que o modo do chat mudou pra "encaminhamento". */}
                {selectionMode ? (
                <div className="px-3 py-2 bg-sky-500/10 border-b-2 border-sky-500/40 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <button
                      onClick={exitSelectionMode}
                      className="p-1 hover:bg-sky-500/20 rounded-lg transition-colors text-sky-600 dark:text-sky-400 flex-shrink-0"
                      title="Sair do modo seleção"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                    <div className="flex items-baseline gap-1.5 min-w-0">
                      <span className="text-sm font-bold text-sky-700 dark:text-sky-300 tabular-nums">
                        {selectedMessageIds.size}
                      </span>
                      <span className="text-[11px] text-sky-700/80 dark:text-sky-300/80">
                        {selectedMessageIds.size === 1 ? 'mensagem selecionada' : 'mensagens selecionadas'}
                      </span>
                      {selectedMessageIds.size === 0 && (
                        <span className="text-[10px] text-muted-foreground italic ml-1">· toque numa mensagem pra começar</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowForwardModal(true)}
                    disabled={selectedMessageIds.size === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                  >
                    <FiSend className="w-3.5 h-3.5" />
                    Encaminhar
                  </button>
                </div>
                ) : (
                /* Header do chat - RESPONSIVO */
                <div className={`px-2 sm:px-3 py-1.5 sm:py-2 border-b border-border flex items-center justify-between flex-shrink-0 glass-bar ${neu ? 'neu-bar' : ''}`}>
                  <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
                    {/* Botão voltar — mobile only */}
                    <button
                      onClick={() => setSelectedConversation(null)}
                      className="md:hidden p-1 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
                    >
                      <FiArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      {selectedConversation.remote_profile_pic && !failedProfilePics.has(selectedConversation.id) ? (
                        <img
                          src={selectedConversation.remote_profile_pic}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                          onError={() => setFailedProfilePics(prev => new Set(prev).add(selectedConversation.id))}
                        />
                      ) : (
                        <FiUser className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-foreground text-[11px] truncate flex items-center gap-1.5">
                        <span className="truncate">{getContactName(selectedConversation)}</span>
                        {selectedConversation.blocked && (
                          <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 font-bold whitespace-nowrap cursor-help" title={blockedTitle(selectedConversation.blocked)}>
                            <FiSlash className="w-2.5 h-2.5" />
                            BLOQUEADO
                          </span>
                        )}
                        {selectedConversation.awaiting_leader && (
                          <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/40 font-bold whitespace-nowrap" title="Lead finalizado pelo bot — atribua a uma líder">⚑ Aguarda líder</span>
                        )}
                        {selectedConversation.assigned_leader && (
                          <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/40 font-bold whitespace-nowrap max-w-[180px] truncate" title={`Atribuído a ${selectedConversation.assigned_leader.name || 'líder'}${selectedConversation.assigned_leader.assigned_at ? ` em ${new Date(selectedConversation.assigned_leader.assigned_at).toLocaleDateString('pt-BR')}` : ''}`}>⚑ {selectedConversation.assigned_leader.name?.split(' ')[0] || 'líder'}</span>
                        )}
                      </h4>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {getPhoneNumber(selectedConversation.remote_jid)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 flex-wrap justify-end">
                    {/* Perfil do contato — abre como drawer em telas <lg (no desktop
                        a coluna lateral já fica fixa, então some lá). */}
                    <button
                      onClick={() => setShowContactDrawer(true)}
                      className="xl:hidden flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-secondary/40 border border-border hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                      title="Ver perfil do contato"
                    >
                      <FiInfo className="w-4 h-4" />
                    </button>
                    {/* Ações minimalistas (portado do fin 2026-07-14): só ícone,
                        quadradinhos iguais ao botão de perfil — rótulo no tooltip. */}
                    {/* Selecionar mensagens — entra em modo seleção pra encaminhar */}
                    <button
                      onClick={() => setSelectionMode(v => !v)}
                      className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg border transition-colors ${
                        selectionMode
                          ? 'bg-sky-500/20 border-sky-500/40 text-sky-600 dark:text-sky-400'
                          : 'bg-secondary/40 border-border hover:bg-secondary text-muted-foreground hover:text-foreground'
                      }`}
                      title={selectionMode ? 'Sair do modo seleção' : 'Selecionar mensagens pra encaminhar'}
                    >
                      <FiCheckSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>

                    {/* Botão de Atribuir Líder - SEMPRE VISÍVEL */}
                    <button
                      onClick={() => setShowAssignLeaderModal(true)}
                      className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-purple-600/15 hover:bg-purple-600/25 border border-purple-600/30 transition-colors"
                      title="Atribuir lead para líder"
                    >
                      <FiUserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 dark:text-purple-400" />
                    </button>

                    {/* Toggle individual de IA removido (2026-07-14, decisão do dono):
                        as regras globais do bot + pausas automáticas (30min quando
                        humano assume; 7 dias quando líder é atribuída, com sweeper
                        de reativação) cobrem o fluxo. Estado segue visível no badge
                        dos cards da lista. */}
                  </div>
                </div>
                )}

                {/* Wrapper relative pra ancorar o botão flutuante de "novas mensagens"
                    fora do scroll (senão ele rola junto e some). O scroll real é o
                    div interno absolute inset-0. */}
                <div
                  className="flex-1 relative min-h-0"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                {dragOver && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary pointer-events-none">
                    <div className="bg-card px-4 py-2 rounded-lg shadow text-sm text-foreground flex items-center gap-2">
                      <FiUploadCloud className="w-4 h-4 text-primary" /> Solte para enviar
                    </div>
                  </div>
                )}
                {/* Mensagens - RESPONSIVO com background estilo WhatsApp.
                    space-y removido: spacing entre msgs agora é condicional. A barra
                    de seleção foi promovida pro header — bg amarelo do chat fica intacto. */}
                <div
                  ref={messagesContainerRef}
                  onScroll={handleMessagesScroll}
                  className={`absolute inset-0 overflow-y-auto p-2 sm:p-4 whatsapp-doodle-bg ${neu ? 'neu-thread' : ''}`}
                >
                  {messages.map((msg, index) => {
                    const isFromMe = msg.fromMe === true || Number(msg.from_me) === 1
                    const isBotResponse = msg.isBotResponse === true || Number(msg.is_bot_response) === 1
                    // Modelo REAL que gerou a resposta, gravado em metadata. llm_model é o
                    // answered_by do Moss (claude|qwen) — rotular pelo provider mascarava o
                    // fallback qwen (incidente 2026-07-17: 4h de qwen exibidas como "Claude").
                    const botProvider: string | undefined = (() => {
                      try {
                        const m = typeof msg.metadata === 'string' ? JSON.parse(msg.metadata || '{}') : (msg.metadata || {})
                        const model = String(m?.llm_model || '').toLowerCase()
                        if (model.includes('qwen')) return 'Qwen'
                        if (model.includes('claude')) return 'Claude'
                        const provider = String(m?.llm_provider || '').toLowerCase()
                        if (provider === 'gemma') return 'Gemma'
                        if (provider) return 'Claude'
                        return undefined
                      } catch { return undefined }
                    })()
                    const out = outboundOrigin(msg)
                    // Origem: agrupa mensagens consecutivas só quando MESMO lado E MESMA origem
                    // (bot e humano podem responder na mesma janela mas têm que ter headers separados).
                    // O rótulo entra na chave: operadores diferentes — ou uma pessoa e o
                    // follow-up automático — não colapsam sob o header do primeiro.
                    const origin = isFromMe ? (isBotResponse ? 'bot' : `${out.kind}:${out.label}`) : 'client'

                    const prev = messages[index - 1]
                    const showDate = index === 0 || formatDate(prev?.timestamp) !== formatDate(msg.timestamp)

                    // Agrupamento: mesmo lado + mesma origem + ≤5min entre mensagens
                    // (e sem quebra de dia entre elas)
                    const prevIsFromMe   = prev ? (prev.fromMe === true || Number(prev.from_me) === 1) : null
                    const prevIsBot      = prev ? (prev.isBotResponse === true || Number(prev.is_bot_response) === 1) : false
                    const prevOut        = prev ? outboundOrigin(prev) : null
                    const prevOrigin     = prev ? (prevIsFromMe ? (prevIsBot ? 'bot' : `${prevOut!.kind}:${prevOut!.label}`) : 'client') : null
                    const minutesGap     = prev?.timestamp && msg.timestamp
                      ? (new Date(msg.timestamp).getTime() - new Date(prev.timestamp).getTime()) / 60000
                      : Infinity
                    const isGrouped = !showDate && prevOrigin === origin && minutesGap <= 5

                    return (
                      <div key={msg.id} className={isGrouped ? 'mt-0.5' : (index === 0 ? '' : 'mt-3 sm:mt-4')}>
                        {showDate && (
                          <div className="flex justify-center my-2 sm:my-4">
                            <span className="bg-card px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs text-muted-foreground shadow-sm border border-border group inline-flex items-center gap-1" title={fmtCGTooltip(msg.timestamp)}>
                              {formatDate(msg.timestamp)}
                              <FiInfo className="w-3 h-3 text-sky-500/80 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </span>
                          </div>
                        )}
                        <div className={`flex items-center ${isFromMe ? 'justify-end' : 'justify-start'} gap-1.5`}>
                          {/* Em selection mode, click no bubble (ou no checkbox) alterna seleção.
                              Checkbox circular visível à ESQUERDA da bubble (com flip pra direita
                              quando é do operador) deixa o estado óbvio sem depender só do ring. */}
                          {(() => {
                            const isTextLike = msg.message_type === 'text' || (!msg.message_type && msg.content)
                            const isSelected = selectionMode && selectedMessageIds.has(msg.id)
                            const selectableClasses = selectionMode
                              ? (isTextLike
                                  ? `cursor-pointer ${isSelected ? 'ring-2 ring-sky-500 ring-offset-1 ring-offset-card' : 'hover:ring-1 hover:ring-sky-500/40'}`
                                  : 'opacity-50 cursor-not-allowed')
                              : ''

                            // Checkbox marker (só renderiza em selectionMode).
                            // Pra msg do operador (isFromMe), fica à DIREITA da bubble.
                            // Pra msg do cliente, fica à ESQUERDA.
                            const checkboxMarker = selectionMode && (
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                  !isTextLike
                                    ? 'bg-muted/40 text-muted-foreground/40'
                                    : isSelected
                                    ? 'bg-sky-500 text-white shadow-sm scale-110'
                                    : 'bg-card border-2 border-muted-foreground/30 hover:border-sky-500'
                                } ${isFromMe ? 'order-2' : 'order-0'}`}
                                title={!isTextLike ? 'Mídia não pode ser encaminhada' : (isSelected ? 'Desmarcar' : 'Marcar pra encaminhar')}
                              >
                                {!isTextLike ? <FiX className="w-3 h-3" /> : isSelected ? <FiCheck className="w-3 h-3" /> : null}
                              </div>
                            )

                            return (<>
                            {checkboxMarker}
                          <div
                            onClick={selectionMode && isTextLike ? (e) => { e.stopPropagation(); toggleMessageSelect(msg.id) } : undefined}
                            title={selectionMode && !isTextLike ? 'Apenas mensagens de texto podem ser encaminhadas' : undefined}
                            className={`${neu ? 'neu-bubble ' : ''}max-w-[88%] sm:max-w-[80%] md:max-w-[75%] lg:max-w-[70%] 2xl:max-w-[820px] rounded-2xl px-3 sm:px-3.5 py-2 sm:py-2.5 shadow-sm overflow-hidden transition-all ${
                              isFromMe
                                ? 'bg-[#d9fdd3] dark:bg-[#005c4b] text-foreground dark:text-white rounded-br-md'
                                : 'bg-card text-foreground rounded-bl-md border border-border'
                            } ${selectableClasses}`}
                          >
                            {/* Header de origem: só na 1ª mensagem do grupo (pra não repetir) */}
                            {!isGrouped && (isFromMe ? (
                              <div className={`flex items-center gap-1 text-xs mb-1 font-medium ${
                                !isBotResponse && out.kind !== 'human'
                                  ? 'text-blue-700 dark:text-blue-300'
                                  : 'text-emerald-700 dark:text-emerald-300'
                              }`}>
                                {isBotResponse ? (
                                  <>
                                    <RiRobot2Line className="w-3 h-3" />
                                    <span title="Modelo que gerou esta resposta">Bot IA{botProvider ? ` · ${botProvider}` : ''}</span>
                                  </>
                                ) : out.kind === 'campaign' ? (
                                  <>
                                    <FiSend className="w-3 h-3" />
                                    <span title={out.hint}>{out.label}</span>
                                  </>
                                ) : out.kind === 'followup' ? (
                                  <>
                                    <FiClock className="w-3 h-3" />
                                    <span title={out.hint}>{out.label}</span>
                                  </>
                                ) : (
                                  <>
                                    <RiUserLine className="w-3 h-3" />
                                    <span title={out.hint}>{out.label}</span>
                                  </>
                                )}
                              </div>
                            ) : (() => {
                              // Header do cliente: círculo com a sigla do nome, colorido
                              // pelo tipo do contato (mesma paleta dos badges G/E/L/C/N da
                              // lista: '0'=slate '1'=amber '2'=purple '3'=sky lead=emerald).
                              const tipo = selectedConversation?.tipo_consultor
                              const nome = selectedConversation?.nome_cadastro || selectedConversation?.remote_name || selectedConversation?.remote_jid?.replace(/@.*$/, '') || 'Cliente'
                              const sigla = nome.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase() || '?'
                              const corCirculo = tipo === '0' ? 'bg-slate-700' : tipo === '1' ? 'bg-amber-500' : tipo === '2' ? 'bg-purple-600' : tipo === '3' ? 'bg-sky-500' : 'bg-emerald-500'
                              const tipoLabel = tipo === '0' ? 'Ger. Distribuição' : tipo === '1' ? 'Empresário' : tipo === '2' ? 'Líder' : tipo === '3' ? 'Consultora' : 'Novo Lead'
                              return (
                                <div className="flex items-center gap-1.5 mb-1" title={tipoLabel}>
                                  <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${corCirculo}`}>
                                    {sigla}
                                  </span>
                                  <span className="text-xs font-medium text-foreground/80 truncate">{nome}</span>
                                </div>
                              )
                            })())}
                            {/* Citação/reply: quando a msg responde a outra (Meta context.id),
                                mostra um preview da original. É o que dá contexto ao toque de
                                botão da líder ("Deu certo") — cita o nudge, que nomeia o lead. */}
                            {msg.quoted_content != null && (() => {
                              const qFromMe = Number(msg.quoted_from_me) === 1
                              const qIsBot = Number(msg.quoted_is_bot_response) === 1
                              const qIsTemplate = msg.quoted_message_type === 'template'
                              const qLabel = qFromMe
                                ? (qIsTemplate ? 'Campanha' : qIsBot ? 'Bot IA' : 'Atendente')
                                : (selectedConversation?.nome_cadastro || selectedConversation?.remote_name || 'Cliente')
                              const qTone = qFromMe
                                ? (qIsTemplate
                                    ? 'border-l-blue-400 text-blue-700 dark:text-blue-300'
                                    : 'border-l-emerald-400 text-emerald-700 dark:text-emerald-300')
                                : 'border-l-purple-400 text-purple-700 dark:text-purple-300'
                              let qText = msg.quoted_content || ''
                              if (qText.startsWith('[Template:')) { const e = qText.indexOf(']'); if (e > -1) qText = qText.slice(e + 1).trim() }
                              return (
                                <div className={`mb-1.5 rounded-md border-l-[3px] ${qTone} bg-black/[0.05] dark:bg-white/[0.08] px-2 py-1 max-w-full overflow-hidden`}>
                                  <div className="text-[11px] font-semibold leading-tight truncate">{qLabel}</div>
                                  <div className="text-[12px] text-muted-foreground leading-snug truncate">{qText || 'mensagem'}</div>
                                </div>
                              )
                            })()}
                            {/* Resposta de botão (quick-reply) da líder/cliente: pill com seta de
                                reply, tingido pelo desfecho quando reconhecível (Deu certo etc.). */}
                            {msg.message_type === 'button' && (() => {
                              const t = (msg.content || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
                              const tone = (t.includes('nao deu'))
                                ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30'
                                : t.includes('deu certo')
                                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                                : t.includes('tentando')
                                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30'
                                : 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30'
                              return (
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold border ${tone}`}>
                                  <FiCornerUpLeft className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="break-words">{msg.content || '[Botão]'}</span>
                                </span>
                              )
                            })()}
                            {/* Renderizar mídia se não for texto (template/botão têm render próprio) */}
                            {msg.message_type !== 'text' && msg.message_type !== 'template' && msg.message_type !== 'button' && renderMediaContent(msg)}
                            {/* Conteúdo de texto (ou caption para mídia) — formatado igual WhatsApp:
                                *negrito*, _itálico_, ~tachado~, `mono`, > citação, listas. */}
                            {msg.content && msg.message_type === 'text' && (
                              <p className="whitespace-pre-wrap break-words overflow-hidden">{formatWhatsAppText(msg.content)}</p>
                            )}
                            {/* Mensagem de template (campanha) — card estilo WhatsApp:
                                imagem de header + corpo + footer + botões com divisória. */}
                            {msg.content && msg.message_type === 'template' && (() => {
                              let templateMeta: { footer?: string; template_name?: string; header_format?: string; buttons?: Array<{ type: string; text: string; url?: string; phone?: string }> } = {};
                              try {
                                templateMeta = typeof msg.metadata === 'string'
                                  ? JSON.parse(msg.metadata || '{}')
                                  : ((msg.metadata as typeof templateMeta) || {});
                              } catch { templateMeta = {}; }

                              let bodyContent = msg.content;
                              if (bodyContent.startsWith('[Template:')) {
                                const bracketEnd = bodyContent.indexOf(']');
                                if (bracketEnd > -1) bodyContent = bodyContent.substring(bracketEnd + 1).trim();
                              }
                              if (bodyContent.startsWith('Variáveis:')) bodyContent = bodyContent.substring(10).trim();
                              // Legacy: nudges antigos da líder anexavam "📲 Botões enviados à líder:
                              // [ Deu certo ]…" no corpo (antes de virarem botões de verdade via
                              // metadata.buttons). Remove o texto cru pra os antigos também ficarem limpos.
                              bodyContent = bodyContent.replace(/\n*📲 Botões enviados à líder:[\s\S]*$/, '').trim();

                              const headerUrl = getMediaUrl(msg);
                              const headerIsVideo = templateMeta.header_format === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(headerUrl || '');
                              const tplButtons = Array.isArray(templateMeta.buttons) ? templateMeta.buttons : [];

                              return (
                                <div
                                  className="rounded-lg overflow-hidden bg-card text-foreground border border-border shadow-sm"
                                  title={templateMeta.template_name ? `Template: ${templateMeta.template_name}` : undefined}
                                >
                                  {headerUrl && (headerIsVideo ? (
                                    <div className="relative cursor-pointer" onClick={() => setMediaModal({ url: headerUrl, type: 'video' })}>
                                      <video
                                        src={headerUrl}
                                        className="w-full max-h-56 object-cover"
                                        muted
                                        preload="metadata"
                                        onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }}
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                                        <FiPlay className="w-12 h-12 text-white" />
                                      </div>
                                    </div>
                                  ) : (
                                    <img
                                      src={headerUrl}
                                      alt=""
                                      loading="lazy"
                                      className="w-full max-h-56 object-cover cursor-pointer hover:opacity-95 transition-opacity"
                                      onClick={() => setMediaModal({ url: headerUrl, type: 'image' })}
                                      onError={(e) => { e.currentTarget.style.display = 'none' }}
                                    />
                                  ))}
                                  <div className="px-2.5 py-2">
                                    {bodyContent && (
                                      <p className="text-sm whitespace-pre-wrap break-words">{formatWhatsAppText(bodyContent)}</p>
                                    )}
                                    {templateMeta.footer && (
                                      <p className="text-[11px] text-muted-foreground mt-1.5 whitespace-pre-wrap break-words">{formatWhatsAppText(templateMeta.footer)}</p>
                                    )}
                                  </div>
                                  {tplButtons.length > 0 && (
                                    <div>
                                      {tplButtons.map((btn, idx) => {
                                        const BtnIcon = btn.type === 'URL' ? FiExternalLink : btn.type === 'PHONE_NUMBER' ? FiPhone : FiCornerUpLeft;
                                        const btnCls = 'flex items-center justify-center gap-1.5 w-full px-3 py-2 text-[13px] font-medium text-[#0096de] dark:text-[#53bdeb] border-t border-border hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors';
                                        const btnInner = (<><BtnIcon className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{btn.text}</span></>);
                                        if (btn.type === 'URL' && btn.url) {
                                          return <a key={idx} href={btn.url} target="_blank" rel="noopener noreferrer" className={btnCls}>{btnInner}</a>;
                                        }
                                        if (btn.type === 'PHONE_NUMBER' && btn.phone) {
                                          return <a key={idx} href={`tel:${btn.phone}`} className={btnCls}>{btnInner}</a>;
                                        }
                                        return <span key={idx} className={btnCls}>{btnInner}</span>;
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Caption para mídia (botão tem pill próprio acima — não duplicar) */}
                            {msg.content && msg.message_type !== 'text' && msg.message_type !== 'template' && msg.message_type !== 'button' && !['[Imagem]', '[Vídeo]', '[Áudio]', '[Documento]', '[Sticker]', '[Localização]', '[Contato]', '[IMAGE]', '[VIDEO]', '[AUDIO]', '[DOCUMENT]', '[STICKER]', '[LOCALIZAÇÃO]', '[CONTATO]', '[ÁUDIO]'].includes(msg.content) && !msg.content.startsWith('[') && (
                              <p className="whitespace-pre-wrap break-words overflow-hidden text-sm mt-1">{formatWhatsAppText(msg.content)}</p>
                            )}
                            {/* Footer da bubble: timestamp + check status.
                                Cores ajustadas pro contraste contra o verde claro/escuro do fromMe
                                (text-muted-foreground não funciona bem em fundo verde). */}
                            <div className={`flex items-center justify-end gap-1 mt-1 text-[11px] ${
                              isFromMe ? 'text-black/55 dark:text-white/65' : 'text-muted-foreground'
                            }`}>
                              <span title={fmtCGTooltip(msg.timestamp)} className="group inline-flex items-center gap-0.5">
                                {formatTime(msg.timestamp)}
                                <FiInfo className="w-2.5 h-2.5 text-sky-500/80 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </span>
                              {isFromMe && (
                                msg.status === 'read' ? (
                                  <FiCheckCircle className="w-3.5 h-3.5 text-sky-600 dark:text-sky-300" />
                                ) : msg.status === 'delivered' ? (
                                  <FiCheck className="w-3.5 h-3.5" />
                                ) : (
                                  <FiCheck className="w-3.5 h-3.5 opacity-50" />
                                )
                              )}
                            </div>
                          </div>
                            </>)
                          })()}
                        </div>
                      </div>
                    )
                  })}

                  {/* Indicador de digitando */}
                  {typingConversations.has(selectedConversation.id) && (
                    <div className="flex justify-start">
                      <div className="bg-card rounded-lg px-4 py-2 shadow-sm border border-border">
                        <div className="flex items-center gap-1">
                          <RiRobot2Line className="w-4 h-4 text-primary" />
                          <span className="text-muted-foreground text-sm">Digitando</span>
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Botão flutuante de "novas mensagens" — só aparece quando o usuário está
                    lendo histórico (longe do fim) E há mensagens não vistas. Click rola
                    pro fim e zera o contador. */}
                {!isNearBottom && unseenCount > 0 && (
                  <button
                    onClick={scrollToBottom}
                    className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full pl-2 pr-3 py-1.5 shadow-lg hover:bg-primary/90 transition-colors text-xs font-medium"
                    title="Ir para a última mensagem"
                  >
                    <FiArrowDown className="w-3.5 h-3.5" />
                    <span>{unseenCount} {unseenCount === 1 ? 'nova' : 'novas'}</span>
                  </button>
                )}
                </div>

                {/* Input "pill": container único arredondado com anexo + textarea + enviar.
                    items-end alinha o anexo e o botão ao bottom quando o textarea cresce
                    (em vez de flutuarem no centro). Textarea sem fundo/borda própria —
                    o container externo é quem dá a aparência. */}
                <div className={`px-2 sm:px-3 py-2 border-t border-border glass-bar ${neu ? 'neu-bar' : ''}`}>
                  <div className={`flex items-end gap-1.5 bg-muted rounded-2xl pl-1 pr-1 py-1 ${neu ? 'neu-inset' : ''}`}>
                    {/* Menu de anexos */}
                    <div className="relative shrink-0" ref={attachMenuRef}>
                      <button
                        onClick={() => setShowAttachMenu(!showAttachMenu)}
                        className="w-9 h-9 flex items-center justify-center hover:bg-foreground/10 rounded-full transition-colors text-muted-foreground hover:text-foreground"
                        title="Anexar arquivo"
                      >
                        <FiPaperclip className="w-5 h-5" />
                      </button>

                      {showAttachMenu && (
                        <div className="absolute bottom-full left-0 mb-2 glass-popover rounded-lg py-1 min-w-[150px] z-10">
                          <label className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer transition-colors">
                            <FiImage className="w-4 h-4 text-blue-500" />
                            <span className="text-sm">Imagem</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileSelect(e, 'image')}
                            />
                          </label>
                          <label className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer transition-colors">
                            <FiVideo className="w-4 h-4 text-purple-500" />
                            <span className="text-sm">Vídeo</span>
                            <input
                              type="file"
                              accept="video/*"
                              className="hidden"
                              onChange={(e) => handleFileSelect(e, 'video')}
                            />
                          </label>
                          <label className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer transition-colors">
                            <FiFile className="w-4 h-4 text-orange-500" />
                            <span className="text-sm">Documento</span>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                              className="hidden"
                              onChange={(e) => handleFileSelect(e, 'document')}
                            />
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Textarea — sem fundo/borda própria; padding vertical alinha
                        a primeira linha verticalmente com o centro do botão de anexo (h-9). */}
                    <textarea
                      ref={inputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      placeholder="Digite uma mensagem…"
                      title="Digite uma mensagem — você também pode colar um print ou arrastar um arquivo aqui"
                      className="flex-1 min-w-0 bg-transparent border-0 resize-none text-sm py-2 focus:outline-none focus:ring-0 max-h-32 placeholder:text-muted-foreground"
                      rows={1}
                      disabled={isSending}
                    />

                    {/* Botão enviar */}
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || isSending}
                      className={`${neu ? 'neu-send ' : ''}shrink-0 w-9 h-9 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed rounded-full transition-colors text-primary-foreground flex items-center justify-center`}
                      title="Enviar mensagem"
                    >
                      {isSending ? (
                        <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      ) : (
                        <FiSend className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              /* Tela vazia - RESPONSIVO */
              <div className="flex-1 flex items-center justify-center text-muted-foreground p-4">
                <div className="text-center">
                  <FiMessageSquare className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 opacity-30" />
                  <p className="text-sm sm:text-lg">Selecione uma conversa</p>
                  <p className="text-xs sm:text-sm mt-1">para visualizar as mensagens</p>
                </div>
              </div>
            )}
          </div>

          {/* Coluna 3: Perfil do Contato - visível apenas em desktop quando conversa selecionada.
              No kanban encolhe pra ~10% pra liberar espaço pro board. */}
          {/* h-full + min-h-0 fixam altura na linha-pai (flex row stretch); a
              column 3 sem h-full tinha altura content-driven — expandir/colapsar
              uma seção do painel "encurtava" ou "esticava" a coluna. */}
          <div className={`${neu ? 'neu-raised ' : ''}hidden xl:flex h-full min-h-0 shrink-0 glass-panel relative z-30 transition-[width] duration-200 ${selectedConversation ? '' : 'xl:hidden'} ${profileCollapsed ? 'w-0 border-l-0' : `border-l border-border !shadow-[-1px_0_2px_0_rgba(2,6,23,0.15),-4px_0_10px_-2px_rgba(2,6,23,0.10),-10px_0_22px_-4px_rgba(2,6,23,0.07),-18px_0_36px_-8px_rgba(2,6,23,0.04)] ${viewMode === 'kanban' ? 'xl:w-[10%]' : 'w-[280px] 2xl:w-[340px]'}`}`}>
            {/* Recolher/expandir o perfil — filipeta centrada na vertical nos
                dois estados: recolhido sai da borda da tela; expandido gruda na
                borda esquerda do painel.
                REMOVIDA no tema neu (2026-07-24) junto com a da lista — a
                diretoria não usa recolher/expandir coluna. */}
            {!neu && (profileCollapsed ? (
              <button
                onClick={() => setProfileCollapsed(false)}
                title="Expandir perfil"
                className="flex absolute right-0 top-1/2 -translate-y-1/2 z-50 items-center justify-center w-7 h-9 text-muted-foreground hover:text-foreground transition-colors rounded-l-lg bg-card border border-r-0 border-border hover:bg-secondary shadow-[-4px_0_8px_-2px_rgba(2,6,23,0.12),-10px_0_18px_-4px_rgba(2,6,23,0.06)]"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => setProfileCollapsed(true)}
                title="Recolher perfil"
                className="flex absolute left-0 -translate-x-full top-1/2 -translate-y-1/2 z-50 items-center justify-center w-7 h-9 rounded-l-lg text-muted-foreground hover:text-foreground transition-colors bg-card border border-r-0 border-border hover:bg-secondary shadow-[-4px_0_8px_-2px_rgba(2,6,23,0.12),-10px_0_18px_-4px_rgba(2,6,23,0.06)]"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            ))}
            {/* Conteúdo — no colapso some, mas segue montado (sem refetch ao voltar). */}
            <div className={`h-full w-full min-h-0 overflow-hidden ${profileCollapsed ? 'hidden' : 'flex'}`}>
              <ContactProfilePanel
                neu={neu}
                phone={selectedConversation?.remote_jid || null}
                contactName={selectedConversation?.nome_cadastro || selectedConversation?.remote_name || undefined}
                conversationId={selectedConversation?.id}
                blocked={selectedConversation?.blocked}
                onBlockChange={refreshAfterBlock}
              />
            </div>
          </div>
        </div>

        {/* Drawer de perfil do contato — telas <lg (no desktop é coluna fixa à
            direita). Abre pelo botão FiInfo no header do chat. */}
        {showContactDrawer && selectedConversation && (
          <div className="fixed inset-0 z-[120] xl:hidden">
            <div className="absolute inset-0 glass-overlay" onClick={() => setShowContactDrawer(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-[380px] glass-panel flex flex-col animate-in slide-in-from-right duration-200">
              <button
                onClick={() => setShowContactDrawer(false)}
                className="absolute top-2 right-2 z-10 flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Fechar"
              >
                <FiX className="w-4 h-4" />
              </button>
              <ContactProfilePanel
                neu={neu}
                phone={selectedConversation.remote_jid || null}
                contactName={selectedConversation.nome_cadastro || selectedConversation.remote_name || undefined}
                conversationId={selectedConversation.id}
                blocked={selectedConversation.blocked}
                onBlockChange={refreshAfterBlock}
              />
            </div>
          </div>
        )}

        {/* Error toast - RESPONSIVO */}
        {error && (
          <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-red-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-lg flex items-center gap-2 text-xs sm:text-sm max-w-[90%] sm:max-w-none">
            <span className="truncate">{error}</span>
            <button onClick={() => setError(null)} className="hover:bg-red-600 p-0.5 sm:p-1 rounded flex-shrink-0">
              <FiX className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        )}

        {/* Modal de mídia expandida */}
        {mediaModal && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100]"
            onClick={() => setMediaModal(null)}
          >
            <button
              onClick={() => setMediaModal(null)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 p-1.5 sm:p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            >
              <FiX className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>
            <div className="max-w-[95vw] sm:max-w-[90vw] max-h-[85vh] sm:max-h-[90vh] p-2 sm:p-0" onClick={(e) => e.stopPropagation()}>
              {mediaModal.type === 'image' ? (
                <img
                  src={mediaModal.url}
                  alt="Mídia expandida"
                  className="max-w-full max-h-[80vh] sm:max-h-[85vh] md:max-h-[90vh] object-contain rounded-lg"
                />
              ) : (
                <video
                  src={mediaModal.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-[80vh] sm:max-h-[85vh] md:max-h-[90vh] rounded-lg"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Modal de Encaminhamento — recebe msgs selecionadas + nome do remetente */}
    <ForwardModal
      isOpen={showForwardModal}
      onClose={() => setShowForwardModal(false)}
      selectedMessages={messages.filter(m => selectedMessageIds.has(m.id))}
      sourceContactName={selectedConversation ? (selectedConversation.nome_cadastro || selectedConversation.remote_name) : null}
      onSuccess={(destConvId) => {
        exitSelectionMode()
        // Refresh da lista de conversas — o destino (depósito/financeiro) pode ser uma
        // conv recém-criada que não estava carregada antes. Sem isso, o operador
        // precisa F5 pra ver a conv subir pro topo.
        loadConversations(1, false, searchTerm)
        // Se backend devolveu o convId do destino, opcionalmente já abre a conversa
        // pra o operador acompanhar a resposta. Sem injetar manualmente porque o
        // loadConversations acima vai trazer a conv enriquecida.
        if (destConvId) {
          // Espera o refresh terminar e seleciona via deep link interno.
          setTimeout(() => {
            setConversations(prev => {
              const found = prev.find(c => c.id === destConvId)
              if (found) {
                setSelectedConversation(found)
                loadMessages(found)
              }
              return prev
            })
          }, 350)
        }
      }}
    />

    {/* Modal de Atribuir Líder */}
    {showAssignLeaderModal && selectedConversation && (
      <AssignLeaderModal
        isOpen={showAssignLeaderModal}
        onClose={() => setShowAssignLeaderModal(false)}
        conversationId={selectedConversation.id}
        deviceId={deviceId}
        contactName={selectedConversation.nome_cadastro || selectedConversation.remote_name || undefined}
        contactPhone={selectedConversation.remote_jid}
        contactRemoteJid={selectedConversation.remote_jid}
        conversationMessages={messages}
        leadExtractedData={
          typeof selectedConversation.lead_extracted_data === 'string'
            ? selectedConversation.lead_extracted_data
            : selectedConversation.lead_extracted_data
              ? JSON.stringify(selectedConversation.lead_extracted_data)
              : null
        }
        onAssign={(_leaderId, leaderData) => {
          console.log('Lead atribuído para líder:', leaderData.name)
          setShowAssignLeaderModal(false)
          // Update otimista: marca assigned_leader + zera awaiting_leader no estado
          // local pra o badge ⚑ Nome aparecer no header da conversa selecionada e
          // pra ela sumir da lista principal sem precisar refetch (que só vem no
          // próximo poll/F5). Backend gravou via AssignLeaderModal.
          const optimistic = {
            name: leaderData.name || null,
            assigned_at: new Date().toISOString(),
          }
          const convId = selectedConversation?.id
          setSelectedConversation(prev => prev ? { ...prev, assigned_leader: optimistic, awaiting_leader: false } : prev)
          setConversations(prev => prev.map(c => c.id === convId ? { ...c, assigned_leader: optimistic, awaiting_leader: false } : c))
          // Some do banner de "aguardando atribuição" na hora (otimista). O
          // backend também emite lead_auto_assign_released — isto aqui só evita
          // o vão de latência do socket pra quem acabou de atribuir.
          setHeldLeads(prev => prev.filter(h => h.conversationId !== convId))
          // Badges device-wide: a conv saiu de "aguardando" e entrou em "atribuída".
          // Ajuste otimista — o próximo fetchDeviceCounts reconcilia com o servidor.
          setDeviceCounts(prev => ({ ...prev, awaiting: Math.max(0, prev.awaiting - 1), assigned: prev.assigned + 1 }))
          // Avisa o painel lateral (ContactProfilePanel) que houve atribuição.
          // Sem isso, a timeline continua mostrando "Enviado para líder: pendente"
          // até o usuário trocar de conversa e voltar (visto após 11:14 de 17/05).
          window.dispatchEvent(new CustomEvent('leaderAssigned', {
            detail: {
              conversationId: convId,
              phone: selectedConversation?.remote_jid?.replace(/@.*/, '') || null,
              leaderName: leaderData.name,
              leaderCode: leaderData.code,
            }
          }))
        }}
      />
    )}

    {/* Modal: Prompt do Agente de IA */}
    {showAgentPromptModal && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center glass-overlay p-4"
        onClick={() => setShowAgentPromptModal(false)}
      >
        <div
          className="glass-panel rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <FiSettings className="w-5 h-5 text-primary" />
              <h3 className="text-base font-semibold text-foreground">Agente de IA — Novos Leads</h3>
            </div>
            <button
              onClick={() => setShowAgentPromptModal(false)}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title="Fechar"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {agentLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                <FiRefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin opacity-50" />
                <p className="text-sm">Carregando...</p>
              </div>
            ) : agentData ? (
              <>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Nome</label>
                  <p className="text-sm font-medium text-foreground mt-0.5">{agentData.name || '—'}</p>
                </div>
                {agentData.description && (
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Descrição</label>
                    <p className="text-sm text-foreground mt-0.5">{agentData.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {(agentData.model || llmConfig) && (
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Modelo</label>
                      {(() => {
                        // Sem override (BOT_LLM_PROVIDER=ollama, sem cooldown):
                        // mostra só o model do agente (comportamento legado).
                        if (!llmConfig || (llmConfig.configured === 'ollama' && !llmConfig.cooldown_active)) {
                          return <p className="text-sm font-mono text-foreground mt-0.5">{agentData.model || '—'}</p>;
                        }
                        // Override ativo OU em cooldown: surfaceia o efetivo + nota.
                        const effLabel = llmConfig.effective === 'claude'
                          ? llmConfig.claude_model
                          : llmConfig.ollama_model;
                        const isClaude = llmConfig.effective === 'claude';
                        const cooldownStr = llmConfig.cooldown_until
                          ? new Date(llmConfig.cooldown_until).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })
                          : null;
                        const noteTitle = llmConfig.cooldown_active
                          ? `Claude estourou rate limit; bot caiu pro fallback Gemma até ${cooldownStr} (BRT). Depois volta automaticamente.`
                          : `BOT_LLM_PROVIDER=claude no .env. Em erro/429/timeout cai pro ${llmConfig.ollama_model}.`;
                        return (
                          <>
                            <p className="text-sm font-mono text-foreground mt-0.5 flex items-center gap-1.5">
                              {effLabel}
                              {llmConfig.cooldown_active && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold uppercase tracking-wide" title={noteTitle}>
                                  fallback
                                </span>
                              )}
                              {!llmConfig.cooldown_active && isClaude && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 dark:text-violet-400 font-semibold uppercase tracking-wide" title={noteTitle}>
                                  override
                                </span>
                              )}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5" title={noteTitle}>
                              {llmConfig.cooldown_active
                                ? <>Claude em cooldown até {cooldownStr} · agente DB: <span className="font-mono">{agentData.model}</span></>
                                : <>Agente DB: <span className="font-mono">{agentData.model}</span> · fallback se Claude falhar</>
                              }
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {agentData.temperature !== undefined && agentData.temperature !== null && (
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Temperature</label>
                      <p className="text-sm font-mono text-foreground mt-0.5">{agentData.temperature}</p>
                    </div>
                  )}
                </div>
                {agentData.welcome_message && (
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Mensagem de boas-vindas</label>
                    <p className="text-sm text-foreground mt-0.5 bg-muted/50 rounded-lg p-2.5 border border-border">{agentData.welcome_message}</p>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Prompt do sistema</label>
                    {agentData.id && !agentEditing && (
                      <button
                        onClick={() => { setAgentEditPrompt(agentData.system_prompt || ''); setAgentEditing(true) }}
                        className="text-[11px] px-2 py-0.5 rounded bg-primary/15 hover:bg-primary/25 text-primary font-medium transition-colors"
                      >
                        Editar
                      </button>
                    )}
                  </div>
                  {agentEditing ? (
                    <textarea
                      value={agentEditPrompt}
                      onChange={e => setAgentEditPrompt(e.target.value)}
                      className="w-full text-xs text-foreground mt-0.5 bg-muted/50 rounded-lg p-3 border border-border font-mono max-h-[40vh] min-h-[200px] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
                      spellCheck={false}
                    />
                  ) : (
                    <pre className="text-xs text-foreground mt-0.5 bg-muted/50 rounded-lg p-3 border border-border whitespace-pre-wrap font-mono max-h-[40vh] overflow-y-auto">
{agentData.system_prompt || '(vazio)'}
                    </pre>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">Sem dados disponíveis.</p>
            )}
          </div>
          <div className="px-5 py-3 border-t border-border flex justify-end gap-2 flex-shrink-0">
            {agentEditing ? (
              <>
                <button
                  onClick={() => { setAgentEditing(false); setAgentEditPrompt('') }}
                  disabled={agentSaving}
                  className="px-4 py-1.5 rounded-lg bg-muted hover:bg-muted-foreground/20 text-foreground text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!agentData?.id) return
                    setAgentSaving(true)
                    try {
                      const r = await authenticatedFetch(`/api/agents/${agentData.id}`, {
                        method: 'PUT',
                        body: JSON.stringify({ systemPrompt: agentEditPrompt })
                      })
                      const d = await r.json()
                      if (d.success) {
                        setAgentData({ ...agentData, system_prompt: agentEditPrompt })
                        setAgentEditing(false)
                      } else {
                        alert('Erro ao salvar: ' + (d.error || 'desconhecido'))
                      }
                    } catch {
                      alert('Falha de rede ao salvar')
                    } finally {
                      setAgentSaving(false)
                    }
                  }}
                  disabled={agentSaving}
                  className="px-4 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  {agentSaving && <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  Salvar
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowAgentPromptModal(false)}
                className="px-4 py-1.5 rounded-lg bg-muted hover:bg-muted-foreground/20 text-foreground text-sm font-medium transition-colors"
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  )
})

export default WhatsAppInboxPanel
